import { useEffect, useState, useMemo, useRef, createContext, useContext } from "react";
import { useParams, Link, useNavigate, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { api, API, fmtMoney, formatError, tError, COUNTRIES } from "@/lib/api";
import { useTranslation, BankLangCtx } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Home, CreditCard as CardIcon, ArrowLeftRight, User, Bell, ChevronRight,
  TrendingUp, ArrowUpRight, Send, Wallet, Sparkles, Shield, ShieldCheck, LogOut,
  MoreHorizontal, ArrowDownLeft, Lock, Key, Filter, CheckCircle2, CheckCircle, AlertCircle,
  Star, Phone, Mail, MapPin, Zap, Banknote, PiggyBank, Briefcase, MessageCircle, FileText,
  HandCoins, Settings as SettingsIcon, Eye, Camera, Plus, ArrowDownToLine, Package, X,
  Copy, Check, Trash2,
} from "lucide-react";
import {
  IbanModal, AccountsPage, SavingsPage, InvestmentPage, MessagesPage,
  CreditsPage, FinancingPage, SettingsPage,
} from "@/pages/BankPages";

/* -------------------------------------------------------------------------- */
/* TEMPLATE STYLES                                                             */
/* -------------------------------------------------------------------------- */
const TEMPLATE_STYLES = {
  /* ── MINIMALISTE ─────────────────────────────────────── */
  "tpl-minimalist-mono": {
    name:"Minimalist Mono", group:"Minimaliste",
    bg:"#FFFFFF", fg:"#0A0A0A", accent:"#0A0A0A",
    cardGradient:"linear-gradient(135deg,#0A0A0A,#2A2A2A)",
    surface:"#F5F5F5", surfaceBorder:"#E5E5E5", isDark:false,
    heroLayout:"split", navStyle:"bordered", cardShape:"0px",
  },
  "tpl-arctic-clean": {
    name:"Arctic Clean", group:"Minimaliste",
    bg:"#F0F6FF", fg:"#0D1B2A", accent:"#2563EB",
    cardGradient:"linear-gradient(135deg,#1E40AF,#3B82F6)",
    surface:"#FFFFFF", surfaceBorder:"#DBEAFE", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"16px",
  },
  "tpl-pearl-elegant": {
    name:"Pearl Elegant", group:"Minimaliste",
    bg:"#FAFAF9", fg:"#292524", accent:"#A16207",
    cardGradient:"linear-gradient(135deg,#78350F,#D97706)",
    surface:"#F5F5F4", surfaceBorder:"#E7E5E4", isDark:false,
    heroLayout:"minimal", navStyle:"glassmorphism", cardShape:"24px",
  },
  /* ── SOMBRE PREMIUM ──────────────────────────────────── */
  "tpl-tech-neon": {
    name:"Tech Neon", group:"Sombre Premium",
    bg:"#0B0B14", fg:"#FAFAFA", accent:"#22D3EE",
    cardGradient:"linear-gradient(135deg,#0D2137,#0A1628)",
    surface:"#13131E", surfaceBorder:"rgba(34,211,238,0.15)", isDark:true,
    heroLayout:"split", navStyle:"glassmorphism", cardShape:"18px",
  },
  "tpl-midnight-gold": {
    name:"Midnight Gold", group:"Sombre Premium",
    bg:"#08070A", fg:"#FAF0DC", accent:"#D4AF37",
    cardGradient:"linear-gradient(135deg,#1A1200,#3D2B00)",
    surface:"#111009", surfaceBorder:"rgba(212,175,55,0.2)", isDark:true,
    heroLayout:"centered", navStyle:"glassmorphism", cardShape:"14px",
  },
  "tpl-obsidian-purple": {
    name:"Obsidian Purple", group:"Sombre Premium",
    bg:"#0D0B18", fg:"#F0EEFF", accent:"#A78BFA",
    cardGradient:"linear-gradient(135deg,#1E0A3C,#3B1278)",
    surface:"#160E28", surfaceBorder:"rgba(167,139,250,0.2)", isDark:true,
    heroLayout:"split", navStyle:"glassmorphism", cardShape:"20px",
  },
  "tpl-carbon-ember": {
    name:"Carbon & Ember", group:"Sombre Premium",
    bg:"#111111", fg:"#F5F5F0", accent:"#F97316",
    cardGradient:"linear-gradient(135deg,#431407,#7C2D12)",
    surface:"#1A1A1A", surfaceBorder:"rgba(249,115,22,0.22)", isDark:true,
    heroLayout:"full-bleed", navStyle:"bordered", cardShape:"4px",
  },
  /* ── NATURE ──────────────────────────────────────────── */
  "tpl-warm-premium": {
    name:"Warm Premium", group:"Nature",
    bg:"#F5F1E8", fg:"#1F2A1F", accent:"#166534",
    cardGradient:"linear-gradient(135deg,#1F2A1F,#3a4f3a)",
    surface:"#EDE8DC", surfaceBorder:"#D4CFC3", isDark:false,
    heroLayout:"split", navStyle:"bordered", cardShape:"14px",
  },
  "tpl-forest-depths": {
    name:"Forest Depths", group:"Nature",
    bg:"#0D1710", fg:"#D4F0D4", accent:"#4ADE80",
    cardGradient:"linear-gradient(135deg,#052E16,#14532D)",
    surface:"#162117", surfaceBorder:"rgba(74,222,128,0.18)", isDark:true,
    heroLayout:"centered", navStyle:"glassmorphism", cardShape:"16px",
  },
  "tpl-terracotta-earth": {
    name:"Terracotta Earth", group:"Nature",
    bg:"#FDF6F0", fg:"#3D1A0C", accent:"#C2522A",
    cardGradient:"linear-gradient(135deg,#7C2D12,#C2522A)",
    surface:"#FAF0E8", surfaceBorder:"#F0D5C0", isDark:false,
    heroLayout:"full-bleed", navStyle:"floating", cardShape:"20px",
  },
  /* ── CLASSIQUE ───────────────────────────────────────── */
  "tpl-classic-financial": {
    name:"Classic Financial", group:"Classique",
    bg:"#F8FAFC", fg:"#0F172A", accent:"#2563EB",
    cardGradient:"linear-gradient(135deg,#1D4ED8,#2563EB 60%,#3B82F6)",
    surface:"#FFFFFF", surfaceBorder:"#E2E8F0", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"12px",
  },
  "tpl-navy-prestige": {
    name:"Navy Prestige", group:"Classique",
    bg:"#0A1628", fg:"#E8F4FE", accent:"#60A5FA",
    cardGradient:"linear-gradient(135deg,#0A1628,#1E3A5F)",
    surface:"#0F2040", surfaceBorder:"rgba(96,165,250,0.16)", isDark:true,
    heroLayout:"centered", navStyle:"glassmorphism", cardShape:"14px",
  },
  "tpl-banker-grey": {
    name:"Banker Grey", group:"Classique",
    bg:"#F4F5F7", fg:"#1A2033", accent:"#334155",
    cardGradient:"linear-gradient(135deg,#1E293B,#334155)",
    surface:"#FFFFFF", surfaceBorder:"#CBD5E1", isDark:false,
    heroLayout:"minimal", navStyle:"bar", cardShape:"6px",
  },
  /* ── VIBRANT ─────────────────────────────────────────── */
  "tpl-electric-indigo": {
    name:"Electric Indigo", group:"Vibrant",
    bg:"#F5F3FF", fg:"#1E1B4B", accent:"#4F46E5",
    cardGradient:"linear-gradient(135deg,#3730A3,#4F46E5 60%,#7C3AED)",
    surface:"#EDE9FE", surfaceBorder:"#DDD6FE", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"20px",
  },
  "tpl-royal-amethyst": {
    name:"Royal Amethyst", group:"Vibrant",
    bg:"#180A28", fg:"#F3E8FF", accent:"#C084FC",
    cardGradient:"linear-gradient(135deg,#3B0764,#6B21A8)",
    surface:"#2D0F4A", surfaceBorder:"rgba(192,132,252,0.2)", isDark:true,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"24px",
  },
  "tpl-coral-sunset": {
    name:"Coral Sunset", group:"Vibrant",
    bg:"#FFF7F5", fg:"#4A1515", accent:"#EF4444",
    cardGradient:"linear-gradient(135deg,#DC2626,#F97316)",
    surface:"#FEF2F2", surfaceBorder:"#FECACA", isDark:false,
    heroLayout:"split", navStyle:"floating", cardShape:"16px",
  },
  /* ── LUXE GRADIENT ───────────────────────────────────── */
  "tpl-aurora-borealis": {
    name:"Aurora Borealis", group:"Luxe Gradient",
    bg:"#04101E", fg:"#E0F7FA", accent:"#06B6D4",
    cardGradient:"linear-gradient(135deg,#164E63,#0F766E 50%,#065F46)",
    surface:"#08182E", surfaceBorder:"rgba(6,182,212,0.18)", isDark:true,
    heroLayout:"centered", navStyle:"glassmorphism", cardShape:"22px",
  },
  "tpl-sunset-gradient": {
    name:"Sunset Gradient", group:"Luxe Gradient",
    bg:"#0F0A18", fg:"#FDF4FF", accent:"#E879F9",
    cardGradient:"linear-gradient(135deg,#7E22CE,#DB2777 50%,#EA580C)",
    surface:"#1A0F2A", surfaceBorder:"rgba(232,121,249,0.2)", isDark:true,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"24px",
  },
  "tpl-ocean-deep": {
    name:"Ocean Deep", group:"Luxe Gradient",
    bg:"#030D1A", fg:"#E0F0FF", accent:"#38BDF8",
    cardGradient:"linear-gradient(135deg,#0C4A6E,#0369A1 50%,#0EA5E9)",
    surface:"#05132A", surfaceBorder:"rgba(56,189,248,0.16)", isDark:true,
    heroLayout:"split", navStyle:"glassmorphism", cardShape:"16px",
  },
  "tpl-emerald-wealth": {
    name:"Emerald Wealth", group:"Luxe Gradient",
    bg:"#F0FDF4", fg:"#052E16", accent:"#059669",
    cardGradient:"linear-gradient(135deg,#064E3B,#059669 60%,#10B981)",
    surface:"#DCFCE7", surfaceBorder:"#BBF7D0", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"18px",
  },
  // ─────────────── PREMIUM SIGNATURE ───────────────
  "tpl-violet-pulse": {
    name:"Violet Pulse", group:"Premium Signature",
    bg:"#191C20", fg:"#FFFFFF", accent:"#7B61FF",
    cardGradient:"linear-gradient(135deg,#191C20 0%,#2D1B69 50%,#7B61FF)",
    surface:"#1E2128", surfaceBorder:"rgba(123,97,255,0.25)", isDark:true,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"22px", layoutVariant:"aurora",
  },
  "tpl-mint-clean": {
    name:"Mint Clean", group:"Premium Signature",
    bg:"#FAFAFA", fg:"#0A0A0A", accent:"#00D4AA",
    cardGradient:"linear-gradient(135deg,#003D2E,#00D4AA)",
    surface:"#FFFFFF", surfaceBorder:"#E5F9F5", isDark:false,
    heroLayout:"minimal", navStyle:"bordered", cardShape:"0px", layoutVariant:"minimal",
  },
  "tpl-jade-soft": {
    name:"Jade Soft", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#0A2A0A", accent:"#00A86B",
    cardGradient:"linear-gradient(135deg,#005C3A,#00A86B 60%,#00D185)",
    surface:"#F0FFF8", surfaceBorder:"#C3F7DF", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"26px", layoutVariant:"soft",
  },
  "tpl-lime-bold": {
    name:"Lime Bold", group:"Premium Signature",
    bg:"#F0FBE8", fg:"#163300", accent:"#9FE870",
    cardGradient:"linear-gradient(135deg,#163300,#2D6A00 50%,#9FE870)",
    surface:"#E8F8D0", surfaceBorder:"#B8EE88", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"16px", layoutVariant:"vivid",
  },
  "tpl-magenta-night": {
    name:"Magenta Night", group:"Premium Signature",
    bg:"#1A0A2E", fg:"#F0E8FF", accent:"#FF3CAC",
    cardGradient:"linear-gradient(135deg,#1A0A2E,#560BAD 50%,#FF3CAC)",
    surface:"#220D3A", surfaceBorder:"rgba(255,60,172,0.25)", isDark:true,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"22px", layoutVariant:"aurora",
  },
  "tpl-indigo-pro": {
    name:"Indigo Pro", group:"Premium Signature",
    bg:"#F7F7F7", fg:"#0A0A14", accent:"#4E27FA",
    cardGradient:"linear-gradient(135deg,#1A0875,#4E27FA 60%,#7B5AFF)",
    surface:"#FFFFFF", surfaceBorder:"#E0D8FF", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"16px", layoutVariant:"vivid",
  },
  "tpl-coral-access": {
    name:"Coral Access", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0800", accent:"#FF6B35",
    cardGradient:"linear-gradient(135deg,#8B2500,#FF6B35 60%,#FF9068)",
    surface:"#FFF5F0", surfaceBorder:"#FFD5C0", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"28px", layoutVariant:"soft",
  },
  "tpl-azure-institut": {
    name:"Azure Institut", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#001133", accent:"#004481",
    cardGradient:"linear-gradient(135deg,#001A44,#004481 60%,#0066CC)",
    surface:"#F0F5FF", surfaceBorder:"#C8D8F0", isDark:false,
    heroLayout:"split", navStyle:"bordered", cardShape:"4px", layoutVariant:"corporate",
  },
  "tpl-flame-red": {
    name:"Flame Red", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0000", accent:"#EC0000",
    cardGradient:"linear-gradient(135deg,#7A0000,#EC0000 60%,#FF3333)",
    surface:"#FFF5F5", surfaceBorder:"#F5CCCC", isDark:false,
    heroLayout:"minimal", navStyle:"bordered", cardShape:"2px", layoutVariant:"editorial",
  },
  "tpl-amber-vivid": {
    name:"Amber Vivid", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0800", accent:"#FF6200",
    cardGradient:"linear-gradient(135deg,#8B3400,#FF6200 60%,#FF8C42)",
    surface:"#FFF8F0", surfaceBorder:"#FFD8B0", isDark:false,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"16px", layoutVariant:"vivid",
  },
  "tpl-sky-azure": {
    name:"Sky Azure", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#001830", accent:"#007EBD",
    cardGradient:"linear-gradient(135deg,#003A5C,#007EBD 60%,#00A8F8)",
    surface:"#F0F8FF", surfaceBorder:"#C0E0F5", isDark:false,
    heroLayout:"minimal", navStyle:"bar", cardShape:"0px", layoutVariant:"minimal",
  },
  "tpl-sapphire-deep": {
    name:"Sapphire Deep", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#00112A", accent:"#004990",
    cardGradient:"linear-gradient(135deg,#00204A,#004990 60%,#0066CC)",
    surface:"#F0F4FF", surfaceBorder:"#C0CFEA", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"4px", layoutVariant:"corporate",
  },
  "tpl-tangerine": {
    name:"Tangerine", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0A00", accent:"#FF6000",
    cardGradient:"linear-gradient(135deg,#8B3300,#FF6000 60%,#FF8533)",
    surface:"#FFF6F0", surfaceBorder:"#FFCCAA", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"24px", layoutVariant:"soft",
  },
  "tpl-midnight-corporate": {
    name:"Midnight Corporate", group:"Premium Signature",
    bg:"#F7F8FA", fg:"#000B37", accent:"#0018A8",
    cardGradient:"linear-gradient(135deg,#000B37,#0018A8 60%,#0033FF)",
    surface:"#ECEEF5", surfaceBorder:"#BCC5E8", isDark:false,
    heroLayout:"split", navStyle:"bordered", cardShape:"4px", layoutVariant:"corporate",
  },
  "tpl-carmine": {
    name:"Carmine", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0000", accent:"#E30613",
    cardGradient:"linear-gradient(135deg,#780010,#E30613 60%,#FF3344)",
    surface:"#FFF5F5", surfaceBorder:"#F0C0C4", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"2px", layoutVariant:"editorial",
  },
  "tpl-emerald-institut": {
    name:"Emerald Institut", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#00200E", accent:"#00965E",
    cardGradient:"linear-gradient(135deg,#004A2E,#00965E 60%,#00C87A)",
    surface:"#F0FBF5", surfaceBorder:"#B8EDD5", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"4px", layoutVariant:"corporate",
  },
  "tpl-noir-rouge": {
    name:"Noir Rouge", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#0A0A0A", accent:"#E60000",
    cardGradient:"linear-gradient(135deg,#1A1A1A,#7A0000 50%,#E60000)",
    surface:"#F8F8F8", surfaceBorder:"#EEDADA", isDark:false,
    heroLayout:"minimal", navStyle:"bordered", cardShape:"0px", layoutVariant:"editorial",
  },
  "tpl-meadow-green": {
    name:"Meadow Green", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#00200E", accent:"#00A651",
    cardGradient:"linear-gradient(135deg,#004A25,#00A651 60%,#00CC66)",
    surface:"#F0FAF4", surfaceBorder:"#B8EAD0", isDark:false,
    heroLayout:"minimal", navStyle:"bordered", cardShape:"0px", layoutVariant:"minimal",
  },
  "tpl-scarlet-prime": {
    name:"Scarlet Prime", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0000", accent:"#DB0011",
    cardGradient:"linear-gradient(135deg,#700008,#DB0011 60%,#FF2233)",
    surface:"#FFF5F5", surfaceBorder:"#F0C0C4", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"2px", layoutVariant:"editorial",
  },
  "tpl-cyan-azure": {
    name:"Cyan Azure", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#00112A", accent:"#00AEEF",
    cardGradient:"linear-gradient(135deg,#00195E,#0050B0 50%,#00AEEF)",
    surface:"#F0F8FF", surfaceBorder:"#BDE5F8", isDark:false,
    heroLayout:"centered", navStyle:"floating", cardShape:"16px", layoutVariant:"vivid",
  },
  "tpl-gold-night": {
    name:"Gold Night", group:"Premium Signature",
    bg:"#1A1A2A", fg:"#F5F0D8", accent:"#FFCC00",
    cardGradient:"linear-gradient(135deg,#1A1A2A,#3A3A1A 50%,#FFCC00)",
    surface:"#22223A", surfaceBorder:"rgba(255,204,0,0.25)", isDark:true,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"22px", layoutVariant:"aurora",
  },
  "tpl-solar-vivid": {
    name:"Solar Vivid", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0800", accent:"#FF6600",
    cardGradient:"linear-gradient(135deg,#8B3800,#FF6600 60%,#FF9933)",
    surface:"#FFF8F0", surfaceBorder:"#FFD8A8", isDark:false,
    heroLayout:"full-bleed", navStyle:"glassmorphism", cardShape:"16px", layoutVariant:"vivid",
  },
  "tpl-ruby-sharp": {
    name:"Ruby Sharp", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#1A0000", accent:"#CC0000",
    cardGradient:"linear-gradient(135deg,#6A0000,#CC0000 60%,#FF2222)",
    surface:"#FFF5F5", surfaceBorder:"#F0C0C0", isDark:false,
    heroLayout:"minimal", navStyle:"bordered", cardShape:"0px", layoutVariant:"editorial",
  },
  "tpl-royal-gold": {
    name:"Royal Gold", group:"Premium Signature",
    bg:"#FFFFFF", fg:"#000D33", accent:"#003D99",
    cardGradient:"linear-gradient(135deg,#003D99,#0052CC 50%,#FFE000)",
    surface:"#F0F4FF", surfaceBorder:"#C0CFEA", isDark:false,
    heroLayout:"split", navStyle:"bar", cardShape:"4px", layoutVariant:"corporate",
  },
};

const TOKEN_KEY = (slug) => `bank_user_token_${slug}`;
const BankCtx = createContext({});

function useStyle(bank) {
  return useMemo(() => {
    if (!bank) return TEMPLATE_STYLES["tpl-classic-financial"];
    const base = TEMPLATE_STYLES[bank.template_id] || TEMPLATE_STYLES["tpl-classic-financial"];
    return { ...base, accent: bank.primary_color || base.accent };
  }, [bank]);
}

const LANG_KEY = (slug) => `bank_lang_${slug}`;

export default function BankPublic({ slugOverride } = {}) {
  const { slug: slugParam } = useParams();
  const slug = slugOverride || slugParam;
  const [searchParams] = useSearchParams();
  const [bank, setBank] = useState(null);
  const [error, setError] = useState("");

  // Public-side language resolution (landing / login / signup):
  //   1. Explicit URL parameter `?lang=xx`     → highest priority (localized share-link)
  //   2. sessionStorage[bank_lang_<slug>]      → persists the URL choice for THIS
  //                                              browser tab only (so internal
  //                                              navigation keeps the language)
  //   3. Bank's default_locale                 → the language the admin configured
  //   4. "fr" fallback
  //
  // We use sessionStorage (per-tab) instead of localStorage (global) on purpose:
  //   • A client opening an admin-shared link `?lang=de` stays in German while
  //     they browse the bank — exactly the desired behaviour.
  //   • A different tab / browser / visitor lands on bank.default_locale and is
  //     NOT contaminated by another visitor's choice (the bug we fixed in iter-4).
  //   • Closing the tab wipes the override — next visit honours default_locale.
  const SS_KEY = `bank_lang_${slug}`;
  const urlLang = searchParams.get("lang");
  useEffect(() => {
    if (!urlLang) return;
    try { sessionStorage.setItem(SS_KEY, urlLang); } catch (_) {}
  }, [urlLang, SS_KEY]);
  const sessionLang = (() => {
    try { return sessionStorage.getItem(SS_KEY); } catch (_) { return null; }
  })();
  const langOverride = urlLang || sessionLang || null;
  const { t } = useTranslation(langOverride || bank?.default_locale || "fr");

  const isDemo = slug === "_demo";
  const demoTemplate = searchParams.get("template") || "tpl-classic-financial";

  useEffect(() => {
    const endpoint = isDemo
      ? `/demo/bank?template_id=${demoTemplate}`
      : `/public/bank/${slug}`;
    api.get(endpoint).then((r) => setBank(r.data)).catch(() => setError(t("error.bank_not_found")));
  }, [slug, isDemo, demoTemplate]);

  // Dynamic favicon + title + Open Graph / Twitter Card / description for this bank.
  // EVERY metadata tag that could leak the platform identity is rewritten here.
  useEffect(() => {
    if (!bank) return;
    const prevTitle = document.title;

    const bankName = bank.name || "Bank";
    const slogan   = bank.subtitle || bank.tagline || bank.description || `${bankName} — votre banque en ligne`;
    const desc     = (bank.description || slogan).slice(0, 240);
    const origin   = window.location.origin;
    // Server-rendered OG card (logo + name + slogan on bank colors)
    const ogImage  = `${API}/og/bank/${bank.slug}/image.png`;
    const ogUrl    = `${origin}${window.location.pathname}`;

    document.title = bankName;

    /** Upsert a single <meta>/<link> element by selector. */
    const upsertMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta('meta[name="description"]',        "name",     "description",      desc);
    upsertMeta('meta[property="og:title"]',       "property", "og:title",         bankName);
    upsertMeta('meta[property="og:description"]', "property", "og:description",   desc);
    upsertMeta('meta[property="og:image"]',       "property", "og:image",         ogImage);
    upsertMeta('meta[property="og:image:width"]', "property", "og:image:width",   "1200");
    upsertMeta('meta[property="og:image:height"]',"property", "og:image:height",  "630");
    upsertMeta('meta[property="og:url"]',         "property", "og:url",           ogUrl);
    upsertMeta('meta[property="og:type"]',        "property", "og:type",          "website");
    upsertMeta('meta[property="og:site_name"]',   "property", "og:site_name",     bankName);
    upsertMeta('meta[name="twitter:card"]',       "name",     "twitter:card",     "summary_large_image");
    upsertMeta('meta[name="twitter:title"]',      "name",     "twitter:title",    bankName);
    upsertMeta('meta[name="twitter:description"]',"name",     "twitter:description", desc);
    upsertMeta('meta[name="twitter:image"]',      "name",     "twitter:image",    ogImage);
    upsertMeta('meta[name="theme-color"]',        "name",     "theme-color",     bank.primary_color || "#0F172A");

    // Canonical URL (linked to this bank's own domain — never neobk.pro)
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = ogUrl;

    // Favicon — bank logo if present, else a generated initial-on-color icon
    const link = document.querySelector("link[rel='icon']") || document.createElement("link");
    link.rel = "icon";
    const prevHref = link.href;

    if (bank.logo_url) {
      link.href = bank.logo_url;
      link.type = bank.logo_url.startsWith("data:image/svg") ? "image/svg+xml" : "image/png";
    } else {
      const color = bank.primary_color || "#0F172A";
      const letter = (bank.name || "B")[0].toUpperCase();
      const canvas = document.createElement("canvas");
      canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 34px Arial,sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(letter, 32, 33);
      link.href = canvas.toDataURL("image/png");
      link.type = "image/png";
    }
    if (!link.parentNode) document.head.appendChild(link);

    return () => {
      document.title = prevTitle;
      link.href = prevHref || "/favicon.svg";
      link.type = "image/svg+xml";
    };
  }, [bank]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-zinc-900">
        <div className="text-center">
          <div className="font-display text-3xl font-extrabold mb-2">404</div>
          <p className="text-zinc-500">{error}</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">{t("error.back_home")}</Link>
        </div>
      </div>
    );
  }
  if (!bank) return <div className="min-h-screen flex items-center justify-center text-zinc-500">{t("common.loading")}</div>;

  const basePath = slugOverride ? "" : `/bank/${slug}`;

  return (
    <BankCtx.Provider value={{ bank, slug, langOverride, isDemo, basePath }}>
      <Routes>
        <Route index element={<Navigate to="login" replace />} />
        <Route path="login" element={<BankAuth mode="login" />} />
        <Route path="signup" element={<Navigate to="../login" replace />} />
        <Route path="app/*" element={<BankApp />} />
        <Route path="*" element={<Navigate to="login" replace />} />
      </Routes>
    </BankCtx.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* LANDING PAGE                                                                */
/* -------------------------------------------------------------------------- */

const LANDING_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400..800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
*,*::before,*::after{box-sizing:border-box}
html{scroll-behavior:smooth}
.lp-root{font-family:'DM Sans',sans-serif;background:var(--lp-bg);color:var(--lp-fg);min-height:100vh;overflow-x:hidden}

/* Navbar */
.lp-nav{position:sticky;top:0;z-index:100;backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);background:var(--lp-bg-glass);border-bottom:1px solid var(--lp-line)}
.lp-nav-inner{max-width:1200px;margin:0 auto;padding:0 1.5rem;height:68px;display:flex;align-items:center;justify-content:space-between;gap:16px}
.lp-nav-brand{display:flex;align-items:center;gap:10px;text-decoration:none;color:inherit}
.lp-nav-brand-name{font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:1.05rem;letter-spacing:-0.015em;color:var(--lp-accent)}
.lp-nav-links{display:flex;align-items:center;gap:28px}
.lp-nav-link{font-size:.87rem;font-weight:500;color:var(--lp-fg);opacity:.65;text-decoration:none;transition:opacity .15s}
.lp-nav-link:hover{opacity:1}
.lp-nav-ctas{display:flex;align-items:center;gap:10px}
.lp-btn-outline{font-size:.87rem;font-weight:600;padding:9px 18px;border-radius:9px;border:1.5px solid var(--lp-fg-20);background:transparent;color:var(--lp-fg);cursor:pointer;transition:border-color .15s,background .15s}
.lp-btn-outline:hover{background:var(--lp-fg-06);border-color:var(--lp-fg-40)}
.lp-btn-primary{font-size:.87rem;font-weight:600;padding:9px 20px;border-radius:9px;border:none;background:var(--lp-accent);color:#fff;cursor:pointer;transition:opacity .15s,transform .12s;box-shadow:0 4px 14px var(--lp-accent-30)}
.lp-btn-primary:hover{opacity:.9;transform:translateY(-1px)}

/* Hero */
.lp-hero{max-width:1200px;margin:0 auto;padding:88px 1.5rem 100px;display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.lp-hero-badge{display:inline-flex;align-items:center;gap:6px;background:var(--lp-accent-12);color:var(--lp-accent);border:1px solid var(--lp-accent-25);border-radius:100px;padding:5px 14px;font-size:.75rem;font-weight:700;letter-spacing:.03em;margin-bottom:24px;animation:lp-fadein .6s ease}
.lp-hero-h1{font-family:'Syne',sans-serif;font-size:clamp(2.1rem,4.2vw,3.6rem);font-weight:700;line-height:1.1;letter-spacing:-.015em;margin:0;animation:lp-fadein .7s ease}
.lp-hero-h1 em{font-style:normal;color:var(--lp-accent)}
.lp-hero-sub{margin-top:22px;font-size:1.05rem;line-height:1.75;opacity:.7;max-width:460px;animation:lp-fadein .8s ease}
.lp-hero-actions{margin-top:38px;display:flex;flex-wrap:wrap;gap:12px;animation:lp-fadein .9s ease}
.lp-hero-cta{display:inline-flex;align-items:center;gap:8px;padding:15px 30px;border-radius:12px;background:var(--lp-accent);color:#fff;font-weight:700;font-size:.95rem;border:none;cursor:pointer;box-shadow:0 6px 24px var(--lp-accent-35);transition:transform .12s,box-shadow .12s}
.lp-hero-cta:hover{transform:translateY(-2px);box-shadow:0 10px 32px var(--lp-accent-45)}
.lp-hero-ghost{display:inline-flex;align-items:center;gap:8px;padding:15px 28px;border-radius:12px;border:1.5px solid var(--lp-fg-20);background:transparent;color:var(--lp-fg);font-weight:600;font-size:.95rem;cursor:pointer;transition:background .12s}
.lp-hero-ghost:hover{background:var(--lp-fg-06)}
.lp-hero-trust{margin-top:32px;display:flex;flex-wrap:wrap;gap:18px;animation:lp-fadein 1s ease}
.lp-hero-trust-item{display:flex;align-items:center;gap:6px;font-size:.8rem;font-weight:500;opacity:.6}

/* Phone mockup */
.lp-phone{width:100%;max-width:320px;margin:0 auto;animation:lp-float 4s ease-in-out infinite}
.lp-phone-shell{background:var(--lp-card-bg);border-radius:34px;padding:18px;box-shadow:0 32px 80px var(--lp-shadow),0 0 0 1px var(--lp-line);border:1px solid var(--lp-line)}
.lp-phone-bar{display:flex;justify-content:space-between;font-size:.62rem;opacity:.4;margin-bottom:14px;padding:0 4px}
.lp-phone-card{background:var(--lp-gradient);border-radius:18px;padding:20px;margin-bottom:14px;color:#fff;position:relative;overflow:hidden}
.lp-phone-card::after{content:'';position:absolute;top:-30px;right:-30px;width:100px;height:100px;border-radius:50%;background:rgba(255,255,255,.08)}
.lp-phone-bal-label{font-size:.6rem;text-transform:uppercase;letter-spacing:.15em;opacity:.75;margin-bottom:4px}
.lp-phone-bal-amount{font-family:'DM Sans',sans-serif;font-size:1.7rem;font-weight:700;letter-spacing:-.02em;font-variant-numeric:tabular-nums lining-nums}
.lp-phone-bal-cur{font-size:.68rem;opacity:.65;margin-top:2px}
.lp-phone-actions{display:flex;gap:8px;margin-top:14px}
.lp-phone-act{font-size:.62rem;padding:5px 12px;border-radius:100px;border:1px solid rgba(255,255,255,.3);cursor:pointer}
.lp-phone-row{display:flex;align-items:center;justify-content:space-between;padding:10px 6px;border-bottom:1px solid var(--lp-line)}
.lp-phone-row:last-child{border-bottom:none}
.lp-phone-row-icon{width:30px;height:30px;border-radius:50%;background:var(--lp-accent-12);display:flex;align-items:center;justify-content:center}
.lp-phone-row-label{font-size:.75rem;font-weight:600;color:var(--lp-fg)}
.lp-phone-row-sub{font-size:.62rem;color:var(--lp-fg);opacity:.45}
.lp-phone-row-amt{font-size:.78rem;font-weight:700}
.lp-phone-row-amt.credit{color:#16a34a}
.lp-phone-row-amt.debit{color:#ef4444}

/* Section layout */
.lp-section{max-width:1200px;margin:0 auto;padding:88px 1.5rem}
.lp-section-label{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.13em;color:var(--lp-accent);margin-bottom:12px}
.lp-section-h2{font-family:'Syne',sans-serif;font-size:clamp(1.6rem,2.6vw,2.2rem);font-weight:700;line-height:1.2;letter-spacing:-.012em;margin:0 0 16px}
.lp-section-sub{font-size:1rem;opacity:.65;line-height:1.7;max-width:520px;margin:0 auto 56px}

/* Stats bar */
.lp-stats{border-top:1px solid var(--lp-line);border-bottom:1px solid var(--lp-line)}
.lp-stats-inner{max-width:1200px;margin:0 auto;padding:0 1.5rem;display:grid;grid-template-columns:repeat(4,1fr)}
.lp-stat{padding:32px 20px;text-align:center;border-right:1px solid var(--lp-line)}
.lp-stat:last-child{border-right:none}
.lp-stat-val{font-family:'Syne',sans-serif;font-size:1.85rem;font-weight:700;color:var(--lp-accent);line-height:1;letter-spacing:-.01em}
.lp-stat-label{font-size:.82rem;font-weight:600;margin-top:6px;opacity:.75}

/* Feature cards */
.lp-features-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.lp-feat{padding:28px;border-radius:18px;background:var(--lp-card-bg);border:1px solid var(--lp-line);transition:transform .2s,box-shadow .2s}
.lp-feat:hover{transform:translateY(-4px);box-shadow:0 16px 40px var(--lp-shadow)}
.lp-feat-icon{width:48px;height:48px;border-radius:14px;background:var(--lp-accent-12);display:flex;align-items:center;justify-content:center;margin-bottom:18px}
.lp-feat-title{font-family:'Syne',sans-serif;font-weight:700;font-size:1rem;margin:0 0 8px}
.lp-feat-body{font-size:.84rem;opacity:.65;line-height:1.6;margin:0}

/* Templates */
.lp-tpl-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.lp-tpl-card{border-radius:18px;overflow:hidden;border:2px solid transparent;cursor:pointer;transition:transform .2s,border-color .2s;position:relative}
.lp-tpl-card:hover{transform:translateY(-4px);border-color:var(--lp-accent)}
.lp-tpl-preview{padding:16px}
.lp-tpl-label{font-size:.75rem;font-weight:700;text-align:center;padding:10px;border-top:1px solid rgba(255,255,255,.1)}

/* Trusted-by logo carousel */
.lp-trusted-wrap{overflow:hidden;width:100%;position:relative;padding:24px 0;mask-image:linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%);-webkit-mask-image:linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)}
.lp-trusted-track{display:flex;gap:48px;width:max-content;animation:lp-trusted-scroll 80s linear infinite;align-items:center}
.lp-trusted-wrap:hover .lp-trusted-track{animation-play-state:paused}
.lp-trusted-item{flex:0 0 auto;width:160px;height:80px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:14px 20px;transition:background .2s, border-color .2s}
.lp-trusted-item:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.18)}
.lp-trusted-item img{max-width:100%;max-height:100%;object-fit:contain;opacity:.92;transition:opacity .25s, transform .25s}
.lp-trusted-item:hover img{opacity:1;transform:scale(1.06)}
.lp-trusted-item--text{padding:14px 22px}
.lp-trusted-text{
  font-family:'Syne',sans-serif;font-weight:700;
  font-size:1.05rem;letter-spacing:-.01em;
  color:rgba(255,255,255,.85);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  max-width:100%;text-align:center;line-height:1.1;
}
.lp-trusted-fallback{display:none;font-family:'Syne',sans-serif;font-weight:700;font-size:.85rem;color:rgba(255,255,255,.7);text-align:center}
@keyframes lp-trusted-scroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@media (max-width: 768px){
  .lp-trusted-item{width:120px;height:60px;padding:10px 14px}
  .lp-trusted-track{gap:32px;animation-duration:60s}
}

/* Dark band */
.lp-dark{background:var(--lp-dark-bg);padding:88px 0}
.lp-dark-inner{max-width:1200px;margin:0 auto;padding:0 1.5rem}

/* Security */
.lp-sec-grid{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.lp-sec-shield{width:120px;height:120px;border-radius:50%;background:linear-gradient(135deg,var(--lp-accent-30),var(--lp-accent-12));display:flex;align-items:center;justify-content:center;margin:0 auto 32px;border:2px solid var(--lp-accent-40)}
.lp-sec-items{display:flex;flex-direction:column;gap:20px}
.lp-sec-item{display:flex;align-items:flex-start;gap:14px;padding:20px;border-radius:16px;background:var(--lp-card-bg);border:1px solid var(--lp-line)}
.lp-sec-item-icon{width:38px;height:38px;border-radius:10px;background:var(--lp-accent-12);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
.lp-sec-item-title{font-weight:700;font-size:.9rem;color:var(--lp-fg);margin:0 0 4px}
.lp-sec-item-body{font-size:.82rem;color:var(--lp-fg);opacity:.6;line-height:1.5;margin:0}

/* Products mini grid */
.lp-prod-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.lp-prod-card{padding:22px;border-radius:16px;background:var(--lp-card-bg);border:1px solid var(--lp-line);display:flex;flex-direction:column;gap:12px;transition:transform .2s}
.lp-prod-card:hover{transform:translateY(-3px)}
.lp-prod-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
.lp-prod-name{font-weight:700;font-size:.92rem;margin:0}
.lp-prod-rate{font-size:.78rem;opacity:.55;margin:0}
.lp-prod-badge{display:inline-block;font-size:.68rem;font-weight:700;padding:3px 10px;border-radius:100px}

/* FAQ */
.lp-faq{display:flex;flex-direction:column;gap:10px;max-width:760px;margin:0 auto}
.lp-faq-item{border-radius:14px;border:1px solid var(--lp-line);background:var(--lp-card-bg);overflow:hidden}
.lp-faq-q{width:100%;display:flex;justify-content:space-between;align-items:center;padding:18px 22px;background:none;border:none;color:var(--lp-fg);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:600;text-align:left;gap:12px}
.lp-faq-chevron{transition:transform .25s;flex-shrink:0;opacity:.5}
.lp-faq-chevron.open{transform:rotate(180deg)}
.lp-faq-body{padding:0 22px 18px;font-size:.88rem;opacity:.7;line-height:1.7;margin:0}

/* CTA band */
.lp-cta{padding:88px 1.5rem;text-align:center;background:var(--lp-gradient);position:relative;overflow:hidden}
.lp-cta::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;border-radius:50%;background:rgba(255,255,255,.06)}
.lp-cta h2{font-family:'Syne',sans-serif;font-size:clamp(1.7rem,3.4vw,2.4rem);font-weight:700;color:#fff;margin:0 0 16px;letter-spacing:-.012em;position:relative}
.lp-cta p{color:rgba(255,255,255,.78);font-size:1rem;margin:0 auto 38px;max-width:480px;line-height:1.7;position:relative}
.lp-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:16px 40px;border-radius:12px;background:#fff;color:var(--lp-accent);font-weight:700;font-size:1rem;border:none;cursor:pointer;box-shadow:0 8px 32px rgba(0,0,0,.25);position:relative;transition:transform .12s}
.lp-cta-btn:hover{transform:translateY(-2px)}

/* Footer */
.lp-footer{border-top:1px solid var(--lp-line);background:var(--lp-bg)}
.lp-footer-inner{max-width:1200px;margin:0 auto;padding:48px 1.5rem 32px;display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:40px}
.lp-footer-brand{display:flex;align-items:center;gap:10px;margin-bottom:14px}
.lp-footer-brand-name{font-family:'Plus Jakarta Sans',sans-serif;font-weight:700;font-size:1rem;letter-spacing:-0.015em;color:var(--lp-accent)}
.lp-footer-tagline{font-size:.82rem;opacity:.5;line-height:1.6;max-width:240px}
.lp-footer-col-title{font-weight:700;font-size:.82rem;text-transform:uppercase;letter-spacing:.08em;opacity:.4;margin-bottom:14px}
.lp-footer-link{display:block;font-size:.85rem;opacity:.6;text-decoration:none;color:inherit;margin-bottom:9px;transition:opacity .12s}
.lp-footer-link:hover{opacity:1}
.lp-footer-social{display:flex;gap:10px;margin-top:16px}
.lp-footer-social-btn{display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:8px;background:var(--lp-accent);color:#fff;text-decoration:none;opacity:.75;transition:opacity .15s,transform .15s}
.lp-footer-social-btn:hover{opacity:1;transform:translateY(-2px)}
.lp-footer-contact-item{font-size:.82rem;opacity:.55;line-height:1.7;margin-bottom:4px;display:flex;align-items:center;gap:8px}
.lp-footer-contact-item svg{flex-shrink:0;opacity:.75}
.lp-footer-bottom{max-width:1200px;margin:0 auto;padding:18px 1.5rem;border-top:1px solid var(--lp-line);display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:nowrap}
.lp-footer-copy{font-size:.78rem;opacity:.45;white-space:normal}
@media (max-width:480px){
  .lp-footer-bottom{gap:8px;padding:14px 1rem}
  .lp-footer-copy{font-size:.68rem}
}

/* Auth page */
.lp-auth-root{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--lp-bg);color:var(--lp-fg);padding:4rem 1.5rem;overflow-x:hidden;font-family:'DM Sans',sans-serif}
.lp-auth-outer{width:100%;max-width:580px;display:flex;flex-direction:column;align-items:center;animation:lp-fadein .5s ease;overflow-x:hidden}
.lp-auth-logo-bubble{width:96px;height:96px;border-radius:50%;display:flex;align-items:center;justify-content:center;overflow:hidden;margin-bottom:-48px;z-index:2;position:relative;box-shadow:0 8px 32px rgba(0,0,0,.20)}
.lp-auth-logo-img{width:100%;height:100%;object-fit:cover;display:block}
.lp-auth-logo-letter{color:#fff;font-weight:900;font-size:40px;line-height:1;font-family:'Syne',sans-serif;letter-spacing:-0.02em}
.lp-auth-card{width:100%;background:var(--lp-card-bg);border-radius:24px;padding:68px 48px 44px;border:1px solid var(--lp-line);box-shadow:0 8px 40px rgba(0,0,0,.07)}
.lp-auth-head{text-align:center;margin-bottom:32px}
.lp-auth-head h1{font-family:'Syne',sans-serif;font-size:1.3rem;font-weight:700;letter-spacing:-0.02em;margin:0 0 14px;line-height:1.25}
.lp-auth-badge{display:inline-flex;align-items:center;background:var(--lp-fg-06);border:1px solid var(--lp-fg-20);border-radius:100px;padding:7px 20px;font-size:.82rem;font-weight:500}
.lp-auth-back{font-size:.85rem;opacity:.55;display:inline-flex;align-items:center;gap:4px;text-decoration:none;color:inherit;margin-bottom:28px;transition:opacity .12s}
.lp-auth-back:hover{opacity:.9}
.lp-auth-field{margin-bottom:14px}
.lp-auth-label{font-size:.75rem;font-weight:600;opacity:.65;display:block;margin-bottom:6px}
.lp-auth-input{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid var(--lp-line);background:var(--lp-bg);color:var(--lp-fg);font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;transition:border-color .15s;box-sizing:border-box}
.lp-auth-input:focus{border-color:var(--lp-accent)}
.lp-auth-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.lp-auth-submit{width:100%;margin-top:6px;padding:13px;border-radius:10px;border:none;background:var(--lp-accent);color:#fff;font-family:'DM Sans',sans-serif;font-weight:700;font-size:.95rem;cursor:pointer;transition:opacity .12s,transform .12s;box-shadow:0 4px 14px var(--lp-accent-30)}
.lp-auth-submit:hover:not(:disabled){opacity:.9;transform:translateY(-1px)}
.lp-auth-submit:disabled{opacity:.55;cursor:not-allowed}
.lp-auth-switch{margin-top:18px;text-align:center;font-size:.84rem;opacity:.6}
.lp-auth-switch a{color:var(--lp-accent);font-weight:700;text-decoration:none}
.lp-auth-pending{text-align:center;padding:8px 0}
.lp-auth-pending-icon{width:64px;height:64px;border-radius:50%;background:#10b98118;display:flex;align-items:center;justify-content:center;margin:0 auto 16px}

/* KYC section */
.lp-kyc-section{margin-top:20px;padding-top:20px;border-top:1px dashed var(--lp-line)}
.lp-kyc-title{font-family:'Syne',sans-serif;font-weight:700;font-size:.95rem;margin:0 0 4px;display:flex;align-items:center;gap:6px;color:var(--lp-fg)}
.lp-kyc-sub{font-size:.78rem;opacity:.6;margin:0 0 14px;line-height:1.5}
.lp-kyc-radio-row{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px}
.lp-kyc-radio{cursor:pointer;padding:10px 8px;border-radius:10px;border:1.5px solid var(--lp-line);text-align:center;font-size:.75rem;font-weight:600;background:var(--lp-bg);color:var(--lp-fg);transition:all .12s;display:flex;flex-direction:column;align-items:center;gap:4px}
.lp-kyc-radio:hover{border-color:var(--lp-accent)}
.lp-kyc-radio.checked{border-color:var(--lp-accent);background:var(--lp-accent-12);color:var(--lp-accent)}
.lp-kyc-file{display:flex;align-items:center;gap:10px;padding:10px 12px;border:1.5px dashed var(--lp-line);border-radius:10px;background:var(--lp-bg);cursor:pointer;margin-bottom:10px;transition:border-color .12s,background .12s}
.lp-kyc-file:hover{border-color:var(--lp-accent);background:var(--lp-accent-12)}
.lp-kyc-file.filled{border-style:solid;border-color:#10b981;background:#10b9810D}
.lp-kyc-file-icon{width:30px;height:30px;border-radius:8px;background:var(--lp-accent-12);display:flex;align-items:center;justify-content:center;flex-shrink:0;color:var(--lp-accent)}
.lp-kyc-file.filled .lp-kyc-file-icon{background:#10b98122;color:#10b981}
.lp-kyc-file-text{flex:1;min-width:0}
.lp-kyc-file-label{font-size:.78rem;font-weight:600;color:var(--lp-fg)}
.lp-kyc-file-meta{font-size:.7rem;opacity:.55;color:var(--lp-fg);margin-top:1px}
.lp-kyc-file-action{font-size:.7rem;font-weight:700;color:var(--lp-accent);text-transform:uppercase;letter-spacing:.04em}
.lp-phone-row{display:grid;grid-template-columns:90px 1fr;gap:8px}
.lp-phone-prefix{padding:11px 10px;border-radius:10px;border:1.5px solid var(--lp-line);background:var(--lp-card-bg);color:var(--lp-fg);font-family:'DM Sans',sans-serif;font-size:.9rem;font-weight:600;text-align:center;display:flex;align-items:center;justify-content:center;gap:4px}

@keyframes lp-fadein{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
@keyframes lp-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}

/* Responsive */
@media(max-width:1024px){
  .lp-features-grid{grid-template-columns:1fr 1fr}
  .lp-tpl-grid{grid-template-columns:1fr 1fr}
  .lp-prod-grid{grid-template-columns:1fr 1fr}
  .lp-footer-inner{grid-template-columns:1fr 1fr}
}
@media(max-width:768px){
  .lp-hero{grid-template-columns:1fr;padding:40px 1rem 48px;gap:32px}
  .lp-phone{display:none}
  .lp-nav-links{display:none}
  .lp-nav-brand-name{display:inline;font-size:.95rem;max-width:60vw;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .lp-auth-logo-name{display:inline}
  .lp-nav-inner{padding:0 1rem;height:60px}
  .lp-hero-h1{font-size:clamp(1.85rem,7vw,2.4rem);line-height:1.1}
  .lp-hero-sub{font-size:.95rem;margin-top:16px;line-height:1.6}
  .lp-hero-actions{margin-top:24px;gap:10px}
  .lp-hero-cta,.lp-hero-ghost{padding:13px 22px;font-size:.88rem;width:100%;justify-content:center}
  .lp-hero-trust{margin-top:24px;gap:14px}
  .lp-hero-trust-item{font-size:.72rem}
  .lp-hero-badge{font-size:.7rem;padding:4px 12px;margin-bottom:18px}
  .lp-section{padding:56px 1rem}
  .lp-section-h2{font-size:clamp(1.5rem,5vw,1.9rem);line-height:1.2}
  .lp-section-sub{font-size:.92rem;margin-bottom:36px;line-height:1.6}
  .lp-section-label{font-size:.66rem;margin-bottom:10px}
  .lp-stats-inner{grid-template-columns:1fr 1fr;padding:0 1rem}
  .lp-stat{border-right:none;border-bottom:1px solid var(--lp-line);padding:24px 12px}
  .lp-stat-val{font-size:1.7rem}
  .lp-stat-label{font-size:.72rem}
  .lp-features-grid{grid-template-columns:1fr;gap:14px}
  .lp-feat{padding:22px}
  .lp-feat-title{font-size:.95rem}
  .lp-feat-body{font-size:.82rem}
  .lp-tpl-grid{grid-template-columns:1fr 1fr;gap:12px}
  .lp-prod-grid{grid-template-columns:1fr}
  .lp-sec-grid{grid-template-columns:1fr;gap:32px}
  .lp-footer-inner{grid-template-columns:1fr 1fr;gap:28px;padding:36px 1rem 24px}
  .lp-auth-root{padding:2rem 1rem}
  .lp-auth-outer{max-width:100%;padding:0;width:100%}
  .lp-auth-card{padding:64px 20px 32px;width:100%}
  .lp-auth-input,.lp-auth-submit{max-width:100%}
  .lp-cta{padding:64px 1rem}
  .lp-cta h2{font-size:clamp(1.5rem,6vw,2rem)}
  .lp-cta p{font-size:.92rem}
  .lp-cta-btn{padding:13px 28px;font-size:.92rem}
  .lp-faq-q{padding:14px 16px;font-size:.88rem}
  .lp-faq-body{padding:0 16px 14px;font-size:.84rem}
}
@media(max-width:480px){
  .lp-hero-h1{font-size:1.7rem}
  .lp-section-h2{font-size:1.4rem}
  .lp-stats-inner{grid-template-columns:1fr 1fr}
  .lp-tpl-grid{grid-template-columns:1fr}
  .lp-footer-inner{grid-template-columns:1fr}
  .lp-auth-row{grid-template-columns:1fr}
}

/* ── Hero layout variants ─────────────────────────────── */
.lp-hero--centered{grid-template-columns:1fr;text-align:center;justify-items:center;padding-top:100px;padding-bottom:120px}
.lp-hero--centered .lp-hero-sub{margin-left:auto;margin-right:auto}
.lp-hero--centered .lp-hero-actions{justify-content:center}
.lp-hero--centered .lp-hero-trust{justify-content:center}
.lp-hero--full-bleed{grid-template-columns:1fr;text-align:center;justify-items:center;max-width:100%;background:var(--lp-gradient);padding:120px 2rem 140px;position:relative;overflow:hidden}
.lp-hero--full-bleed::before{content:'';position:absolute;top:-100px;right:-100px;width:500px;height:500px;border-radius:50%;background:rgba(255,255,255,.07);pointer-events:none;z-index:0}
.lp-hero--full-bleed::after{content:'';position:absolute;bottom:-80px;left:-80px;width:400px;height:400px;border-radius:50%;background:rgba(255,255,255,.04);pointer-events:none;z-index:0}
.lp-hero--full-bleed>div{position:relative;z-index:1}
.lp-hero--minimal{grid-template-columns:1fr;max-width:840px;padding-top:120px;padding-bottom:130px}
.lp-hero--minimal .lp-hero-h1{font-size:clamp(3.2rem,7vw,5.5rem)}
.lp-hero--minimal .lp-hero-sub{font-size:1.1rem;max-width:600px}

/* ── Nav style variants ───────────────────────────────── */
.lp-nav--floating{margin:16px 24px;border-radius:100px;background:var(--lp-card-bg);border:1px solid var(--lp-line);box-shadow:0 8px 32px var(--lp-shadow);backdrop-filter:none;-webkit-backdrop-filter:none;width:calc(100% - 48px)}
.lp-nav--floating .lp-nav-inner{padding:0 20px}
.lp-nav--bar{background:var(--lp-card-bg);backdrop-filter:none;-webkit-backdrop-filter:none;border-bottom:1px solid var(--lp-line)}
.lp-nav--bordered{background:transparent;backdrop-filter:none;-webkit-backdrop-filter:none}

/* ════════ Premium Signature — design-system variants ════════
   Each variant restructures the page body (features, sections,
   typography, density, card shapes). Scoped under .lp-v-* so banks
   without a variant are completely unaffected. */

/* AURORA — neo-bank: glassy cards, glowing gradient tiles, pill CTAs */
.lp-v-aurora .lp-section{padding:104px 1.5rem}
.lp-v-aurora .lp-section-h2{font-size:clamp(1.9rem,3.2vw,2.8rem);letter-spacing:-.03em}
.lp-v-aurora .lp-features-grid{gap:24px}
.lp-v-aurora .lp-feat{padding:32px;border-radius:24px;background:linear-gradient(160deg,var(--lp-card-bg),transparent)}
.lp-v-aurora .lp-feat-icon{width:56px;height:56px;border-radius:18px;background:linear-gradient(135deg,var(--lp-accent),var(--lp-accent-40));box-shadow:0 8px 24px var(--lp-accent-30)}
.lp-v-aurora .lp-feat-icon svg{color:#fff!important;stroke:#fff!important}
.lp-v-aurora .lp-feat-title{font-size:1.12rem}
.lp-v-aurora .lp-prod-card{border-radius:20px}
.lp-v-aurora .lp-hero-cta,.lp-v-aurora .lp-cta-btn{border-radius:100px}

/* EDITORIAL — magazine: serif headings, sharp, numbered feature list */
.lp-v-editorial .lp-hero-h1{font-family:Georgia,'Times New Roman',serif;letter-spacing:-.02em}
.lp-v-editorial .lp-section-h2{font-family:Georgia,'Times New Roman',serif;font-weight:700;font-size:clamp(2rem,3.6vw,3rem);letter-spacing:-.02em}
.lp-v-editorial .lp-section-label{letter-spacing:.26em}
.lp-v-editorial .lp-features-grid{grid-template-columns:1fr;gap:0;counter-reset:lpfeat;max-width:900px;margin:0 auto}
.lp-v-editorial .lp-feat{display:grid;grid-template-columns:60px 1fr;gap:22px;align-items:start;padding:30px 0;border:none;border-top:1px solid var(--lp-line);border-radius:0;background:none}
.lp-v-editorial .lp-feat:hover{transform:none;box-shadow:none}
.lp-v-editorial .lp-feat::before{counter-increment:lpfeat;content:counter(lpfeat,decimal-leading-zero);font-family:Georgia,serif;font-size:1.7rem;font-weight:700;color:var(--lp-accent);opacity:.55;grid-row:span 2}
.lp-v-editorial .lp-feat-icon{display:none}
.lp-v-editorial .lp-feat-title{font-family:Georgia,serif;font-size:1.3rem;font-weight:700}
.lp-v-editorial .lp-hero-cta,.lp-v-editorial .lp-cta-btn{border-radius:2px}

/* CORPORATE — institutional: bordered grid, compact, uppercase labels */
.lp-v-corporate .lp-section{padding:72px 1.5rem}
.lp-v-corporate .lp-section-h2{font-size:clamp(1.5rem,2.2vw,2rem)}
.lp-v-corporate .lp-features-grid{gap:0;border:1px solid var(--lp-line);border-radius:6px;overflow:hidden}
.lp-v-corporate .lp-feat{border-radius:0;border:none;border-right:1px solid var(--lp-line);border-bottom:1px solid var(--lp-line);background:var(--lp-card-bg)}
.lp-v-corporate .lp-feat:hover{transform:none;box-shadow:none;background:var(--lp-accent-12)}
.lp-v-corporate .lp-feat-icon{border-radius:6px;width:40px;height:40px}
.lp-v-corporate .lp-feat-title{font-family:'DM Sans',sans-serif;text-transform:uppercase;letter-spacing:.05em;font-size:.82rem}
.lp-v-corporate .lp-prod-card{border-radius:4px}
.lp-v-corporate .lp-hero-cta,.lp-v-corporate .lp-cta-btn{border-radius:4px}

/* SOFT — friendly: pastel, very rounded, 2-col big cards, pill buttons */
.lp-v-soft .lp-features-grid{grid-template-columns:1fr 1fr;gap:28px}
.lp-v-soft .lp-feat{padding:40px;border-radius:32px;border:none;background:var(--lp-card-bg);box-shadow:0 12px 40px var(--lp-shadow)}
.lp-v-soft .lp-feat-icon{width:60px;height:60px;border-radius:50%}
.lp-v-soft .lp-feat-title{font-size:1.18rem}
.lp-v-soft .lp-section-h2{font-size:clamp(1.8rem,3vw,2.6rem)}
.lp-v-soft .lp-prod-card{border-radius:24px}
.lp-v-soft .lp-sec-item{border-radius:24px}
.lp-v-soft .lp-hero-cta,.lp-v-soft .lp-cta-btn,.lp-v-soft .lp-hero-ghost,.lp-v-soft .lp-btn-outline{border-radius:100px}

/* MINIMAL — understated: hairline, wide tracking, no card boxes */
.lp-v-minimal .lp-section{padding:118px 1.5rem}
.lp-v-minimal .lp-section-h2{font-family:'DM Sans',sans-serif;font-weight:500;font-size:clamp(1.6rem,2.4vw,2rem);letter-spacing:.005em}
.lp-v-minimal .lp-section-label{font-family:ui-monospace,'SFMono-Regular',Menlo,monospace;letter-spacing:.2em;font-weight:500}
.lp-v-minimal .lp-features-grid{grid-template-columns:repeat(2,1fr);gap:48px 44px}
.lp-v-minimal .lp-feat{padding:0;border:none;border-radius:0;background:none}
.lp-v-minimal .lp-feat:hover{transform:none;box-shadow:none}
.lp-v-minimal .lp-feat-icon{width:34px;height:34px;border-radius:0;background:none;margin-bottom:14px}
.lp-v-minimal .lp-feat-icon svg{color:var(--lp-accent)!important;stroke:var(--lp-accent)!important}
.lp-v-minimal .lp-feat-title{font-weight:600;font-size:.96rem}
.lp-v-minimal .lp-hero-cta,.lp-v-minimal .lp-cta-btn{border-radius:0}

/* VIVID — energetic: gradient headings, staggered grid, bold circles */
.lp-v-vivid .lp-section-h2{background:linear-gradient(90deg,var(--lp-accent),var(--lp-fg));-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;font-size:clamp(1.9rem,3.4vw,2.9rem)}
.lp-v-vivid .lp-features-grid{gap:22px}
.lp-v-vivid .lp-feat{border-radius:20px;border:none;background:var(--lp-card-bg);box-shadow:0 10px 30px var(--lp-shadow)}
.lp-v-vivid .lp-feat:nth-child(even){transform:translateY(24px)}
.lp-v-vivid .lp-feat:nth-child(even):hover{transform:translateY(20px)}
.lp-v-vivid .lp-feat-icon{width:54px;height:54px;border-radius:50%;background:linear-gradient(135deg,var(--lp-accent),var(--lp-accent-40))}
.lp-v-vivid .lp-feat-icon svg{color:#fff!important;stroke:#fff!important}
.lp-v-vivid .lp-hero-cta,.lp-v-vivid .lp-cta-btn{border-radius:16px}

/* Variant responsive: keep feature grids usable on small screens */
@media (max-width:768px){
  .lp-v-soft .lp-features-grid,.lp-v-minimal .lp-features-grid,.lp-v-corporate .lp-features-grid{grid-template-columns:1fr}
  .lp-v-vivid .lp-feat:nth-child(even){transform:none}
  .lp-v-editorial .lp-feat{grid-template-columns:40px 1fr;gap:16px;padding:24px 0}
}

@media(max-width:768px){
  .lp-hero--centered{padding-top:64px;padding-bottom:80px}
  .lp-hero--full-bleed{padding:80px 1.5rem 100px}
  .lp-hero--minimal{padding-top:72px;padding-bottom:80px}
  .lp-nav--floating{margin:8px 12px;border-radius:60px;width:calc(100% - 24px)}
}
`;

const INVEST_PRODUCTS = [
  { key: "realestate", rate: "8–14%", color: "#0ea5e9" },
  { key: "crowdfunding", rate: "6–12%", color: "#8b5cf6" },
  { key: "renewable", rate: "5–10%", color: "#16a34a" },
  { key: "stocks", rate: null, color: "#f59e0b" },
  { key: "participatory", rate: "4–9%", color: "#ec4899" },
  { key: "crypto", rate: null, color: "#f97316" },
];

const CREDIT_PRODUCTS_LP = [
  { key: "personal", rate: "2%", color: "#2563eb" },
  { key: "realestate", rate: "2%", color: "#0ea5e9" },
  { key: "auto", rate: "2%", color: "#f59e0b" },
  { key: "student", rate: "2%", color: "#8b5cf6" },
  { key: "pro", rate: "2%", color: "#16a34a" },
  { key: "express", rate: "3%", color: "#ec4899" },
];

const FAQ_KEYS = [
  { qk: "home.faq.q1", ak: "home.faq.a1" },
  { qk: "home.faq.q2", ak: "home.faq.a2" },
  { qk: "home.faq.q3", ak: "home.faq.a3" },
  { qk: "home.faq.q4", ak: "home.faq.a4" },
  { qk: "home.faq.q5", ak: "home.faq.a5" },
  { qk: "home.faq.q6", ak: "home.faq.a6" },
];

// Tries multiple logo sources in cascade. Validates that loaded images
// are large enough (some services return 1x1 placeholders that wouldn't
// trigger onError but leave the card visually empty).
function BankLogoItem({ bank }) {
  const sources = useMemo(() => {
    const list = [];
    if (bank.logo_url) list.push(bank.logo_url);
    const d = bank.domain ? encodeURIComponent(bank.domain) : "";
    if (d) {
      list.push(`https://logo.clearbit.com/${d}?size=200`);
      list.push(`https://icons.duckduckgo.com/ip3/${d}.ico`);
      list.push(`https://icon.horse/icon/${d}`);
      list.push(`https://www.google.com/s2/favicons?sz=128&domain=${d}`);
    }
    return list;
  }, [bank.logo_url, bank.domain]);
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const next = () => {
    if (idx + 1 < sources.length) setIdx(idx + 1);
    else setFailed(true);
  };
  const onLoad = (e) => {
    // Reject tiny placeholders (1x1 trackers, empty favicons).
    if (e.currentTarget.naturalWidth < 24 || e.currentTarget.naturalHeight < 24) next();
  };
  // No working logo source → render the bank's name as text so the carousel
  // never collapses to an empty title.
  if (failed || sources.length === 0) {
    return (
      <div className="lp-trusted-item lp-trusted-item--text" title={bank.name} data-testid="trusted-bank-text">
        <span className="lp-trusted-text">{bank.name}</span>
      </div>
    );
  }
  return (
    <div className="lp-trusted-item" title={bank.name} data-testid="trusted-bank-logo">
      <img
        src={sources[idx]}
        alt={bank.name}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={next}
        onLoad={onLoad}
      />
    </div>
  );
}

function BankHome() {
  const { bank, slug, langOverride, isDemo, basePath } = useContext(BankCtx);
  const style = useStyle(bank);
  const [faqOpen, setFaqOpen] = useState(null);
  const [trustedBanks, setTrustedBanks] = useState([]);
  useEffect(() => {
    api.get("/public/trusted-banks").then((r) => setTrustedBanks(r.data || [])).catch(() => {});
  }, []);
  const { t } = useTranslation(langOverride || bank?.default_locale || "fr");

  // Overlay admin content translations for current locale
  const locale = langOverride || bank?.default_locale || "fr";
  const defLoc = bank?.default_locale || "fr";
  const localeContent = bank?.content_translations?.[locale] || {};
  const sameLoc = locale === defLoc;
  // Custom branding text (typed by the admin in ONE language) must only be used
  // when we are displaying in that same language, OR when a per-locale translation
  // exists. Otherwise we fall back to the fully-translated t() defaults — this
  // prevents the FR/EN mixing where custom features stayed French on an English page.
  // Backend stores French auto-defaults when admin doesn't customize — detect and
  // ignore those so t() fallbacks kick in even when sameLoc is true.
  const isAutoDefault = (field, value) => {
    if (value == null) return false;
    if (field === "cta_text") return value === "Ouvrir un compte";
    if (field === "hero_title") return value === `Bienvenue sur ${bank?.name}`;
    if (field === "hero_subtitle") return value === (bank?.tagline || "Votre banque, en ligne, en quelques clics.");
    if (field === "features") {
      return Array.isArray(value) && value.length === 3
        && value[0]?.title === "Comptes sécurisés"
        && value[1]?.title === "Cartes virtuelles"
        && value[2]?.title === "Virements rapides";
    }
    return false;
  };
  const pickCustom = (field) => {
    const overlay = localeContent[field];
    if (overlay !== undefined) return overlay;
    if (!sameLoc) return undefined;
    const v = bank?.[field];
    if (isAutoDefault(field, v)) return undefined;
    return v;
  };

  const cta = pickCustom("cta_text") || t("home.footer.signup");
  const heroLayout = style.heroLayout || "split";
  const navStyle = style.navStyle || "glassmorphism";
  const layoutVariant = style.layoutVariant || "";
  // Filter out empty items so a partially-saved content_translations doesn't
  // result in blank feature cards (regression seen in iteration 8).
  const customFeatures = (localeContent.features ?? (sameLoc ? bank?.features : undefined))?.filter(
    (f) => f && (f.title || f.body),
  );
  const features = (customFeatures && customFeatures.length) ? customFeatures : [
    { title: t("home.feature.security"), body: t("home.feature.security_body"), icon: Shield },
    { title: t("home.feature.cards"), body: t("home.feature.cards_body"), icon: CardIcon },
    { title: t("home.feature.transfers"), body: t("home.feature.transfers_body"), icon: Send },
    { title: t("home.feature.analytics"), body: t("home.feature.analytics_body"), icon: TrendingUp },
    { title: t("home.feature.investments"), body: t("home.feature.investments_body"), icon: Briefcase },
    { title: t("home.feature.support"), body: t("home.feature.support_body"), icon: MessageCircle },
  ];
  const heroTagline = pickCustom("tagline") || t("home.hero.tagline");
  const heroTitleCustom = pickCustom("hero_title");
  const heroSubtitle = pickCustom("hero_subtitle") || pickCustom("tagline") || t("home.hero.sub");
  const footerTagline = pickCustom("tagline") || t("home.footer.tagline");

  const cssVars = {
    "--lp-accent": style.accent,
    "--lp-accent-12": style.accent + "1F",
    "--lp-accent-20": style.accent + "33",
    "--lp-accent-25": style.accent + "40",
    "--lp-accent-30": style.accent + "4D",
    "--lp-accent-35": style.accent + "59",
    "--lp-accent-40": style.accent + "66",
    "--lp-accent-45": style.accent + "73",
    "--lp-fg": style.fg,
    "--lp-fg-06": style.fg + "0F",
    "--lp-fg-20": style.fg + "33",
    "--lp-fg-40": style.fg + "66",
    "--lp-bg": style.bg,
    "--lp-bg-glass": style.bg + "E6",
    "--lp-card-bg": style.surface,
    "--lp-line": style.surfaceBorder,
    "--lp-dark-bg": style.isDark ? "#0a0a12" : "#0F172A",
    "--lp-gradient": style.cardGradient,
    "--lp-shadow": style.fg + "18",
  };

  const TPL_PREVIEWS = [
    { id: "tpl-minimalist-mono", label: "Mono", bg: "#0A0A0A", accent: "#fff", name: "Minimalist" },
    { id: "tpl-warm-premium", label: "Warm", bg: "#1F2A1F", accent: "#4ade80", name: "Premium" },
    { id: "tpl-tech-neon", label: "Neon", bg: "#0B0B14", accent: "#22D3EE", name: "Tech" },
    { id: "tpl-classic-financial", label: "Blue", bg: "#1D4ED8", accent: "#fff", name: "Classic" },
  ];

  return (
    <div className={`lp-root${layoutVariant ? " lp-v-" + layoutVariant : ""}`} style={cssVars} data-testid="bank-landing">
      <style>{LANDING_CSS}</style>
      {isDemo && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, background: "#1e293b", color: "#fff", textAlign: "center", padding: "10px 16px", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }} data-testid="demo-banner">
          {t("demo.banner")}
        </div>
      )}

      {/* ── Navbar ── */}
      <nav className={`lp-nav lp-nav--${navStyle}`}>
        <div className="lp-nav-inner">
          <Link to={basePath || "/"} className="lp-nav-brand" style={{ textDecoration: "none", color: "inherit" }}>
            {bank.logo_url
              ? <img src={bank.logo_url} alt={bank.name} style={{ height: 34, width: 34, objectFit: "contain", borderRadius: 8 }} />
              : <div style={{ width: 34, height: 34, borderRadius: 10, background: style.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 15 }}>{bank.name[0]}</div>
            }
            <span className="lp-nav-brand-name">{bank.name}</span>
          </Link>
          <div className="lp-nav-links">
            {[["#features", t("home.nav.features")],["#security", t("home.nav.security")],["#invest", t("home.nav.invest")],["#credits", t("home.nav.credits")],["#faq", t("home.nav.faq")]].map(([href, label]) => (
              <a key={href} href={href} className="lp-nav-link">{label}</a>
            ))}
          </div>
          <div className="lp-nav-ctas">
            <Link to={`${basePath}/login`} style={{ textDecoration: "none" }}>
              <button className="lp-btn-outline" data-testid="bank-nav-login">{t("home.nav.login")}</button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero (layout adapts to template) ── */}
      {(() => {
        const wt = heroLayout === "full-bleed";
        const heroInner = (
          <div>
            <div className="lp-hero-badge" style={wt ? { background:"rgba(255,255,255,.15)", borderColor:"rgba(255,255,255,.35)", color:"#fff" } : {}}>
              <Sparkles size={13} />
              {heroTagline}
            </div>
            <h1 className="lp-hero-h1" style={wt ? { color:"#fff" } : {}}>
              {heroTitleCustom
                ? heroTitleCustom
                : <>{t("home.hero.title1")}<br /><em style={wt ? { color:"rgba(255,255,255,.85)" } : {}}>{t("home.hero.title2")}</em></>
              }
            </h1>
            <p className="lp-hero-sub" style={wt ? { color:"rgba(255,255,255,.78)" } : {}}>
              {heroSubtitle}
            </p>
            <div className="lp-hero-actions">
              {bank.signup_enabled !== false && (
                <Link to={`${basePath}/signup`} style={{ textDecoration: "none" }}>
                  <button className="lp-hero-cta" style={wt ? { background:"#fff", color:style.accent, boxShadow:"0 6px 28px rgba(0,0,0,.3)" } : {}} data-testid="bank-cta-signup">
                    {cta} <ArrowUpRight size={16} />
                  </button>
                </Link>
              )}
              <Link to={`${basePath}/login`} style={{ textDecoration: "none" }}>
                <button className="lp-hero-ghost" style={wt ? { borderColor:"rgba(255,255,255,.4)", color:"#fff" } : {}}>{t("home.hero.login")}</button>
              </Link>
            </div>
            <div className="lp-hero-trust">
              {[{ v:<Lock size={12}/>, l:t("home.hero.trust.secure") }, { v:<Zap size={12}/>, l:t("home.hero.trust.fast") }, { v:<Shield size={12}/>, l:t("home.hero.trust.encrypted") }, { v:<Star size={12}/>, l:t("home.hero.trust.support") }].map((ti) => (
                <div key={ti.l} className="lp-hero-trust-item" style={wt ? { color:"rgba(255,255,255,.75)", opacity:1 } : {}}>{ti.v}<span>{ti.l}</span></div>
              ))}
            </div>
          </div>
        );
        const phoneMockup = (
          <div style={{ display:"flex", justifyContent:"center" }}>
            <div className="lp-phone">
              <div className="lp-phone-shell">
                <div className="lp-phone-bar"><span>9:41</span><span>⚡ 100%</span></div>
                <div className="lp-phone-card">
                  <div className="lp-phone-bal-label">{t("home.mockup.balance")}</div>
                  <div className="lp-phone-bal-amount">12 487,50</div>
                  <div className="lp-phone-bal-cur">{t("home.mockup.currency")}</div>
                  <div className="lp-phone-actions">
                    <span className="lp-phone-act">{t("home.mockup.recharge")}</span>
                    <span className="lp-phone-act">{t("home.mockup.send")}</span>
                    <span className="lp-phone-act">{t("home.mockup.save")}</span>
                  </div>
                </div>
                {[
                  { icon:<TrendingUp size={14} color={style.accent}/>, label:t("home.mockup.salary"), sub:t("home.mockup.days2"), amt:"+3 200 €", type:"credit" },
                  { icon:<Send size={14} color={style.accent}/>, label:t("home.mockup.rent"), sub:t("home.mockup.days3"), amt:"-850 €", type:"debit" },
                  { icon:<Briefcase size={14} color={style.accent}/>, label:t("home.mockup.investment"), sub:t("home.mockup.days5"), amt:"+124 €", type:"credit" },
                ].map((tx, i) => (
                  <div key={i} className="lp-phone-row">
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div className="lp-phone-row-icon">{tx.icon}</div>
                      <div>
                        <div className="lp-phone-row-label">{tx.label}</div>
                        <div className="lp-phone-row-sub">{tx.sub}</div>
                      </div>
                    </div>
                    <div className={`lp-phone-row-amt ${tx.type}`}>{tx.amt}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        if (heroLayout === "split") return (
          <section className="lp-hero lp-hero--split">{heroInner}{phoneMockup}</section>
        );
        if (heroLayout === "centered") return (
          <section className="lp-hero lp-hero--centered">{heroInner}</section>
        );
        if (heroLayout === "full-bleed") return (
          <div className="lp-hero lp-hero--full-bleed">{heroInner}</div>
        );
        if (heroLayout === "minimal") return (
          <section className="lp-hero lp-hero--minimal">{heroInner}</section>
        );
        return <section className="lp-hero lp-hero--split">{heroInner}{phoneMockup}</section>;
      })()}

      {/* ── Stats ── */}
      <div className="lp-stats">
        <div className="lp-stats-inner">
          {[{ val: "+50K", label: t("home.stats.accounts") }, { val: "99.9%", label: t("home.stats.uptime") }, { val: "< 2s", label: t("home.stats.transfers") }, { val: "24/7", label: t("home.stats.support") }].map((s) => (
            <div key={s.label} className="lp-stat">
              <div className="lp-stat-val">{s.val}</div>
              <div className="lp-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <section id="features" className="lp-section">
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div className="lp-section-label">{t("home.features.label")}</div>
          <h2 className="lp-section-h2">{t("home.features.title")}</h2>
          <p className="lp-section-sub">{t("home.features.sub")}</p>
        </div>
        <div className="lp-features-grid">
          {features.slice(0, 6).map((f, i) => {
            const defaultIcons = [Shield, CardIcon, Send, TrendingUp, Briefcase, MessageCircle];
            const Ic = f.icon || defaultIcons[i % defaultIcons.length];
            return (
              <div key={i} className="lp-feat">
                <div className="lp-feat-icon"><Ic size={22} color={style.accent} /></div>
                <h3 className="lp-feat-title">{f.title}</h3>
                <p className="lp-feat-body">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Templates showcase ── */}
      <div className="lp-dark">
        <div className="lp-dark-inner">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: ".72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".13em", color: style.accent, marginBottom: 12 }}>{t("home.tpl.label")}</div>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: "clamp(1.6rem,2.6vw,2.1rem)", fontWeight: 700, color: "#fff", margin: "0", letterSpacing: "-.012em" }}>{t("home.tpl.title")}</h2>
          </div>
          <div className="lp-trusted-wrap">
            <div className="lp-trusted-track">
              {[...trustedBanks, ...trustedBanks].map((b, i) => (
                <BankLogoItem key={`${b.id}-${i}`} bank={b} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Security ── */}
      <section id="security" className="lp-section">
        <div className="lp-sec-grid">
          <div style={{ textAlign: "center" }}>
            <div className="lp-sec-shield">
              <Shield size={52} color={style.accent} />
            </div>
            <div className="lp-section-label" style={{ textAlign: "center" }}>{t("home.security.label")}</div>
            <h2 className="lp-section-h2" style={{ textAlign: "center" }}>{t("home.security.title1")}<br />{t("home.security.title2")}</h2>
            <p style={{ fontSize: "1rem", opacity: .65, lineHeight: 1.7, maxWidth: 360, margin: "0 auto" }}>{t("home.security.sub")}</p>
          </div>
          <div className="lp-sec-items">
            {[
              { title: t("home.security.aes.title"), body: t("home.security.aes.body"), Ic: Lock },
              { title: t("home.security.auth.title"), body: t("home.security.auth.body"), Ic: Key },
              { title: t("home.security.compliance.title"), body: t("home.security.compliance.body"), Ic: ShieldCheck },
            ].map(({ title, body, Ic }) => (
              <div key={title} className="lp-sec-item">
                <div className="lp-sec-item-icon"><Ic size={18} color={style.accent} /></div>
                <div>
                  <p className="lp-sec-item-title">{title}</p>
                  <p className="lp-sec-item-body">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Investments ── */}
      <div id="invest" className="lp-dark">
        <div className="lp-dark-inner">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: ".72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".13em", color: style.accent, marginBottom: 12 }}>{t("home.invest.label")}</div>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: "clamp(1.6rem,2.6vw,2.1rem)", fontWeight: 700, color: "#fff", margin: "0 0 14px", letterSpacing: "-.012em" }}>{t("home.invest.title")}</h2>
            <p style={{ fontSize: "1rem", color: "rgba(255,255,255,.55)", margin: "0 auto", maxWidth: 480, lineHeight: 1.7 }}>{t("home.invest.sub")}</p>
          </div>
          <div className="lp-prod-grid">
            {INVEST_PRODUCTS.map((p) => (
              <div key={p.key} className="lp-prod-card" style={{ background: "rgba(255,255,255,.04)", borderColor: "rgba(255,255,255,.08)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="lp-prod-dot" style={{ background: p.color }} />
                  <p className="lp-prod-name" style={{ color: "#fff" }}>{t("home.invest.p." + p.key)}</p>
                </div>
                <p className="lp-prod-rate" style={{ color: "rgba(255,255,255,.45)" }}>{t("home.invest.p." + p.key + "_desc")}</p>
                <div>
                  <span className="lp-prod-badge" style={{ background: p.color + "22", color: p.color }}>{t("home.invest.yield")} {p.rate || t("home.invest.p.variable")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Credits ── */}
      <section id="credits" className="lp-section">
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div className="lp-section-label">{t("home.credits.label")}</div>
          <h2 className="lp-section-h2">{t("home.credits.title")}</h2>
          <p className="lp-section-sub">{t("home.credits.sub")}</p>
        </div>
        <div className="lp-prod-grid">
          {CREDIT_PRODUCTS_LP.map((p) => (
            <div key={p.key} className="lp-prod-card">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="lp-prod-dot" style={{ background: p.color }} />
                <p className="lp-prod-name">{t("home.credits.p." + p.key)}</p>
              </div>
              <div>
                <span className="lp-prod-badge" style={{ background: p.color + "18", color: p.color }}>{t("home.credits.rate.from", { rate: p.rate })}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <div className="lp-dark" id="faq">
        <div className="lp-dark-inner">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: ".72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".13em", color: style.accent, marginBottom: 12 }}>{t("home.faq.label")}</div>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: "clamp(1.6rem,2.6vw,2.1rem)", fontWeight: 700, color: "#fff", margin: "0 0 14px", letterSpacing: "-.012em" }}>{t("home.faq.title")}</h2>
          </div>
          <div className="lp-faq">
            {FAQ_KEYS.map((item, i) => (
              <div key={i} className="lp-faq-item" style={{ background: "rgba(255,255,255,.04)", borderColor: "rgba(255,255,255,.08)" }}>
                <button className="lp-faq-q" style={{ color: "#fff" }} onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                  {t(item.qk)}
                  <ChevronRight size={18} className={`lp-faq-chevron${faqOpen === i ? " open" : ""}`} style={{ transform: faqOpen === i ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .25s", flexShrink: 0, opacity: .5 }} />
                </button>
                {faqOpen === i && <p className="lp-faq-body" style={{ color: "rgba(255,255,255,.65)" }}>{t(item.ak)}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      {bank.signup_enabled !== false && (
        <div className="lp-cta">
          <h2>{t("home.cta.title")}</h2>
          <p>{t("home.cta.sub", { bank: bank.name })}</p>
          <Link to={`${basePath}/signup`} style={{ textDecoration: "none" }}>
            <button className="lp-cta-btn" data-testid="bank-cta-bottom">
              {cta} <ArrowUpRight size={18} />
            </button>
          </Link>
        </div>
      )}

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div>
            <div className="lp-footer-brand">
              {bank.logo_url
                ? <img src={bank.logo_url} alt={bank.name} style={{ height: 28, width: 28, objectFit: "contain", borderRadius: 6 }} />
                : <div style={{ width: 28, height: 28, borderRadius: 8, background: style.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 13 }}>{bank.name[0]}</div>
              }
              <span className="lp-footer-brand-name">{bank.name}</span>
            </div>
            <p className="lp-footer-tagline">{footerTagline}</p>
            {(bank.contact_email || bank.contact_phone || bank.bank_address) && (
              <div style={{ marginTop: 14 }}>
                {bank.contact_email && (
                  <p className="lp-footer-contact-item"><Mail size={13} /><span>{bank.contact_email}</span></p>
                )}
                {bank.contact_phone && (
                  <p className="lp-footer-contact-item"><Phone size={13} /><span>{bank.contact_phone}</span></p>
                )}
                {bank.bank_address && (
                  <p className="lp-footer-contact-item"><MapPin size={13} /><span>{bank.bank_address}</span></p>
                )}
              </div>
            )}
            {(bank.facebook_url || bank.instagram_url || bank.tiktok_url || bank.whatsapp_url || bank.twitter_url || bank.linkedin_url || bank.threads_url) && (
              <div className="lp-footer-social">
                {bank.facebook_url && (
                  <a href={bank.facebook_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="Facebook" aria-label="Facebook">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/></svg>
                  </a>
                )}
                {bank.instagram_url && (
                  <a href={bank.instagram_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="Instagram" aria-label="Instagram">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" stroke="none"/></svg>
                  </a>
                )}
                {bank.twitter_url && (
                  <a href={bank.twitter_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="X (Twitter)" aria-label="X">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21.5l-7.36 8.408L23 22h-6.828l-5.34-6.99L4.76 22H1.5l7.88-9.005L1 2h6.984l4.82 6.378L18.244 2zm-1.2 18h1.88L7.05 4H5.04l12 16z"/></svg>
                  </a>
                )}
                {bank.linkedin_url && (
                  <a href={bank.linkedin_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="LinkedIn" aria-label="LinkedIn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.66H9.36V9h3.41v1.57h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.27zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
                  </a>
                )}
                {bank.threads_url && (
                  <a href={bank.threads_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="Threads" aria-label="Threads">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.42 11.13c-.08-.04-.16-.07-.24-.11-.14-2.6-1.56-4.08-3.95-4.1h-.03c-1.43 0-2.62.61-3.36 1.72l1.32.9c.55-.83 1.41-1.01 2.04-1.01 1.32.01 2.01.79 2.13 2.36-.6-.1-1.25-.13-1.94-.09-1.95.11-3.21 1.25-3.12 2.83.04.8.44 1.49 1.12 1.94.57.38 1.31.57 2.08.53 1.02-.06 1.82-.45 2.39-1.16.43-.54.7-1.25.83-2.12.51.31.89.71 1.1 1.2.36.82.38 2.17-.72 3.27-.96.96-2.12 1.38-3.86 1.39-1.94-.01-3.4-.63-4.36-1.83-.9-1.13-1.36-2.76-1.38-4.84.02-2.08.48-3.71 1.38-4.84.96-1.21 2.42-1.82 4.36-1.83 1.95.01 3.44.62 4.43 1.82.49.6.85 1.35 1.1 2.23l1.55-.42c-.31-1.06-.79-1.99-1.44-2.78-1.28-1.55-3.16-2.34-5.6-2.36h-.01c-2.42.02-4.27.82-5.51 2.39C3.93 7.62 3.41 9.5 3.39 12s.54 4.38 1.6 5.7c1.24 1.57 3.1 2.37 5.51 2.39h.01c2.15-.02 3.66-.58 4.91-1.83 1.63-1.63 1.58-3.67 1.04-4.92-.29-.67-.74-1.22-1.39-1.63-.08-.05-.16-.09-.24-.13-.03-.02-.07-.04-.11-.06-.07-.03-.12-.05-.17-.07.27-.27.43-.46.43-.46-.46-1.16-1.5-1.61-2.4-1.61-.92 0-1.95.5-2.45 1.92l1.4.61c.27-.74.79-.95 1.05-.95.49 0 .73.36.84.59.05-.07.15-.18.27-.31z"/></svg>
                  </a>
                )}
                {bank.tiktok_url && (
                  <a href={bank.tiktok_url} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="TikTok" aria-label="TikTok">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.77a4.85 4.85 0 0 1-1.01-.08z"/></svg>
                  </a>
                )}
                {bank.whatsapp_url && (
                  <a href={(() => {
                    // Accept either a full URL (wa.me / api.whatsapp.com / etc.)
                    // or a raw phone number; in the latter case build a wa.me link.
                    const v = String(bank.whatsapp_url).trim();
                    if (/^https?:\/\//i.test(v)) return v;
                    // Strip non-digit chars then prepend country if missing
                    const digits = v.replace(/[^\d]/g, "");
                    return digits ? `https://wa.me/${digits}` : "#";
                  })()} target="_blank" rel="noopener noreferrer" className="lp-footer-social-btn" title="WhatsApp" aria-label="WhatsApp">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.78 11.78 0 0 0 12.03 0C5.5 0 .19 5.31.19 11.85a11.83 11.83 0 0 0 1.59 5.93L0 24l6.36-1.67a11.83 11.83 0 0 0 5.67 1.44h.01c6.53 0 11.84-5.31 11.85-11.85a11.78 11.78 0 0 0-3.37-8.44zM12.04 21.79h-.01a9.84 9.84 0 0 1-5.01-1.37l-.36-.21-3.78 1 1.01-3.68-.23-.38a9.83 9.83 0 0 1-1.51-5.3c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.1 1.03 6.96 2.89a9.78 9.78 0 0 1 2.89 6.97c0 5.43-4.42 9.85-9.85 9.85zm5.4-7.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.16-.17.2-.34.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.61-.91-2.2-.24-.58-.49-.5-.66-.51l-.56-.01a1.07 1.07 0 0 0-.78.37c-.27.3-1.03 1.01-1.03 2.46 0 1.45 1.06 2.85 1.21 3.05.15.2 2.09 3.19 5.06 4.47.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.75-.71 2-1.4.25-.69.25-1.28.17-1.4-.07-.13-.27-.2-.57-.35z"/></svg>
                  </a>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="lp-footer-col-title">{t("home.footer.products")}</div>
            {[t("home.footer.accounts"), t("home.footer.transfers"), t("home.footer.cards"), t("home.footer.savings")].map((l) => (
              <Link key={l} to={`${basePath}/login`} className="lp-footer-link">{l}</Link>
            ))}
          </div>
          <div>
            <div className="lp-footer-col-title">{t("home.footer.services")}</div>
            {[t("home.footer.investments"), t("home.footer.credits"), t("home.footer.financing"), t("home.footer.messages")].map((l) => (
              <Link key={l} to={`${basePath}/login`} className="lp-footer-link">{l}</Link>
            ))}
          </div>
          <div>
            <div className="lp-footer-col-title">{t("home.footer.access")}</div>
            <Link to={`${basePath}/login`} className="lp-footer-link">{t("home.footer.login")}</Link>
            {bank.signup_enabled !== false && <Link to={`${basePath}/signup`} className="lp-footer-link">{t("home.footer.signup")}</Link>}
            <Link to={`${basePath}/login`} className="lp-footer-link">{t("home.footer.support")}</Link>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span className="lp-footer-copy">{t("home.footer.copyright", { year: new Date().getFullYear(), bank: bank.name })}</span>
          <span className="lp-footer-copy">{t("home.footer.powered", { bank: bank.name })}</span>
        </div>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* AUTH — login / signup                                                       */
/* -------------------------------------------------------------------------- */
function BankAuth({ mode }) {
  const { bank, slug, langOverride, isDemo, basePath } = useContext(BankCtx);
  const style = useStyle(bank);
  const nav = useNavigate();
  const isSignup = mode === "signup";
  const [form, setForm] = useState({
    email: "", password: "", pin: "", pinConfirm: "",
    firstName: "", lastName: "", phoneLocal: "", country: "", address: "",
    locale: langOverride || bank?.default_locale || "fr", currency: "EUR",
    kyc_id_type: "id_card",
    kyc_id_recto: null, kyc_id_verso: null, kyc_selfie: null, kyc_address_proof: null,
  });
  const [busy, setBusy] = useState(false);
  const [pendingSignup, setPendingSignup] = useState(false);
  const [pinStep, setPinStep] = useState(false);   // true after password OK, awaiting PIN
  const [tempToken, setTempToken] = useState("");
  const [pinValue, setPinValue] = useState("");
  const selectedCountry = COUNTRIES.find((c) => c.name === form.country);
  const { t } = useTranslation(form.locale || langOverride || bank?.default_locale || "fr");

  const onCountryChange = (countryName) => {
    const c = COUNTRIES.find((x) => x.name === countryName);
    if (!c) { setForm({ ...form, country: countryName }); return; }
    setForm({ ...form, country: c.name, locale: c.lang, currency: c.currency });
  };

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleKycFile = async (field, file) => {
    if (!file) return;
    const MAX = 5 * 1024 * 1024;
    if (file.size > MAX) { toast.error(t("kyc.file_too_large")); return; }
    try {
      const url = await readFileAsDataURL(file);
      setForm((f) => ({ ...f, [field]: { url, name: file.name, size: file.size } }));
    } catch { toast.error(t("kyc.file_read_error")); }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Demo mode: verify admin credentials before granting access
    if (isDemo) {
      setBusy(true);
      try {
        await api.post("/demo/login", { email: form.email, password: form.password });
        localStorage.setItem(TOKEN_KEY(slug), "demo_token");
        nav(`${basePath}/app`);
      } catch (err) { toast.error(tError(err, t)); }
      finally { setBusy(false); }
      return;
    }
    if (isSignup) {
      if (!form.country) { toast.error(t("auth.error.select_country")); return; }
      if (!form.pin || form.pin.length !== 4 || !/^\d{4}$/.test(form.pin)) {
        toast.error(t("auth.pin.length_error") || "Le Code PIN doit contenir exactement 4 chiffres"); return;
      }
      if (form.pin !== form.pinConfirm) {
        toast.error(t("auth.pin.mismatch") || "Les deux codes PIN ne correspondent pas"); return;
      }
      const errs = [];
      if (!form.kyc_id_recto) errs.push(t("kyc.recto_id"));
      if (form.kyc_id_type !== "passport" && !form.kyc_id_verso) errs.push(t("kyc.verso"));
      if (!form.kyc_selfie) errs.push(t("kyc.selfie"));
      if (!form.kyc_address_proof) errs.push(t("kyc.address_proof"));
      if (errs.length) { toast.error(errs[0]); return; }
    }
    setBusy(true);
    try {
      const endpoint = mode === "signup" ? `/public/bank/${slug}/signup` : `/public/bank/${slug}/login`;
      const payload = isSignup ? {
        email: form.email,
        password: form.password,
        pin: form.pin,
        first_name: form.firstName,
        last_name: form.lastName,
        phone: selectedCountry ? `${selectedCountry.phone} ${form.phoneLocal}`.trim() : form.phoneLocal,
        country: form.country,
        address: form.address,
        locale: form.locale,
        currency: form.currency,
        kyc_id_type: form.kyc_id_type,
        kyc_id_recto: form.kyc_id_recto?.url || null,
        kyc_id_verso: form.kyc_id_verso?.url || null,
        kyc_selfie: form.kyc_selfie?.url || null,
        kyc_address_proof: form.kyc_address_proof?.url || null,
      } : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      if (mode === "signup" && data.status === "pending") { setPendingSignup(true); return; }
      // 2-step login: password OK, now need PIN
      if (data.pin_required) {
        setTempToken(data.temp_token);
        setPinStep(true);
        return;
      }
      localStorage.setItem(TOKEN_KEY(slug), data.token);
      toast.success(mode === "signup" ? t("pending.title") : `${t("dash.welcome")}, ${data.name}`);
      nav(`${basePath}/app`);
    } catch (err) { toast.error(tError(err, t)); }
    finally { setBusy(false); }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (!pinValue || pinValue.length !== 4) { toast.error(t("auth.pin.length_error") || "Le Code PIN doit contenir 4 chiffres"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/bank/${slug}/verify-pin`, { temp_token: tempToken, pin: pinValue });
      localStorage.setItem(TOKEN_KEY(slug), data.token);
      toast.success(`${t("dash.welcome")}, ${data.name}`);
      nav(`${basePath}/app`);
    } catch (err) {
      toast.error(tError(err, t));
      setPinValue("");
    }
    finally { setBusy(false); }
  };

  const cta = (bank.cta_text && bank.cta_text !== "Ouvrir un compte") ? bank.cta_text : t("home.footer.signup");

  const KycFile = ({ field, label, hint }) => {
    const ref = useRef(null);
    const f = form[field];
    return (
      <>
        <input ref={ref} type="file" accept="image/*,application/pdf" hidden onChange={(e) => handleKycFile(field, e.target.files?.[0])} />
        <div className={`lp-kyc-file ${f ? "filled" : ""}`} onClick={() => ref.current?.click()}>
          <div className="lp-kyc-file-icon">
            {f ? <CheckCircle2 size={16} /> : <FileText size={16} />}
          </div>
          <div className="lp-kyc-file-text">
            <div className="lp-kyc-file-label">{label}</div>
            <div className="lp-kyc-file-meta">{f ? `${f.name} · ${(f.size/1024).toFixed(0)} Ko` : hint}</div>
          </div>
          <div className="lp-kyc-file-action">{f ? t("kyc.replace") : t("kyc.upload")}</div>
        </div>
      </>
    );
  };

  const cssVars = {
    "--lp-accent": style.accent,
    "--lp-accent-12": style.accent + "1F",
    "--lp-accent-20": style.accent + "33",
    "--lp-accent-25": style.accent + "40",
    "--lp-accent-30": style.accent + "4D",
    "--lp-accent-35": style.accent + "59",
    "--lp-accent-40": style.accent + "66",
    "--lp-accent-45": style.accent + "73",
    "--lp-fg": style.fg,
    "--lp-fg-06": style.fg + "0F",
    "--lp-fg-20": style.fg + "33",
    "--lp-fg-40": style.fg + "66",
    "--lp-bg": style.bg,
    "--lp-bg-glass": style.bg + "E6",
    "--lp-card-bg": style.surface,
    "--lp-line": style.surfaceBorder,
    "--lp-gradient": style.cardGradient,
    "--lp-shadow": style.fg + "18",
  };

  if (pendingSignup) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px", background: style.bg, color: style.fg }} data-testid="bank-auth-signup">
        <style>{LANDING_CSS}</style>
        <div style={{ width: "100%", maxWidth: 440 }}>
          <div className="lp-auth-card" style={cssVars}>
            <div className="lp-auth-pending">
              <div className="lp-auth-pending-icon"><CheckCircle2 size={30} color="#10b981" /></div>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: "1.25rem", fontWeight: 700, margin: "0 0 12px", letterSpacing: "-.01em" }}>{t("pending.title")}</h2>
              <p style={{ fontSize: ".9rem", opacity: .7, lineHeight: 1.6, margin: "0 0 24px" }}>{t("pending.body")}</p>
              <Link to={`${basePath}/login`} style={{ textDecoration: "none" }}>
                <button className="lp-auth-submit" style={{ "--lp-accent": style.accent, "--lp-accent-30": style.accent + "4D" }}>{t("pending.back")}</button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-auth-root" style={cssVars} data-testid={`bank-auth-${mode}`}>
      <style>{LANDING_CSS}</style>
      <div className="lp-auth-outer">
        {/* Logo floating above card */}
        <div className="lp-auth-logo-bubble" style={{ background: bank.logo_url ? "transparent" : style.accent }}>
          {bank.logo_url
            ? <img src={bank.logo_url} alt={bank.name} className="lp-auth-logo-img" />
            : <span className="lp-auth-logo-letter">{bank.name[0]}</span>
          }
        </div>
        <div className="lp-auth-card">
          <div className="lp-auth-head">
            <h1>{isDemo ? (t("auth.demo.title") || "Connexion administrateur") : pinStep ? (t("auth.pin.title") || "Code PIN") : `${bank.name} — ${t("auth.card.tagline") || "Votre espace client"}`}</h1>
            <span className="lp-auth-badge">{isDemo ? (t("auth.demo.subtitle") || "Entrez vos identifiants admin") : pinStep ? (t("auth.pin.subtitle") || "Entrez votre Code PIN à 4 chiffres") : t("auth.login.badge")}</span>
          </div>
            {/* ── PIN verification step ── */}
            {pinStep ? (
              <form onSubmit={submitPin}>
                <div className="lp-auth-field">
                  <label className="lp-auth-label">{t("auth.pin.label") || "Code PIN"} *</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    required
                    autoFocus
                    className="lp-auth-input"
                    style={{ fontSize: "2rem", letterSpacing: "0.5em", textAlign: "center" }}
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="••••"
                    data-testid="bauth-pin"
                  />
                </div>
                <button type="submit" disabled={busy || pinValue.length !== 4} className="lp-auth-submit" data-testid="bauth-pin-submit">
                  {busy ? (t("auth.submit.loading") || "Vérification…") : (t("auth.pin.submit") || "Confirmer")}
                </button>
                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <button type="button" onClick={() => { setPinStep(false); setTempToken(""); setPinValue(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: style.accent, fontSize: "0.85rem" }}>
                    ← {t("auth.pin.back") || "Retour à la connexion"}
                  </button>
                </div>
              </form>
            ) : (
            <form onSubmit={submit}>
              {isSignup && (
                <>
                  <div className="lp-auth-field">
                    <label className="lp-auth-label">{t("auth.field.lastName")} *</label>
                    <input required className="lp-auth-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Dupont" data-testid="bauth-lastname" />
                  </div>
                  <div className="lp-auth-field">
                    <label className="lp-auth-label">{t("auth.field.firstName")} *</label>
                    <input required className="lp-auth-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Jean Marie" data-testid="bauth-firstname" />
                  </div>
                  <div className="lp-auth-field">
                    <label className="lp-auth-label">{t("auth.field.country")} *</label>
                    <select required className="lp-auth-input" value={form.country} onChange={(e) => onCountryChange(e.target.value)} data-testid="bauth-country" style={{ appearance: "auto" }}>
                      <option value="">{t("auth.field.country.placeholder")}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.name} value={c.name}>{c.flag} {c.name} ({c.phone})</option>
                      ))}
                    </select>
                  </div>
                  <div className="lp-auth-field">
                    <label className="lp-auth-label">{t("auth.field.phone")} *</label>
                    <div className="lp-phone-row">
                      <div className="lp-phone-prefix">
                        {selectedCountry ? <>{selectedCountry.flag} {selectedCountry.phone}</> : "—"}
                      </div>
                      <input
                        type="tel"
                        required
                        className="lp-auth-input"
                        value={form.phoneLocal}
                        onChange={(e) => setForm({ ...form, phoneLocal: e.target.value })}
                        placeholder="6 12 34 56 78"
                        disabled={!selectedCountry}
                        data-testid="bauth-phone"
                      />
                    </div>
                  </div>
                  <div className="lp-auth-field">
                    <label className="lp-auth-label">{t("auth.field.address")} *</label>
                    <input required className="lp-auth-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("auth.field.address.placeholder")} data-testid="bauth-address" />
                  </div>
                </>
              )}
              <div className="lp-auth-field">
                <label className="lp-auth-label">{t("auth.field.email")} *</label>
                <input type="email" required className="lp-auth-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("auth.field.email.placeholder")} data-testid="bauth-email" />
              </div>
              <div className="lp-auth-field">
                <label className="lp-auth-label">{t("auth.field.password")} *</label>
                <input type="password" required minLength={6} className="lp-auth-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="bauth-password" />
              </div>

              {isSignup && (
                <>
                <div className="lp-auth-field">
                  <label className="lp-auth-label">{t("auth.pin.label") || "Code PIN"} * <span style={{ fontSize: "0.78rem", opacity: 0.6 }}>({t("auth.pin.digit_hint")})</span></label>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    required
                    className="lp-auth-input"
                    style={{ fontSize: "1.8rem", letterSpacing: "0.4em", textAlign: "center" }}
                    value={form.pin}
                    onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    placeholder="••••"
                    data-testid="bauth-pin-signup"
                  />
                </div>
                <div className="lp-auth-field">
                  <label className="lp-auth-label">{t("auth.pin.confirm") || "Confirmer le Code PIN"} *</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    required
                    className="lp-auth-input"
                    style={{ fontSize: "1.8rem", letterSpacing: "0.4em", textAlign: "center" }}
                    value={form.pinConfirm}
                    onChange={(e) => setForm({ ...form, pinConfirm: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    placeholder="••••"
                    data-testid="bauth-pin-confirm"
                  />
                </div>
                </>
              )}

              {isSignup && (
                <div className="lp-kyc-section">
                  <h3 className="lp-kyc-title"><Shield size={15} /> {t("kyc.title")}</h3>
                  <p className="lp-kyc-sub">{t("kyc.sub")}</p>

                  <label className="lp-auth-label">{t("kyc.id_type")}</label>
                  <div className="lp-kyc-radio-row">
                    {[
                      { v: "id_card", k: "kyc.id_card" },
                      { v: "passport", k: "kyc.passport" },
                      { v: "driver_license", k: "kyc.driver_license" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        className={`lp-kyc-radio ${form.kyc_id_type === opt.v ? "checked" : ""}`}
                        onClick={() => setForm({ ...form, kyc_id_type: opt.v, kyc_id_verso: opt.v === "passport" ? null : form.kyc_id_verso })}
                      >
                        {t(opt.k)}
                      </button>
                    ))}
                  </div>

                  <KycFile field="kyc_id_recto" label={form.kyc_id_type === "passport" ? t("kyc.recto_passport") : t("kyc.recto_id")} hint={t("kyc.hint_doc")} />
                  {form.kyc_id_type !== "passport" && (
                    <KycFile field="kyc_id_verso" label={t("kyc.verso")} hint={t("kyc.hint_doc")} />
                  )}
                  <KycFile field="kyc_selfie" label={t("kyc.selfie")} hint={t("kyc.hint_selfie")} />
                  <KycFile field="kyc_address_proof" label={t("kyc.address_proof")} hint={t("kyc.hint_address")} />
                </div>
              )}

              <button type="submit" disabled={busy} className="lp-auth-submit" data-testid="bauth-submit">
                {busy ? t("auth.submit.loading") : isSignup ? t("auth.submit.signup") : t("auth.submit.login")}
              </button>
            </form>
            )} {/* end PIN step else */}
            {!pinStep && isSignup && (
            <div className="lp-auth-switch">
              <>{t("auth.switch.have_account")}{" "}<Link to={`${basePath}/login`} style={{ color: style.accent }} data-testid="bauth-switch-login">{t("auth.switch.login_link")}</Link></>
            </div>
            )}
        </div>{/* .lp-auth-card */}
      </div>{/* .lp-auth-outer */}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* APP — authenticated bank user portal                                       */
/* -------------------------------------------------------------------------- */

const DB_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400..800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
*, *::before, *::after { box-sizing: border-box; }
html, body { overflow-x: hidden; max-width: 100%; }

.db-shell {
  font-family: 'DM Sans', sans-serif;
  display: flex;
  height: 100vh;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  background: var(--bg);
  color: var(--navy);
}
.db-page { overflow-x: hidden; }
.db-content-wrap { max-width: 100%; overflow-x: hidden; }

/* ── Sidebar ── */
.db-sidebar {
  width: 240px;
  height: 100%;
  background: var(--surface);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow-y: auto;
  scrollbar-width: none;
}
.db-sidebar::-webkit-scrollbar { display: none; }

.db-sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
}
.db-sidebar-brand-name {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 1.02rem;
  letter-spacing: -0.015em;
  color: var(--accent);
}

.db-sidebar-nav {
  flex: 1;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.db-nav-label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--muted);
  padding: 10px 10px 4px;
}
.db-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 0.855rem;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
  user-select: none;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  font-family: 'DM Sans', sans-serif;
}
.db-nav-item:hover { background: var(--line); color: var(--accent); }
.db-nav-item.active { background: var(--accent); color: #fff; font-weight: 600; }

.db-sidebar-footer {
  padding: 10px 8px;
  border-top: 1px solid var(--line);
  flex-shrink: 0;
}
.db-sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 8px;
}
.db-sidebar-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-family: 'Syne', sans-serif;
}
.db-sidebar-user-name { font-weight: 600; font-size: 0.82rem; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.db-sidebar-user-role { font-size: 0.68rem; color: var(--muted); }

/* ── Content wrap ── */
.db-content-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Topbar ── */
.db-topbar {
  height: 60px;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 1px 12px var(--shadow-soft, rgba(75,113,250,.07));
}
.db-topbar-mobile-brand {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 0.98rem;
  letter-spacing: -0.015em;
  color: var(--accent);
  display: none;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.db-topbar-mobile-logo {
  height: 30px;
  width: 30px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}
.db-topbar-mobile-logo-fallback {
  height: 30px;
  width: 30px;
  border-radius: 8px;
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 13px;
  flex-shrink: 0;
}
.db-topbar-mobile-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.db-search {
  flex: 1;
  max-width: 300px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 12px;
  height: 36px;
}
.db-search input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  color: var(--navy);
  outline: none;
  font-family: 'DM Sans', sans-serif;
}
.db-search input::placeholder { color: var(--muted); }
.db-topbar-right { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.db-bell {
  position: relative;
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--bg);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--muted); flex-shrink: 0;
}
.db-bell-badge {
  position: absolute; top: -2px; right: -2px;
  width: 16px; height: 16px;
  background: #ef4444; color: #fff;
  border-radius: 50%; font-size: 0.55rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid var(--surface);
}
.db-topbar-avatar {
  width: 34px; height: 34px;
  border-radius: 50%;
  background: var(--accent); color: #fff;
  font-weight: 700; font-size: 0.78rem;
  display: grid; place-items: center;
  cursor: pointer; user-select: none; flex-shrink: 0;
  font-family: 'Syne', sans-serif;
}
.db-topbar-name { font-size: 0.82rem; font-weight: 600; color: var(--navy); }

/* ── Page scroll area ── */
.db-page {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--line) transparent;
}

/* ── Dashboard header ── */
.db-dash-header { padding: 1.5rem 1.5rem 0.75rem; }
.db-dash-title { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 1.05rem; color: var(--navy); margin: 0 0 2px; letter-spacing: -.005em; }
.db-dash-sub { font-size: 0.82rem; color: var(--muted); margin: 0; }

/* ── Dashboard 3-column grid ── */
.db-dash-grid {
  display: grid;
  grid-template-columns: 1.5fr 1.1fr 1fr;
  gap: 1.25rem;
  padding: 1rem 1.5rem 1.5rem;
  align-items: start;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}
/* min-width:0 — grid items default to min-width:auto, letting wide content
   push a column past the viewport (the mobile right-side overflow bug) */
.db-col { display: flex; flex-direction: column; gap: 1.25rem; min-width: 0; }

/* ── Widget ── */
.db-widget {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  box-shadow: 0 2px 14px var(--shadow-soft, rgba(75,113,250,.06));
}
.db-widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
}
.db-widget-title { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 0.82rem; color: var(--navy); }
.db-widget-link {
  font-size: 0.75rem; font-weight: 600; color: var(--accent);
  background: none; border: none; cursor: pointer;
  display: flex; align-items: center; gap: 3px; padding: 0;
  font-family: 'DM Sans', sans-serif;
}
.db-widget-body { padding: 0 1.25rem 1.25rem; }

/* ── Balance card ── */
.db-balance-card {
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  color: #fff;
}
.db-balance-card::before {
  content: '';
  position: absolute;
  top: -50px; right: -50px;
  width: 180px; height: 180px;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
  pointer-events: none;
}
.db-balance-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.12em; opacity: 0.75; margin-bottom: 8px; }
.db-balance-amount { font-family: 'DM Sans', sans-serif; font-weight: 600; font-size: 1.55rem; line-height: 1.15; letter-spacing: -0.02em; font-variant-numeric: tabular-nums lining-nums; word-break: break-word; overflow-wrap: anywhere; }
.db-balance-growth {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 10px;
  background: rgba(255,255,255,0.15);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.72rem; font-weight: 600;
}

/* ── Account rows ── */
.db-account-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 1.25rem;
  border-bottom: 1px solid var(--line);
}
.db-account-row:last-of-type { border-bottom: none; }
.db-account-icon { width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; flex-shrink: 0; }
.db-account-info { flex: 1; min-width: 0; }
.db-account-name { font-weight: 600; font-size: 0.84rem; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.db-account-num { font-size: 0.7rem; color: var(--muted); margin-top: 1px; }
.db-account-bal { font-weight: 700; font-size: 0.88rem; color: var(--navy); flex-shrink: 0; }
.db-account-btns { display: flex; gap: 8px; padding: 0.75rem 1.25rem 1.25rem; }
.db-acct-btn {
  flex: 1; padding: 8px;
  border-radius: 9px;
  border: 1px solid var(--line);
  background: var(--bg); color: var(--navy);
  font-size: 0.78rem; font-weight: 600;
  cursor: pointer; transition: background 0.12s;
  font-family: 'DM Sans', sans-serif;
}
.db-acct-btn:hover { background: var(--line); }
.db-acct-btn.primary { background: var(--accent); color: #fff; border-color: var(--accent); }
.db-acct-btn.primary:hover { opacity: 0.88; }

/* ── Quick actions ── */
.db-qa-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; padding: 0 1.25rem 1.25rem; }
.db-qa-btn {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 12px 4px;
  border-radius: 12px;
  background: var(--bg); border: 1px solid var(--line);
  cursor: pointer; transition: transform 0.12s;
  font-family: 'DM Sans', sans-serif;
}
.db-qa-btn:hover { transform: translateY(-2px); }
.db-qa-icon { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; }
.db-qa-label { font-size: 0.65rem; font-weight: 500; color: var(--navy); text-align: center; }

/* ── Budget ── */
.db-budget { display: flex; flex-direction: column; gap: 12px; padding: 0 1.25rem 1.25rem; }
.db-budget-item { display: flex; flex-direction: column; gap: 5px; }
.db-budget-row-top { display: flex; justify-content: space-between; font-size: 0.75rem; }
.db-budget-lbl { color: var(--muted); }
.db-budget-val { font-weight: 600; }
.db-bar { height: 7px; border-radius: 999px; background: var(--line); overflow: hidden; }
.db-bar-fill { height: 100%; border-radius: 999px; transition: width 0.5s ease; }

/* ── Donut ── */
.db-donut-wrap { display: flex; flex-direction: column; align-items: center; padding: 0 1.25rem 1.25rem; }
.db-donut-legend { width: 100%; display: flex; flex-direction: column; gap: 9px; margin-top: 1rem; }
.db-legend-row { display: flex; align-items: center; gap: 8px; font-size: 0.78rem; }
.db-legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.db-legend-name { flex: 1; color: var(--muted); }
.db-legend-val { font-weight: 600; color: var(--navy); }
.db-legend-pct { color: var(--muted); font-size: 0.7rem; min-width: 30px; text-align: right; }

/* ── Transaction rows ── */
.db-tx-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 1.25rem;
  border-bottom: 1px solid var(--line);
  transition: background 0.1s;
}
.db-tx-row:last-child { border-bottom: none; }
.db-tx-row:hover { background: var(--bg); }
.db-tx-icon { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; flex-shrink: 0; }
.db-tx-info { flex: 1; min-width: 0; }
.db-tx-lbl { font-weight: 600; font-size: 0.82rem; color: var(--navy); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.db-tx-date { font-size: 0.68rem; color: var(--muted); margin-top: 1px; }
.db-tx-amount { font-weight: 700; font-size: 0.85rem; flex-shrink: 0; }

/* ── VISA card ── */
.db-visa {
  aspect-ratio: 1.6/1;
  border-radius: 14px;
  padding: 1.1rem 1.25rem;
  display: flex; flex-direction: column; justify-content: space-between;
  position: relative; overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  margin: 0 1.25rem;
}
.db-visa::before {
  content: '';
  position: absolute; top: -40%; right: -20%;
  width: 70%; height: 170%;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
  pointer-events: none;
}
.db-card-actions { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; padding: 0.75rem 1.25rem 1.25rem; }
.db-card-action {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  padding: 10px 4px; border-radius: 10px;
  background: var(--bg); border: 1px solid var(--line);
  cursor: pointer; font-size: 0.62rem; font-weight: 500; color: var(--navy);
  transition: background 0.12s;
  font-family: 'DM Sans', sans-serif;
}
.db-card-action:hover { background: var(--line); }

/* ── Page inner (non-dashboard pages) ── */
.db-page-inner { padding: 1.5rem; max-width: 860px; margin: 0 auto; width: 100%; }
.db-page-h { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 1rem; color: var(--navy); margin: 0 0 1.25rem; display: flex; align-items: center; gap: 8px; letter-spacing: -.005em; }

/* ── Mobile bottom nav ── */
.db-bottom-nav {
  position: fixed; bottom: 0; left: 0; right: 0;
  background: var(--surface);
  border-top: 1px solid var(--line);
  display: none;
  justify-content: space-around; align-items: center;
  padding: 8px 0 calc(14px + env(safe-area-inset-bottom)); z-index: 50;
  box-shadow: 0 -3px 18px var(--shadow-soft, rgba(75,113,250,.09));
}
.db-bnav-item {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 4px 10px;
  font-size: 0.6rem; font-weight: 500; color: var(--muted);
  cursor: pointer; user-select: none;
  transition: color 0.12s;
  background: none; border: none;
  font-family: 'DM Sans', sans-serif;
}
.db-bnav-item.active { color: var(--accent); font-weight: 700; }
.db-bnav-plus { background: var(--accent); color: #fff !important; border-radius: 50%; width: 28px; height: 28px; display: grid; place-items: center; font-size: 1.25rem; font-weight: 700; line-height: 1; margin-bottom: 0; }

/* ── More drawer overlay ── */
.db-more-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  z-index: 200; display: flex; align-items: flex-end;
  animation: db-fade-in 0.15s ease;
}
.db-more-sheet {
  width: 100%; background: var(--surface);
  border-radius: 20px 20px 0 0;
  padding: 12px 16px 32px;
  animation: db-slide-up 0.2s cubic-bezier(0.32,0.72,0,1);
}
.db-more-handle {
  width: 36px; height: 4px; background: var(--line);
  border-radius: 2px; margin: 0 auto 16px;
}
.db-more-title {
  font-family: 'Syne', sans-serif; font-weight: 700; font-size: 0.85rem;
  color: var(--muted); text-transform: uppercase; letter-spacing: 0.06em;
  margin: 0 0 12px 4px;
}
.db-more-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
}
.db-more-item {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 12px 6px; border-radius: 14px;
  background: var(--bg); border: 1px solid var(--line);
  font-size: 0.68rem; font-weight: 600; color: var(--fg);
  font-family: 'DM Sans', sans-serif;
  cursor: pointer; user-select: none; text-align: center;
  transition: background 0.12s, border-color 0.12s;
}
.db-more-item:active { background: var(--accent); color: #fff; border-color: var(--accent); }
.db-more-item.active { background: var(--accent)18; border-color: var(--accent)60; color: var(--accent); }
.db-more-sep { grid-column: 1/-1; height: 1px; background: var(--line); margin: 2px 0; }
.db-more-logout { color: #ef4444 !important; border-color: #ef444430 !important; }
@keyframes db-fade-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes db-slide-up { from { transform: translateY(100%) } to { transform: translateY(0) } }

/* ── Responsive ── */
@media (max-width: 1100px) {
  .db-dash-grid { grid-template-columns: 1fr 1fr; }
  .db-col-right { display: none; }
}
@media (max-width: 767px) {
  .db-sidebar { display: none !important; }
  .db-topbar { padding: 0 1rem; height: 56px; }
  .db-topbar-mobile-brand { display: flex; flex: 1; min-width: 0; }
  .db-topbar-mobile-name { display: block; font-size: 0.95rem; }
  .db-search { display: none; }
  .db-topbar-name { display: none; }
  .db-dash-grid { grid-template-columns: 1fr; padding: 0.75rem 1rem 1rem; gap: 0.9rem; }
  .db-col-right { display: flex !important; }
  .db-page { padding-bottom: 76px; }
  .db-bottom-nav { display: flex !important; }
  .db-dash-header { padding: 1rem 1rem 0.5rem; }
  .db-dash-title { font-size: 1.1rem; }
  .db-dash-sub { font-size: 0.78rem; }
  .db-balance-card { padding: 1.2rem; }
  .db-balance-amount { font-size: 1.6rem; }
  .db-balance-label { font-size: 0.66rem; }
  .db-widget-header { padding: 0.85rem 1rem 0.6rem; }
  .db-widget-body { padding: 0 1rem 1rem; }
  .db-widget-title { font-size: 0.86rem; }
  .db-more-grid { grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .db-more-item { font-size: 0.62rem; padding: 10px 4px; }
}
@keyframes db-spin { to { transform: rotate(360deg); } }

/* Bell ring/shake animation triggered when a new transfer code arrives */
@keyframes db-bell-ring {
  0% { transform: rotate(0); }
  15% { transform: rotate(18deg); }
  30% { transform: rotate(-15deg); }
  45% { transform: rotate(12deg); }
  60% { transform: rotate(-9deg); }
  75% { transform: rotate(5deg); }
  100% { transform: rotate(0); }
}
.db-bell.is-ringing svg { animation: db-bell-ring 0.9s ease-in-out 2; transform-origin: 50% 4px; }
.db-bell.is-ringing { box-shadow: 0 0 0 0 #ef444466; animation: db-bell-pulse 1.2s ease-out 2; }
@keyframes db-bell-pulse {
  0%   { box-shadow: 0 0 0 0   #ef444499; }
  70%  { box-shadow: 0 0 0 12px #ef444400; }
  100% { box-shadow: 0 0 0 0   #ef444400; }
}
.db-bell-badge.is-pulsing { animation: db-bell-pulse 1.2s ease-out 2; }

/* Transfer code banner — same shape as admin notif, just a subtle finance-green
   tint (no neon, no game vibes). Color follows accent on the timer bar so it
   still feels native to the bank's palette. */
.db-code-banner {
  position: relative;
  border-radius: 10px;
  padding: 12px 14px;
  background: rgba(16,185,129,0.06);
  border: 1px solid rgba(16,185,129,0.22);
  display: flex; flex-direction: column; gap: 8px;
  animation: db-fade-in .25s ease;
}
.db-code-countdown {
  height: 2px; background: rgba(16,185,129,0.12); border-radius: 2px; overflow: hidden;
}
.db-code-countdown > span {
  display: block; height: 100%; background: rgba(16,185,129,0.7);
  transition: width 1s linear;
}

/* ── Template-specific: use --radius for card shapes ─── */
.db-widget { border-radius: var(--radius, 16px); }
.db-balance-card { border-radius: var(--radius, 16px); }
.db-visa { border-radius: var(--radius, 14px); }
.db-qa-btn { border-radius: calc(var(--radius, 12px) * 0.85); }
.db-acct-btn { border-radius: calc(var(--radius, 9px) * 0.65); }
.db-nav-item { border-radius: calc(var(--radius, 10px) * 0.75); }
.db-more-item { border-radius: calc(var(--radius, 14px) * 0.85); }

/* Minimalist Mono — razor-sharp, high-contrast borders */
[data-tpl="tpl-minimalist-mono"] .db-widget { border-radius:0; border-width:2px; }
[data-tpl="tpl-minimalist-mono"] .db-balance-card { border-radius:0; }
[data-tpl="tpl-minimalist-mono"] .db-visa { border-radius:0; }
[data-tpl="tpl-minimalist-mono"] .db-nav-item.active { border-radius:0; }
[data-tpl="tpl-minimalist-mono"] .db-sidebar { border-right:2px solid var(--line); }
[data-tpl="tpl-minimalist-mono"] .db-topbar { border-bottom:2px solid var(--line); }

/* Banker Grey — professional, subdued */
[data-tpl="tpl-banker-grey"] .db-nav-item.active { background:var(--navy); color:#fff; }

/* Carbon & Ember — dark sharp industrial */
[data-tpl="tpl-carbon-ember"] .db-widget { border-radius:4px; }
[data-tpl="tpl-carbon-ember"] .db-balance-card { border-radius:4px; box-shadow:0 0 0 1px var(--accent)44; }
[data-tpl="tpl-carbon-ember"] .db-sidebar { border-right:1px solid var(--accent)30; }

/* Tech Neon — glowing borders */
[data-tpl="tpl-tech-neon"] .db-widget { box-shadow:0 0 0 1px var(--accent)1A,0 4px 24px var(--accent)12; }
[data-tpl="tpl-tech-neon"] .db-balance-card { box-shadow:0 8px 40px var(--accent)35; }
[data-tpl="tpl-tech-neon"] .db-nav-item.active { box-shadow:0 0 12px var(--accent)40; }

/* Obsidian Purple — violet glow */
[data-tpl="tpl-obsidian-purple"] .db-widget { box-shadow:0 0 0 1px var(--accent)22; }
[data-tpl="tpl-obsidian-purple"] .db-balance-card { box-shadow:0 8px 48px var(--accent)30; }

/* Midnight Gold — gold accents */
[data-tpl="tpl-midnight-gold"] .db-widget-title { color:var(--accent); }
[data-tpl="tpl-midnight-gold"] .db-balance-card { box-shadow:0 8px 40px rgba(212,175,55,.25); }

/* Forest Depths — green glow */
[data-tpl="tpl-forest-depths"] .db-widget { box-shadow:0 0 0 1px var(--accent)1A; }
[data-tpl="tpl-forest-depths"] .db-balance-card { box-shadow:0 8px 40px var(--accent)28; }

/* Aurora / Ocean / Sunset — vivid gradient glow */
[data-tpl="tpl-aurora-borealis"] .db-balance-card,
[data-tpl="tpl-ocean-deep"] .db-balance-card,
[data-tpl="tpl-sunset-gradient"] .db-balance-card { box-shadow:0 12px 48px var(--accent)35; }

/* Electric Indigo / Royal Amethyst — pill nav items */
[data-tpl="tpl-electric-indigo"] .db-nav-item,
[data-tpl="tpl-royal-amethyst"] .db-nav-item,
[data-tpl="tpl-pearl-elegant"] .db-nav-item,
[data-tpl="tpl-aurora-borealis"] .db-nav-item { border-radius:100px; }

/* Pearl Elegant / Emerald / Arctic — soft rounded */
[data-tpl="tpl-pearl-elegant"] .db-widget,
[data-tpl="tpl-arctic-clean"] .db-widget { border-radius:20px; }
`;

const DEMO_ME = { id: "demo", name: "Jean Dupont", email: "demo@neobk.pro", balance: 5280.50, currency: "EUR", status: "active", iban: "FR76 3000 6000 0112 3456 7890 189", bic: "BNPAFRPPXXX", locale: "fr", avatar_url: null };
const DEMO_TXNS = [
  { id: "dt1", type: "credit", op: "transfer_in", amount: 3200, label: "Salaire octobre", balance_after: 5280.50, created_at: new Date(Date.now() - 864e5).toISOString(), status: "completed" },
  { id: "dt2", type: "debit", op: "transfer_out", amount: 850, label: "Loyer novembre", balance_after: 2080.50, created_at: new Date(Date.now() - 2 * 864e5).toISOString(), status: "completed" },
  { id: "dt3", type: "credit", op: "recharge", amount: 500, label: "Virement ami", balance_after: 2930.50, created_at: new Date(Date.now() - 4 * 864e5).toISOString(), status: "completed" },
  { id: "dt4", type: "debit", op: "withdraw", amount: 200, label: "Retrait DAB", balance_after: 2430.50, created_at: new Date(Date.now() - 5 * 864e5).toISOString(), status: "completed" },
  { id: "dt5", type: "debit", op: "transfer_out", amount: 125, label: "Abonnement streaming", balance_after: 2230.50, created_at: new Date(Date.now() - 7 * 864e5).toISOString(), status: "completed" },
];
const DEMO_CARDS = [
  { id: "dc1", holder_name: "JEAN DUPONT", last_four: "4242", card_type: "visa", status: "active", expires: "12/28", bank_user_id: "demo", color: "#1D4ED8" },
];
const DEMO_NOTIFS = [];

function BankApp() {
  const { bank, slug, langOverride, isDemo, basePath } = useContext(BankCtx);
  const style = useStyle(bank);
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cards, setCards] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [activeSection, setActiveSection] = useState("home");
  const [moreOpen, setMoreOpen] = useState(false);
  // Transfer code banner machinery: dismissed ids + ticker (1 Hz for countdown)
  const [dismissedCodeIds, setDismissedCodeIds] = useState(() => new Set());
  const [bellShake, setBellShake] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState(null);
  const [, forceTick] = useState(0);
  const seenCodeIdsRef = useRef(new Set());
  useEffect(() => {
    const id = setInterval(() => forceTick((x) => (x + 1) % 1_000_000), 1000);
    return () => clearInterval(id);
  }, []);

  const headers = useMemo(() => {
    const t = localStorage.getItem(TOKEN_KEY(slug));
    return t ? { Authorization: `Bearer ${t}` } : null;
  }, [slug]);

  const reload = async () => {
    if (isDemo) {
      setMe(DEMO_ME); setTxns(DEMO_TXNS); setCards(DEMO_CARDS); setNotifs(DEMO_NOTIFS);
      return;
    }
    if (!headers) return;
    try {
      const m = await api.get("/bank-user/me", { headers });
      setMe(m.data);
      setLoadError(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) { logout(); return; }
      const msg = e?.response?.data?.detail || e?.message || "Erreur inconnue";
      setLoadError(`Erreur (${status || "réseau"}): ${msg}`);
      return;
    }
    try {
      const [tx, c, n] = await Promise.all([
        api.get("/bank-user/transactions", { headers }),
        api.get("/bank-user/cards", { headers }),
        api.get("/bank-user/notifications", { headers }),
      ]);
      setTxns(tx.data); setCards(c.data); setNotifs(n.data);
    } catch (_) {}
  };

  const pollNotifs = async () => {
    if (!headers) return;
    try {
      const { data } = await api.get("/bank-user/notifications", { headers });
      setNotifs(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) logout();
    }
  };

  const pollUnreadMsgs = async () => {
    if (!headers) return;
    try {
      const { data } = await api.get("/bank-user/messages/unread-count", { headers });
      setUnreadMsgs(data.count || 0);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) logout();
    }
  };

  useEffect(() => {
    if (!headers && !isDemo) { nav(`${basePath}/login`); return; }
    reload();
    if (!isDemo) pollUnreadMsgs();
    const intvl = setInterval(() => { if (!isDemo) { pollNotifs(); pollUnreadMsgs(); } }, 5000);

    // Listen for cross-component reload triggers (e.g. after an investment withdrawal)
    const onReload = () => { if (!isDemo) reload(); };
    window.addEventListener("bank-user-reload", onReload);

    return () => {
      clearInterval(intvl);
      window.removeEventListener("bank-user-reload", onReload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear unread badge when the messages page is opened (server marks them read on fetch)
  useEffect(() => {
    if (activeSection === "messages") setUnreadMsgs(0);
  }, [activeSection]);

  // Detect newly-arrived transfer_code notifications → ring the bell + vibrate.
  // Uses a ref to remember already-seen codes across re-renders so we don't ring twice.
  useEffect(() => {
    const fresh = notifs.filter(
      (n) =>
        n.notif_type === "transfer_code" &&
        !seenCodeIdsRef.current.has(n.id) &&
        Date.now() - new Date(n.created_at).getTime() < 120_000,
    );
    if (fresh.length === 0) return;
    fresh.forEach((n) => seenCodeIdsRef.current.add(n.id));
    setBellShake(true);
    try { navigator.vibrate?.([220, 100, 220]); } catch (_) {}
    const t = setTimeout(() => setBellShake(false), 2000);
    return () => clearTimeout(t);
  }, [notifs]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY(slug)); nav(basePath || "/"); };

  const locale = me?.locale || langOverride || bank?.default_locale || "fr";
  const { t } = useTranslation(locale);

  // User may dismiss automatic/system notifications. Admin (manual) notifications
  // cannot be dismissed by the user — only the admin can delete them.
  const dismissNotif = async (nid) => {
    if (!headers) return;
    setNotifs((prev) => prev.filter((n) => n.id !== nid)); // optimistic
    try { await api.delete(`/bank-user/notifications/${nid}`, { headers }); }
    catch (_) { pollNotifs(); }
  };
  const isAdminNotif = (n) => n.kind === "admin";
  // Live transfer-code notifications stay visible until the user explicitly
  // dismisses them (X). No auto-disappearance, no countdown — bank-pro feel.
  const isLiveCode = (n) =>
    n.notif_type === "transfer_code" &&
    !!n.code &&
    !dismissedCodeIds.has(n.id);

  // Banners shown at the top of every dashboard page:
  //   • admin notifications (user cannot dismiss)
  //   • live transfer_code notifs (user dismisses with X)
  const activeBanners = notifs.filter((n) => isAdminNotif(n) || isLiveCode(n));

  const cssVars = {
    "--accent": style.accent,
    "--navy": style.isDark ? style.fg : "#0E2434",
    "--fg": style.fg,
    "--bg": style.bg,
    "--surface": style.surface,
    "--muted": style.isDark ? "rgba(250,250,250,0.5)" : "#6b82a8",
    "--line": style.isDark ? `${style.accent}22` : "rgba(75,113,250,.13)",
    "--green": "#16a34a",
    "--radius": style.cardShape || "14px",
    "--gradient": style.cardGradient,
    "--shadow-soft": style.accent + "14",
  };

  const { t: tLoad } = useTranslation(bank?.default_locale || "fr");

  if (!me) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: style.bg, color: style.fg }}>
      <style>{DB_CSS}</style>
      <div style={{ textAlign: "center", maxWidth: 400, padding: "0 24px" }}>
        {loadError ? (
          <>
            <p style={{ color: "#f87171", fontSize: "0.9rem", marginBottom: 12, wordBreak: "break-all" }}>{loadError}</p>
            <button onClick={() => { localStorage.removeItem(TOKEN_KEY(slug)); nav(`${basePath}/login`); }} style={{ padding: "8px 20px", background: style.accent, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: "0.85rem" }}>
              {tLoad("app.error_back")}
            </button>
          </>
        ) : (
          <>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${style.accent}`, borderTopColor: "transparent", animation: "db-spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ opacity: 0.5, fontSize: "0.9rem" }}>{tLoad("app.loading")}</p>
          </>
        )}
      </div>
    </div>
  );

  const initials = (me.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const firstName = (me.name || "").split(" ")[0];

  const NAV = [
    { id: "home", label: t("app.nav.dashboard"), Icon: Home },
    { id: "deposit", label: t("dash.quick.deposit"), Icon: ArrowDownToLine },
    { id: "accounts", label: t("app.nav.accounts"), Icon: Banknote },
    { id: "transactions", label: t("app.nav.transactions"), Icon: TrendingUp },
    { id: "transfers", label: t("app.nav.transfers"), Icon: Send },
    { id: "cards", label: t("app.nav.cards"), Icon: CardIcon },
    { id: "card-tracking", label: t("app.nav.card_tracking"), Icon: Package },
    { id: "savings", label: t("app.nav.savings"), Icon: PiggyBank },
    { id: "investments", label: t("app.nav.investments"), Icon: Briefcase },
    { id: "credits", label: t("app.nav.credit"), Icon: FileText },
    { id: "financings", label: t("app.nav.financing"), Icon: HandCoins },
    { id: "messages", label: t("app.nav.messages"), Icon: MessageCircle },
    { id: "notifications", label: t("app.nav.notifications"), Icon: Bell },
    { id: "settings", label: t("app.nav.settings"), Icon: SettingsIcon },
    { id: "profile", label: t("app.nav.profile"), Icon: User },
  ];

  // Bottom mobile nav — primary 4 + "Plus" button
  const MOBILE_NAV = [
    { id: "home", label: t("app.nav.mobile.home"), Icon: Home },
    { id: "transfers", label: t("app.nav.mobile.transfers"), Icon: ArrowLeftRight },
    { id: "cards", label: t("app.nav.mobile.cards"), Icon: CardIcon },
    { id: "transactions", label: t("app.nav.mobile.transactions"), Icon: TrendingUp },
  ];

  // Items hidden behind the "Plus" drawer
  const MORE_NAV = [
    { id: "accounts", label: t("app.nav.accounts"), Icon: Banknote },
    { id: "deposit", label: t("dash.quick.deposit"), Icon: ArrowDownToLine },
    { id: "credits", label: t("app.nav.credit"), Icon: FileText },
    { id: "savings", label: t("app.nav.savings"), Icon: PiggyBank },
    { id: "financings", label: t("app.nav.financing"), Icon: HandCoins },
    { id: "investments", label: t("app.nav.more.invest"), Icon: Briefcase },
    { id: "card-tracking", label: t("app.nav.card_tracking"), Icon: Package },
    { id: "messages", label: t("app.nav.messages"), Icon: MessageCircle },
    { id: "notifications", label: t("app.nav.notifications"), Icon: Bell },
    { id: "profile", label: t("app.nav.profile"), Icon: User },
    { id: "settings", label: t("app.nav.settings"), Icon: SettingsIcon },
  ];

  return (
    <BankLangCtx.Provider value={{ t, isDemo }}>
    <div className="db-shell" style={cssVars} data-tpl={bank?.template_id || "tpl-classic-financial"} data-testid="bank-app-root">
      <style>{DB_CSS}</style>
      {isDemo && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, background: "#1e293b", color: "#fff", textAlign: "center", padding: "8px 16px", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.04em" }} data-testid="demo-banner-app">
          {t("demo.banner")}
        </div>
      )}

      {/* ── Sidebar (desktop) ── */}
      <aside className="db-sidebar">
        <div className="db-sidebar-brand">
          {bank.logo_url
            ? <img src={bank.logo_url} alt={bank.name} style={{ height: 30, width: 30, objectFit: "contain", borderRadius: 8 }} />
            : <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--accent)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{bank.name[0]}</div>
          }
          <span className="db-sidebar-brand-name">{bank.name}</span>
        </div>

        <nav className="db-sidebar-nav">
          <div className="db-nav-label">{t("app.menu_label")}</div>
          {NAV.map(({ id, label, Icon }) => (
            <button key={id} className={`db-nav-item${activeSection === id ? " active" : ""}`} onClick={() => setActiveSection(id)} data-testid={`sidebar-nav-${id}`}>
              <Icon size={15} />{label}
            </button>
          ))}
        </nav>

        <div className="db-sidebar-footer">
          <div className="db-sidebar-user">
            <div className="db-sidebar-avatar">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="db-sidebar-user-name">{me.name}</div>
              <div className="db-sidebar-user-role">{t("app.user_role")}</div>
            </div>
          </div>
          <button className="db-nav-item" onClick={logout} style={{ color: "#ef4444" }} data-testid="bank-app-logout">
            <LogOut size={15} />{t("app.logout")}
          </button>
        </div>
      </aside>

      {/* ── Content area ── */}
      <div className="db-content-wrap">

        {/* Topbar */}
        <header className="db-topbar">
          <div className="db-topbar-mobile-brand">
            {bank.logo_url
              ? <img src={bank.logo_url} alt={bank.name} className="db-topbar-mobile-logo" />
              : <div className="db-topbar-mobile-logo-fallback" style={{ background: "var(--accent)" }}>{bank.name[0]}</div>
            }
            <span className="db-topbar-mobile-name">{bank.name}</span>
          </div>
          <div className="db-topbar-right">
            <button className="db-bell" onClick={() => setActiveSection("messages")} data-testid="topbar-messages-btn" aria-label="Messages">
              <MessageCircle size={16} />
              {unreadMsgs > 0 && <span className="db-bell-badge">{unreadMsgs > 9 ? "9+" : unreadMsgs}</span>}
            </button>
            <button className={`db-bell${bellShake ? " is-ringing" : ""}`} onClick={() => setActiveSection("notifications")} data-testid="topbar-notif-btn" aria-label="Notifications">
              <Bell size={16} />
              {notifs.length > 0 && <span className={`db-bell-badge${bellShake ? " is-pulsing" : ""}`}>{notifs.length > 9 ? "9+" : notifs.length}</span>}
            </button>
            <div className="db-topbar-avatar" onClick={() => setActiveSection("profile")} title={me.name}>{initials}</div>
            <span className="db-topbar-name">{firstName}</span>
          </div>
        </header>

        {/* Notification banners — admin-pushed + live transfer codes.
            Admin: user cannot dismiss. Transfer code: copy + dismiss + countdown.
            Hidden on messages page so the chat stays in front. */}
        {activeBanners.length > 0 && activeSection !== "messages" && (
          <div style={{ padding: "8px 1.5rem 0", display: "flex", flexDirection: "column", gap: 6 }} data-testid="notif-banners">
            {activeBanners.slice(0, 2).map((n) => {
              const isCode = isLiveCode(n);
              if (isCode) {
                const titleText = t("notif.transfer_code.title");
                const bodyText = t("notif.transfer_code.body");
                // Render exactly like an admin banner: single block, subtle tint,
                // title + body inline, code shown as an inline pill with a Copy
                // button at the right. Subtle green so it stays readable and "pro"
                // rather than neon/game-like. NO countdown, NO auto-dismiss.
                const GREEN = "16,185,129";
                return (
                  <div
                    key={n.id}
                    data-testid={`notif-banner-${n.id}`}
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      padding: "10px 14px",
                      background: `rgba(${GREEN}, 0.08)`,
                      border: `1px solid rgba(${GREEN}, 0.22)`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Bell size={14} color={`rgb(${GREEN})`} style={{ flexShrink: 0, opacity: 0.85 }} />
                      <div style={{ flex: 1, minWidth: 0, fontSize: "0.8rem", lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 700, color: "var(--navy)" }}>{titleText}</span>
                        {" "}
                        <span style={{ color: "var(--muted)" }}>{bodyText}</span>
                      </div>
                      <button
                        onClick={() => setDismissedCodeIds((prev) => { const s = new Set(prev); s.add(n.id); return s; })}
                        data-testid={`notif-banner-dismiss-${n.id}`}
                        aria-label={t("notif.dismiss")}
                        title={t("notif.dismiss")}
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 5, border: "none", background: "transparent", color: "var(--muted)", cursor: "pointer", flexShrink: 0 }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          letterSpacing: "0.18em",
                          color: "var(--navy)",
                          background: "#fff",
                          border: `1px solid rgba(${GREEN}, 0.2)`,
                          borderRadius: 6,
                          padding: "4px 12px",
                        }}
                        data-testid={`notif-banner-code-${n.id}`}
                      >
                        {n.code}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          try { await navigator.clipboard?.writeText(n.code); } catch (_) {}
                          setCopiedCodeId(n.id);
                          setTimeout(() => setCopiedCodeId((c) => (c === n.id ? null : c)), 2000);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: `rgb(${GREEN})`,
                          background: "transparent",
                          border: `1px solid rgba(${GREEN}, 0.3)`,
                          borderRadius: 6,
                          padding: "4px 10px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        data-testid={`notif-banner-copy-${n.id}`}
                      >
                        {copiedCodeId === n.id ? <Check size={12} /> : <Copy size={12} />}
                        {copiedCodeId === n.id ? t("transfer.code.copied") : t("transfer.code.copy")}
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <div key={n.id} style={{ position: "relative", borderRadius: 10, padding: "10px 14px", background: style.accent + "12", border: `1px solid ${style.accent}30`, display: "flex", flexDirection: "column", gap: 6 }} data-testid={`notif-banner-${n.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Bell size={14} color={style.accent} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, fontSize: "0.8rem" }}>
                      <span style={{ fontWeight: 700, color: "var(--navy)" }}>{n.title}</span>
                      {" "}<span style={{ color: "var(--muted)" }}>{n.body}</span>
                    </div>
                    <Lock size={11} color={style.accent} style={{ opacity: 0.5, flexShrink: 0 }} />
                  </div>
                  <button
                    onClick={() => setActiveSection("messages")}
                    style={{ alignSelf: "flex-end", fontSize: "0.72rem", color: style.accent, background: style.accent + "18", border: `1px solid ${style.accent}40`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}
                    data-testid={`notif-banner-support-${n.id}`}
                  >
                    <MessageCircle size={11} />{t("notif.contact_support")}
                  </button>
                </div>
              );
            })}
            {activeBanners.length > 2 && (
              <button onClick={() => setActiveSection("notifications")} style={{ alignSelf: "flex-start", marginTop: 2, fontSize: "0.72rem", color: style.accent, background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }} data-testid="see-all-notifs">
                {t("app.see_more_notifs", { count: activeBanners.length - 2 })}
              </button>
            )}
          </div>
        )}

        {/* Pages */}
        <div className="db-page">
          {activeSection === "home" && (
            <DashboardHome bank={bank} style={style} me={me} txns={txns} cards={cards} headers={headers} setActiveSection={setActiveSection} reload={reload} />
          )}
          {activeSection === "accounts" && (
            <div className="db-page-inner">
              <div className="db-page-h"><Banknote size={18} color={style.accent} />{t("page.accounts")}</div>
              <AccountsPage bank={bank} style={style} headers={headers} me={me} />
            </div>
          )}
          {activeSection === "transactions" && (
            <div className="db-page-inner">
              <div className="db-page-h"><TrendingUp size={18} color={style.accent} />{t("page.transactions")}</div>
              <TransactionsSectionBody bank={bank} style={style} txns={txns} me={me} />
            </div>
          )}
          {activeSection === "deposit" && (
            <div className="db-page-inner">
              <div className="db-page-h"><ArrowDownToLine size={18} color={style.accent} />{t("page.deposit")}</div>
              <DepositSection bank={bank} style={style} t={t} setActiveSection={setActiveSection} />
            </div>
          )}
          {activeSection === "card-tracking" && (
            <div className="db-page-inner">
              <div className="db-page-h"><Package size={18} color={style.accent} />{t("app.nav.card_tracking")}</div>
              <CardTrackingSection style={style} headers={headers} t={t} />
            </div>
          )}
          {activeSection === "transfers" && (
            <div className="db-page-inner">
              <div className="db-page-h"><Send size={18} color={style.accent} />{t("page.transfers")}</div>
              <TransfersSectionBody style={style} bank={bank} headers={headers} reload={reload} me={me} setActiveSection={setActiveSection} notifs={notifs} />
            </div>
          )}
          {activeSection === "cards" && (
            <div className="db-page-inner">
              <CardsSectionBody style={style} cards={cards} me={me} bank={bank} headers={headers} setCards={setCards} reloadUser={reload} />
            </div>
          )}
          {activeSection === "savings" && (
            <div className="db-page-inner">
              <div className="db-page-h"><PiggyBank size={18} color={style.accent} />{t("page.savings")}</div>
              <SavingsPage bank={bank} style={style} headers={headers} me={me} reload={reload} />
            </div>
          )}
          {activeSection === "investments" && (
            <div className="db-page-inner">
              <div className="db-page-h"><Briefcase size={18} color={style.accent} />{t("page.investments")}</div>
              <InvestmentPage bank={bank} style={style} headers={headers} me={me} setActiveSection={setActiveSection} />
            </div>
          )}
          {activeSection === "credits" && (
            <div className="db-page-inner">
              <div className="db-page-h"><FileText size={18} color={style.accent} />{t("page.credits")}</div>
              <CreditsPage bank={bank} style={style} headers={headers} me={me} setActiveSection={setActiveSection} />
            </div>
          )}
          {activeSection === "financings" && (
            <div className="db-page-inner">
              <div className="db-page-h"><HandCoins size={18} color={style.accent} />{t("page.financing")}</div>
              <FinancingPage bank={bank} style={style} headers={headers} setActiveSection={setActiveSection} />
            </div>
          )}
          {activeSection === "messages" && (
            <div className="db-page-inner has-chat">
              <div className="db-page-h"><MessageCircle size={18} color={style.accent} />{t("page.messages")}</div>
              <MessagesPage bank={bank} style={style} headers={headers} me={me} />
            </div>
          )}
          {activeSection === "settings" && (
            <div className="db-page-inner">
              <div className="db-page-h"><SettingsIcon size={18} color={style.accent} />{t("page.settings")}</div>
              <SettingsPage bank={bank} style={style} headers={headers} me={me} reload={reload} />
            </div>
          )}
          {activeSection === "notifications" && (
            <div className="db-page-inner">
              <div className="db-page-h"><Bell size={18} color={style.accent} />{t("page.notifications")}</div>
              <NotificationsSection style={style} notifs={notifs} setActiveSection={setActiveSection} dismissNotif={dismissNotif} />
            </div>
          )}
          {activeSection === "profile" && (
            <div className="db-page-inner">
              <div className="db-page-h"><User size={18} color={style.accent} />{t("page.profile")}</div>
              <ProfileSectionBody style={style} me={me} notifs={notifs} logout={logout} initials={initials} headers={headers} reload={reload} />
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="db-bottom-nav">
        {MOBILE_NAV.map(({ id, label, Icon }) => (
          <button key={id} className={`db-bnav-item${activeSection === id ? " active" : ""}`} onClick={() => { setActiveSection(id); setMoreOpen(false); }} data-testid={`bottom-nav-${id}`}>
            <Icon size={20} />{label}
          </button>
        ))}
        <button className={`db-bnav-item${moreOpen ? " active" : ""}`} onClick={() => setMoreOpen((v) => !v)} data-testid="bottom-nav-more">
          <span className="db-bnav-plus">{moreOpen ? "×" : "+"}</span>
          {t("app.more_btn")}
        </button>
      </nav>

      {/* More drawer */}
      {moreOpen && (
        <div className="db-more-overlay" onClick={() => setMoreOpen(false)} data-testid="more-overlay">
          <div className="db-more-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="db-more-handle" />
            <div className="db-more-title">{t("app.more_title")}</div>
            <div className="db-more-grid">
              {MORE_NAV.map(({ id, label, Icon }) => (
                <button key={id} className={`db-more-item${activeSection === id ? " active" : ""}`} onClick={() => { setActiveSection(id); setMoreOpen(false); }} data-testid={`more-nav-${id}`}>
                  <Icon size={22} color={activeSection === id ? "var(--accent)" : "var(--muted)"} />
                  {label}
                </button>
              ))}
              <div className="db-more-sep" />
              <button className="db-more-item db-more-logout" onClick={() => { logout(); setMoreOpen(false); }} data-testid="more-nav-logout" style={{ gridColumn: "1/-1" }}>
                <LogOut size={18} color="#ef4444" />
                {t("app.logout")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </BankLangCtx.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* DASHBOARD HOME — 3-column grid layout                                      */
/* -------------------------------------------------------------------------- */
function CardTrackingSection({ style, headers, t }) {
  const [trackingNum, setTrackingNum] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const STEPS = [
    { key: "preparation", label: t("tracking.status.preparation") },
    { key: "shipped", label: t("tracking.status.shipped") },
    { key: "transit", label: t("tracking.status.transit") },
    { key: "delivered", label: t("tracking.status.delivered") },
  ];

  const search = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await api.get(`/bank-user/cards/tracking/${trackingNum.trim()}`, { headers });
      setResult(data);
    } catch { setError(t("tracking.error.not_found")); }
    finally { setLoading(false); }
  };

  const currentIdx = result ? STEPS.findIndex((s) => s.key === result.tracking_status) : -1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <form onSubmit={search} style={{ display: "flex", gap: 10 }}>
        <input
          value={trackingNum}
          onChange={(e) => setTrackingNum(e.target.value.toUpperCase())}
          placeholder="TRK123456789"
          required
          style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${style.accent}50`, background: "var(--bg)", color: "var(--navy)", fontSize: "0.9rem", fontFamily: "monospace", outline: "none", boxSizing: "border-box" }}
        />
        <button type="submit" disabled={loading} style={{ padding: "12px 20px", borderRadius: 10, background: style.accent, color: "#fff", fontWeight: 700, border: "none", cursor: "pointer", flexShrink: 0 }}>
          {loading ? "…" : t("common.search")}
        </button>
      </form>
      {error && <div style={{ color: "#ef4444", fontSize: "0.85rem", padding: "10px 14px", background: "#fef2f2", borderRadius: 10, border: "1px solid #fecaca" }}>{error}</div>}
      {result && (
        <div style={{ borderRadius: 16, padding: 20, background: "var(--bg)", border: `1px solid ${style.accent}30` }}>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--navy)", marginBottom: 4 }}>N° {result.tracking_number}</div>
          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: 20, textTransform: "capitalize" }}>{result.physical_type} • {result.physical_network}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {STEPS.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20, flexShrink: 0 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: done ? style.accent : "var(--line)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: active ? `0 0 0 4px ${style.accent}30` : "none" }}>
                      {done && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
                    </div>
                    {i < STEPS.length - 1 && <div style={{ width: 2, height: 32, background: done ? style.accent : "var(--line)", marginTop: 2 }} />}
                  </div>
                  <div style={{ paddingBottom: i < STEPS.length - 1 ? 20 : 0, paddingTop: 0 }}>
                    <div style={{ fontWeight: active ? 700 : 500, fontSize: "0.88rem", color: done ? "var(--navy)" : "var(--muted)" }}>{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DepositSection({ bank, style, t, setActiveSection }) {
  const instructions = bank?.deposit_instructions || t("deposit.default_instructions");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ borderRadius: 16, padding: 20, background: style.accent + "10", border: `1px solid ${style.accent}30` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: style.accent + "20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ArrowDownToLine size={18} color={style.accent} />
          </div>
          <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)" }}>{t("deposit.title")}</div>
        </div>
        <div style={{ fontSize: "0.88rem", color: "var(--navy)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{instructions}</div>
      </div>
      <button onClick={() => setActiveSection("messages")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 12, background: style.accent, color: "#fff", fontWeight: 600, fontSize: "0.88rem", border: "none", cursor: "pointer" }}>
        <MessageCircle size={16} />{t("notif.contact_support")}
      </button>
    </div>
  );
}

function DashboardHome({ bank, style, me, txns, cards, headers, setActiveSection, reload }) {
  const { t } = useContext(BankLangCtx);
  const [ibanOpen, setIbanOpen] = useState(false);
  const [cardActionLoading, setCardActionLoading] = useState(null);
  const [dashPinData, setDashPinData] = useState(null);
  const [dashRevealData, setDashRevealData] = useState(null);
  const [dashDeleteId, setDashDeleteId] = useState(null);
  // PIN gate before revealing card details (same flow as the cards page)
  const [dashPinPrompt, setDashPinPrompt] = useState(false);
  const [dashPinInput, setDashPinInput] = useState("");
  const [dashPinError, setDashPinError] = useState("");
  const [dashPinVerifying, setDashPinVerifying] = useState(false);
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  const thisMonthCredit = txns
    .filter((t) => { const d = new Date(t.created_at); return t.type === "credit" && d.getMonth() === thisMonth && d.getFullYear() === thisYear; })
    .reduce((s, t) => s + Number(t.amount), 0);

  const thisMonthDebit = txns
    .filter((t) => { const d = new Date(t.created_at); return t.type === "debit" && d.getMonth() === thisMonth && d.getFullYear() === thisYear; })
    .reduce((s, t) => s + Number(t.amount), 0);

  const mainCard = cards[0] || null;

  // Sparkline from last transactions
  const sparkPoints = (() => {
    const pts = txns.slice(0, 12).reverse();
    if (pts.length < 2) return null;
    const W = 200, H = 44;
    const vals = pts.map((t) => Number(t.amount));
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = max - min || 1;
    return pts.map((t, i) => `${(i / (pts.length - 1)) * W},${H - ((Number(t.amount) - min) / range) * (H - 8) - 4}`).join(" ");
  })();

  // Spending by category (donut)
  const spendCats = (() => {
    const cats = {};
    txns.filter((t) => t.type === "debit").forEach((t) => {
      const c = t.category || "Autre";
      cats[c] = (cats[c] || 0) + Number(t.amount);
    });
    return Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 5);
  })();
  const totalSpend = spendCats.reduce((s, [, v]) => s + v, 0) || 1;
  const COLORS = [style.accent, "#10b981", "#f59e0b", "#6366f1", "#ec4899"];

  // SVG donut segments
  const R = 52, CX = 64, CY = 64, SW = 20;
  const CIRC = 2 * Math.PI * R;
  let offset = 0;
  const donutSegs = spendCats.map(([name, val], i) => {
    const pct = val / totalSpend;
    const dash = pct * CIRC;
    const seg = { name, val, pct, dash, offset, color: COLORS[i] };
    offset += dash;
    return seg;
  });

  const quickBlock = async () => {
    if (!mainCard) return;
    setCardActionLoading("block");
    try {
      const { data } = await api.put(`/bank-user/cards/${mainCard.id}/block`, {}, { headers });
      toast.success(data.status === "blocked" ? t("cards.toast.blocked") : t("cards.toast.unblocked"));
      if (reload) reload();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setCardActionLoading(null); }
  };

  const quickPin = async () => {
    if (!mainCard) return;
    setCardActionLoading("pin");
    try {
      const { data } = await api.get(`/bank-user/cards/${mainCard.id}/pin`, { headers });
      setDashPinData(data);
    } catch (err) { toast.error(tError(err, t)); }
    finally { setCardActionLoading(null); }
  };

  // Step 1 — clicking "Infos" asks for the user's PIN before revealing anything.
  const quickReveal = () => {
    if (!mainCard) return;
    setDashPinPrompt(true); setDashPinInput(""); setDashPinError("");
  };

  // Step 2 — PIN verified → fetch and show the details modal.
  const confirmDashRevealPin = async () => {
    if (!mainCard || dashPinInput.length !== 4) return;
    setDashPinVerifying(true); setDashPinError("");
    try {
      await api.post("/bank-user/verify-pin", { pin: dashPinInput }, { headers });
      const { data } = await api.get(`/bank-user/cards/${mainCard.id}/reveal`, { headers });
      setDashRevealData(data);
      setDashPinPrompt(false); setDashPinInput("");
    } catch (err) {
      setDashPinError(err?.response?.status === 401 ? (t("auth.pin.length_error") || "Code PIN incorrect") : tError(err, t));
      setDashPinInput("");
    } finally { setDashPinVerifying(false); }
  };

  const quickDelete = async () => {
    if (!dashDeleteId) return;
    setCardActionLoading("delete");
    try {
      await api.delete(`/bank-user/cards/${dashDeleteId}`, { headers });
      setDashDeleteId(null);
      toast.success(t("cards.deleted") || "Carte supprimée");
      if (reload) reload();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setCardActionLoading(null); }
  };

  const QUICK = [
    { label: t("dash.quick.deposit"), Icon: ArrowDownToLine, color: "#16a34a", bg: "#dcfce7", fn: () => setActiveSection("deposit") },
    { label: t("dash.quick.transfer"), Icon: Send, color: style.accent, bg: style.accent + "18", fn: () => setActiveSection("transfers") },
    { label: t("dash.quick.history"), Icon: TrendingUp, color: "#7c3aed", bg: "#ede9fe", fn: () => setActiveSection("transactions") },
    { label: t("dash.quick.cards"), Icon: CardIcon, color: "#16a34a", bg: "#dcfce7", fn: () => setActiveSection("cards") },
    { label: t("dash.quick.profile"), Icon: User, color: "#d97706", bg: "#fef3c7", fn: () => setActiveSection("profile") },
  ];

  const monthTxCount = txns.filter((t) => {
    const d = new Date(t.created_at);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  }).length;

  return (
    <>
      <div className="db-dash-header">
        <h1 className="db-dash-title">{t("dash.greeting", { name: (me.name || "").split(" ")[0] })}</h1>
        <p className="db-dash-sub">{t("dash.overview", { date: now.toLocaleDateString(undefined, { month: "long", year: "numeric" }) })}</p>
      </div>

      <div className="db-dash-grid">

        {/* ── Left column ── */}
        <div className="db-col">

          {/* Balance card */}
          <div className="db-balance-card" style={{ background: style.cardGradient }} data-testid="balance-card">
            <div>
              <div className="db-balance-label">{t("dash.total_balance")}</div>
              {(() => {
                const balStr = fmtMoney(me.balance, me?.currency || bank?.default_currency);
                const fs = Math.min(1.9, Math.max(0.72, 20.9 / balStr.length));
                return <div className="db-balance-amount" style={{ fontSize: `${fs}rem` }} data-testid="balance-amount">{balStr}</div>;
              })()}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, alignItems: "center" }}>
                <div className="db-balance-growth">
                  <TrendingUp size={11} />{t("dash.this_month", { amount: fmtMoney(thisMonthCredit, me?.currency || bank?.default_currency) })}
                </div>
                <button onClick={() => setIbanOpen(true)} data-testid="show-iban-btn" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, color: "#fff", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  <Eye size={11} />{t("dash.view_iban")}
                </button>
              </div>
            </div>
            {sparkPoints && (
              <svg width="100%" height="44" viewBox="0 0 200 44" preserveAspectRatio="none" style={{ marginTop: 14, opacity: 0.75 }}>
                <polyline points={sparkPoints} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <IbanModal open={ibanOpen} onClose={() => setIbanOpen(false)} headers={headers} bank={bank} />

          {/* Account cards */}
          <div className="db-widget">
            <div className="db-widget-header">
              <span className="db-widget-title">{t("dash.my_accounts")}</span>
            </div>
            <div className="db-account-row">
              <div className="db-account-icon" style={{ background: style.accent + "18" }}><Wallet size={16} color={style.accent} /></div>
              <div className="db-account-info">
                <div className="db-account-name">{t("dash.primary_account")}</div>
                <div className="db-account-num">••{String(me.id || "").slice(-4)}</div>
              </div>
              <div className="db-account-bal">{fmtMoney(me.balance, me?.currency || bank?.default_currency)}</div>
            </div>
            <div className="db-account-row">
              <div className="db-account-icon" style={{ background: "#10b98118" }}><Star size={16} color="#10b981" /></div>
              <div className="db-account-info">
                <div className="db-account-name">{t("dash.savings_account")}</div>
                <div className="db-account-num">{t("dash.remunerated")}</div>
              </div>
              <div className="db-account-bal" style={{ color: "#10b981" }}>{fmtMoney(0, me?.currency || bank?.default_currency)}</div>
            </div>
            <div className="db-account-btns">
              <button className="db-acct-btn" onClick={() => setActiveSection("transactions")}>{t("dash.details")}</button>
              <button className="db-acct-btn primary" onClick={() => setActiveSection("transfers")}>{t("dash.transfer_btn")}</button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="db-widget">
            <div className="db-widget-header"><span className="db-widget-title">{t("dash.quick_actions")}</span></div>
            <div className="db-qa-grid">
              {QUICK.map(({ label, Icon, color, bg, fn }) => (
                <button key={label} className="db-qa-btn" onClick={fn}>
                  <div className="db-qa-icon" style={{ background: bg }}><Icon size={16} color={color} /></div>
                  <span className="db-qa-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Budget */}
          <div className="db-widget">
            <div className="db-widget-header"><span className="db-widget-title">{t("dash.monthly_budget")}</span></div>
            <div className="db-budget">
              {[
                { label: t("dash.income"), val: thisMonthCredit, total: thisMonthCredit + thisMonthDebit || 1, color: "#16a34a" },
                { label: t("dash.expenses"), val: thisMonthDebit, total: thisMonthCredit + thisMonthDebit || 1, color: "#ef4444" },
              ].map((item) => (
                <div key={item.label} className="db-budget-item">
                  <div className="db-budget-row-top">
                    <span className="db-budget-lbl">{item.label}</span>
                    <span className="db-budget-val" style={{ color: item.color }}>{fmtMoney(item.val, me?.currency || bank?.default_currency)}</span>
                  </div>
                  <div className="db-bar">
                    <div className="db-bar-fill" style={{ width: `${Math.round((item.val / item.total) * 100)}%`, background: item.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Center column ── */}
        <div className="db-col">

          {/* Donut chart */}
          <div className="db-widget">
            <div className="db-widget-header"><span className="db-widget-title">{t("dash.spending_cat")}</span></div>
            <div className="db-donut-wrap">
              {spendCats.length === 0 ? (
                <div style={{ padding: "24px 0", textAlign: "center", color: "var(--muted)", fontSize: "0.82rem" }}>{t("dash.no_spending")}</div>
              ) : (
                <>
                  <svg width={128} height={128} viewBox="0 0 128 128">
                    <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--line)" strokeWidth={SW} />
                    {donutSegs.map((seg, i) => (
                      <circle
                        key={i}
                        cx={CX} cy={CY} r={R}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth={SW}
                        strokeDasharray={`${seg.dash} ${CIRC - seg.dash}`}
                        strokeDashoffset={-seg.offset}
                        transform={`rotate(-90 ${CX} ${CY})`}
                        strokeLinecap="butt"
                      />
                    ))}
                    <text x={CX} y={CY - 6} textAnchor="middle" fontSize={10} fill="var(--muted)" fontFamily="DM Sans, sans-serif">{t("common.total") || "Total"}</text>
                    <text x={CX} y={CY + 10} textAnchor="middle" fontSize={12} fill="var(--navy)" fontWeight={700} fontFamily="Syne, sans-serif">
                      {fmtMoney(totalSpend, me?.currency || bank?.default_currency).split(" ")[0]}
                    </text>
                  </svg>
                  <div className="db-donut-legend">
                    {donutSegs.map((seg, i) => (
                      <div key={i} className="db-legend-row">
                        <div className="db-legend-dot" style={{ background: seg.color }} />
                        <span className="db-legend-name">{seg.name}</span>
                        <span className="db-legend-val">{fmtMoney(seg.val, me?.currency || bank?.default_currency)}</span>
                        <span className="db-legend-pct">{Math.round(seg.pct * 100)}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Recent transactions */}
          <div className="db-widget">
            <div className="db-widget-header">
              <span className="db-widget-title">{t("dash.recent_tx")}</span>
              <button className="db-widget-link" onClick={() => setActiveSection("transactions")}>{t("dash.see_all")} <ChevronRight size={12} /></button>
            </div>
            {txns.length === 0 ? (
              <div style={{ padding: "28px 1.25rem", textAlign: "center", color: "var(--muted)", fontSize: "0.82rem" }}>{t("dash.no_tx")}</div>
            ) : (
              txns.slice(0, 6).map((t) => (
                <div key={t.id} className="db-tx-row">
                  <div className="db-tx-icon" style={{ background: t.type === "credit" ? "#16a34a18" : "#ef444418" }}>
                    {t.type === "credit" ? <ArrowDownLeft size={14} color="#16a34a" /> : <ArrowUpRight size={14} color="#ef4444" />}
                  </div>
                  <div className="db-tx-info">
                    <div className="db-tx-lbl">{t.label}</div>
                    <div className="db-tx-date">{new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="db-tx-amount" style={{ color: t.type === "credit" ? "#16a34a" : "#ef4444" }}>
                    {t.type === "credit" ? "+" : "−"}{fmtMoney(t.amount, me?.currency || bank?.default_currency)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Right column ── */}
        <div className="db-col db-col-right">

          {/* VISA card */}
          <div className="db-widget">
            <div className="db-widget-header">
              <span className="db-widget-title">{t("dash.main_card")}</span>
              <button className="db-widget-link" onClick={() => setActiveSection("cards")}>{t("dash.manage")} <ChevronRight size={12} /></button>
            </div>
            {mainCard ? (
              <>
                <div className="db-visa" style={{ background: style.cardGradient }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex" }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.55)" }} />
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.35)", marginLeft: -10 }} />
                    </div>
                    <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.85rem", fontStyle: "italic" }}>VISA</span>
                  </div>
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.82rem", letterSpacing: "0.18em", fontFamily: "monospace", marginBottom: 8 }}>{mainCard.number_masked}</div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>{t("dash.holder")}</div>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.72rem" }}>{mainCard.holder_name}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>{t("dash.expires")}</div>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.72rem" }}>{mainCard.expiry}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="db-card-actions">
                  <button onClick={quickBlock} disabled={cardActionLoading === "block"} className="db-card-action" style={mainCard?.status === "blocked" ? { color: "#dc2626" } : {}}>
                    <Lock size={15} color={mainCard?.status === "blocked" ? "#dc2626" : style.accent} />
                    {cardActionLoading === "block" ? "…" : mainCard?.status === "blocked" ? t("cards.btn.unblock") : t("dash.block")}
                  </button>
                  <button onClick={quickPin} disabled={cardActionLoading === "pin"} className="db-card-action">
                    <Key size={15} color={style.accent} />
                    {cardActionLoading === "pin" ? "…" : t("dash.pin")}
                  </button>
                  <button onClick={quickReveal} disabled={cardActionLoading === "reveal"} className="db-card-action">
                    <Eye size={15} color={style.accent} />
                    {cardActionLoading === "reveal" ? "…" : t("cards.btn.info")}
                  </button>
                  <button onClick={() => mainCard && !mainCard.physical_ordered && setDashDeleteId(mainCard.id)} disabled={!!mainCard?.physical_ordered} className="db-card-action" style={{ color: mainCard?.physical_ordered ? "var(--muted)" : "#dc2626", opacity: mainCard?.physical_ordered ? 0.45 : 1, cursor: mainCard?.physical_ordered ? "not-allowed" : "pointer" }} title={mainCard?.physical_ordered ? t("cards.delete.physical_tooltip") : undefined}>
                    <Trash2 size={15} color={mainCard?.physical_ordered ? "var(--muted)" : "#dc2626"} />{t("common.delete")}
                  </button>
                </div>

                {/* PIN modal */}
                {dashPinData && (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
                    <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 300, width: "100%", border: "1px solid var(--line)", padding: "1.5rem", textAlign: "center" }}>
                      <Key size={26} color={style.accent} style={{ marginBottom: 10 }} />
                      <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", marginBottom: 14 }}>{t("cards.pin.title")}</div>
                      <div style={{ background: "var(--bg)", borderRadius: 12, padding: "16px", letterSpacing: "0.5em", fontFamily: "monospace", fontWeight: 700, fontSize: "2rem", color: "var(--navy)", marginBottom: 16 }}>{dashPinData.pin}</div>
                      <button onClick={() => setDashPinData(null)} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", cursor: "pointer", fontWeight: 600, color: "var(--navy)" }}>{t("common.close")}</button>
                    </div>
                  </div>
                )}

                {/* PIN gate before reveal */}
                {dashPinPrompt && (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
                    <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 380, width: "100%", border: "1px solid var(--line)" }}>
                      <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 8 }}>
                          <Key size={16} color={style.accent} /> {t("cards.reveal.title")}
                        </span>
                        <button onClick={() => { setDashPinPrompt(false); setDashPinInput(""); setDashPinError(""); }} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
                      </div>
                      <div style={{ padding: "1.5rem 1.4rem" }}>
                        <p style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", margin: "0 0 18px", lineHeight: 1.5 }}>
                          {t("cards.reveal.pin_prompt")}
                        </p>
                        <OTPBoxes value={dashPinInput} onChange={(v) => { setDashPinInput(v); setDashPinError(""); }} disabled={dashPinVerifying} accent={style.accent} length={4} autoFocus testId="dash-reveal-pin" />
                        {dashPinError && <p style={{ fontSize: ".78rem", color: "#dc2626", textAlign: "center", margin: "12px 0 0" }}>{dashPinError}</p>}
                        <button
                          type="button"
                          onClick={confirmDashRevealPin}
                          disabled={dashPinVerifying || dashPinInput.length !== 4}
                          style={{ width: "100%", marginTop: 18, padding: "13px", borderRadius: 12, background: style.accent, color: "#fff", fontWeight: 700, fontSize: "0.92rem", border: "none", cursor: (dashPinVerifying || dashPinInput.length !== 4) ? "not-allowed" : "pointer", opacity: (dashPinVerifying || dashPinInput.length !== 4) ? 0.6 : 1 }}
                          data-testid="dash-reveal-pin-submit"
                        >
                          {dashPinVerifying ? t("common.loading") : t("cards.reveal.show")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reveal modal */}
                {dashRevealData && (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
                    <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 360, width: "100%", border: "1px solid var(--line)", padding: "1.5rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)" }}>{t("cards.reveal.title")}</div>
                        <button onClick={() => setDashRevealData(null)} style={{ background: "var(--bg)", border: "none", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {[[t("cards.reveal.number"), dashRevealData.number_full], [t("cards.holder"), dashRevealData.holder_name], [t("cards.reveal.expiry"), dashRevealData.expiry], [t("cards.reveal.cvv"), dashRevealData.cvv]].map(([label, val]) => val && (
                          <div key={label} style={{ background: "var(--bg)", borderRadius: 10, padding: "10px 14px" }}>
                            <div style={{ fontSize: ".68rem", color: "var(--muted)", marginBottom: 2 }}>{label}</div>
                            <div style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--navy)", letterSpacing: "0.05em" }}>{val}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete confirm */}
                {dashDeleteId && (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
                    <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 340, width: "100%", border: "1px solid var(--line)", padding: "1.5rem" }}>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <Trash2 size={26} color="#dc2626" style={{ marginBottom: 10 }} />
                        <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", marginBottom: 6 }}>{t("cards.delete.title")}</div>
                        <p style={{ color: "var(--muted)", fontSize: ".82rem", lineHeight: 1.5, margin: 0 }}>{t("cards.delete.confirm_msg")}</p>
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                        <button onClick={() => setDashDeleteId(null)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", cursor: "pointer", fontWeight: 600 }}>{t("common.cancel")}</button>
                        <button onClick={quickDelete} disabled={cardActionLoading === "delete"} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: "#dc2626", color: "#fff", cursor: "pointer", fontWeight: 700 }}>
                          {cardActionLoading === "delete" ? "…" : t("common.delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: "28px 1.25rem", textAlign: "center", color: "var(--muted)", fontSize: "0.82rem" }}>
                <CardIcon size={28} style={{ margin: "0 auto 8px", display: "block", opacity: 0.35 }} />
                {t("dash.no_card")}
              </div>
            )}
          </div>

          {/* Activity stats */}
          <div className="db-widget">
            <div className="db-widget-header"><span className="db-widget-title">{t("dash.monthly_activity")}</span></div>
            <div style={{ padding: "0 1.25rem 1.25rem", display: "flex", flexDirection: "column" }}>
              {[
                { label: t("dash.operations"), value: `${monthTxCount} ops` },
                { label: t("dash.income"), value: fmtMoney(thisMonthCredit, me?.currency || bank?.default_currency), color: "#16a34a" },
                { label: t("tx.expenses"), value: fmtMoney(thisMonthDebit, me?.currency || bank?.default_currency), color: "#ef4444" },
              ].map((stat, i, arr) => (
                <div key={stat.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--line)" : "none" }}>
                  <span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{stat.label}</span>
                  <span style={{ fontSize: "0.85rem", fontWeight: 700, color: stat.color || "var(--navy)" }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* TRANSACTIONS SECTION BODY                                                   */
/* -------------------------------------------------------------------------- */
function TransactionsSectionBody({ bank, style, txns, me }) {
  const { t } = useContext(BankLangCtx);
  const [filter, setFilter] = useState("all");

  const filtered = txns.filter((tx) => {
    if (filter === "credit") return tx.type === "credit";
    if (filter === "debit") return tx.type === "debit";
    return true;
  });

  const groupByDate = (items) => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    items.forEach((tx) => {
      const d = new Date(tx.created_at);
      let key;
      if (d.toDateString() === today.toDateString()) key = t("tx.today");
      else if (d.toDateString() === yesterday.toDateString()) key = t("tx.yesterday");
      else key = d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
      if (!groups[key]) groups[key] = [];
      groups[key].push(tx);
    });
    return groups;
  };

  const groups = groupByDate(filtered);
  const filters = [{ k: "all", label: t("tx.all") }, { k: "credit", label: t("tx.income") }, { k: "debit", label: t("tx.expenses") }];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 18, background: "var(--bg)", padding: 4, borderRadius: 10, border: "1px solid var(--line)" }}>
        {filters.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} style={{ flex: 1, padding: "7px", borderRadius: 8, border: "none", background: filter === f.k ? "var(--accent)" : "transparent", color: filter === f.k ? "#fff" : "var(--muted)", fontWeight: filter === f.k ? 600 : 400, fontSize: "0.82rem", cursor: "pointer", transition: "all 0.15s" }}>
            {f.label}
          </button>
        ))}
      </div>
      {Object.keys(groups).length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)", fontSize: "0.85rem" }}>{t("tx.none")}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {Object.entries(groups).map(([date, items]) => (
            <div key={date}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 8 }}>{date}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {items.map((tx) => (
                  <div key={tx.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: 12, background: "var(--bg)", border: "1px solid var(--line)" }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: tx.type === "credit" ? "#16a34a18" : "#ef444418", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {tx.type === "credit" ? <ArrowDownLeft size={15} color="#16a34a" /> : <ArrowUpRight size={15} color="#ef4444" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--navy)" }}>{tx.label}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>{tx.category || (tx.type === "credit" ? t("tx.income") : t("tx.expenses"))}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: tx.type === "credit" ? "#16a34a" : "#ef4444", flexShrink: 0 }}>
                      {tx.type === "credit" ? "+" : "−"}{fmtMoney(tx.amount, me?.currency || bank?.default_currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* TRANSFERS SECTION BODY                                                      */
/* -------------------------------------------------------------------------- */
const EMPTY_TRANSFER_FORM = { amount: "", last_name: "", first_name: "", iban_beneficiary: "", bic_beneficiary: "", bank_name: "", country: "", city: "", label: "" };

function OTPBoxes({ value = "", onChange, disabled, accent, length = 6, autoFocus = false, testId = "bu-tr-code" }) {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");
  const last = length - 1;

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < last) {
      refs.current[i + 1]?.focus();
    }
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) return;
    const char = raw[raw.length - 1];
    const next = (value.slice(0, i) + char + value.slice(i + 1)).slice(0, length);
    onChange(next);
    if (i < last) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted) { onChange(pasted); refs.current[Math.min(pasted.length, last)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center" }} data-testid={testId}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          style={{
            width: "min(44px, 11.5vw)", height: 52, borderRadius: 10, textAlign: "center",
            fontSize: "1.4rem", fontWeight: 700, fontFamily: "monospace",
            border: d ? `2px solid ${accent}` : "2px dashed rgba(0,0,0,0.25)",
            background: d ? accent + "12" : "var(--bg)",
            color: "var(--navy)", outline: "none",
            boxShadow: d ? `0 0 0 3px ${accent}22` : "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
            opacity: disabled ? 0.5 : 1,
          }}
        />
      ))}
    </div>
  );
}

function TransfersSectionBody({ style, bank, headers, reload, me, setActiveSection, notifs = [] }) {
  const { t, isDemo } = useContext(BankLangCtx);

  // ── Persist in-progress form for 5 min across section navigation ─────────
  // When the user clicks "Notifications" or "Messages" mid-transfer to copy
  // a code, this component unmounts. On remount we restore from sessionStorage
  // so they keep their amount, beneficiary, step, etc.
  const PERSIST_KEY = `bu_transfer_state__${bank?.slug || "default"}`;
  const PERSIST_TTL_MS = 5 * 60 * 1000;
  const loadPersisted = () => {
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.ts !== "number") return null;
      if (Date.now() - parsed.ts > PERSIST_TTL_MS) {
        sessionStorage.removeItem(PERSIST_KEY);
        return null;
      }
      return parsed;
    } catch { return null; }
  };
  const persistedRef = useRef(loadPersisted());
  const persisted = persistedRef.current;

  const [form, setForm] = useState(persisted?.form || EMPTY_TRANSFER_FORM);
  const [execution, setExecution] = useState(persisted?.execution || "immediate");
  const [pending, setPending] = useState(persisted?.pending || null);
  const [code, setCode] = useState(persisted?.code || "");
  const [progress, setProgress] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [progressDone, setProgressDone] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const barRef = useRef(null);
  const pctRef = useRef(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [saveBeneficiary, setSaveBeneficiary] = useState(!!persisted?.saveBeneficiary);
  const [showBenefPicker, setShowBenefPicker] = useState(false);

  // Mirror state into sessionStorage every time anything relevant changes.
  // Stays for max 5 min (checked on mount). Cleared explicitly on successful
  // transfer completion via setForm(EMPTY_TRANSFER_FORM) which writes an empty state.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        PERSIST_KEY,
        JSON.stringify({ ts: Date.now(), form, execution, pending, code, saveBeneficiary }),
      );
    } catch (_) {}
  }, [form, execution, pending, code, saveBeneficiary, PERSIST_KEY]);

  useEffect(() => {
    if (!headers) return;
    api.get("/bank-user/beneficiaries", { headers })
      .then(({ data }) => setBeneficiaries(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.warn("[beneficiaries] GET failed", err?.response?.status, err?.response?.data || err?.message);
      });
  }, [headers]);

  const pickBenef = (b) => {
    setForm((f) => ({ ...f, last_name: b.last_name, first_name: b.first_name, iban_beneficiary: b.iban, bic_beneficiary: b.bic || "", bank_name: b.bank_name || "", country: b.country || "", city: b.city || "" }));
    setShowBenefPicker(false);
  };

  const deleteBenef = async (id) => {
    try {
      await api.delete(`/bank-user/beneficiaries/${id}`, { headers });
      setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.warn("[beneficiaries] DELETE failed", err?.response?.status, err?.response?.data);
      toast.error(tError(err, t));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (isDemo) { toast.info(t("demo.disabled")); return; }
    try {
      const { data } = await api.post("/bank-user/transfers", { ...form, amount: Number(form.amount) }, { headers });
      if (saveBeneficiary && form.last_name && form.iban_beneficiary) {
        try {
          const nb = await api.post("/bank-user/beneficiaries", {
            last_name: form.last_name, first_name: form.first_name,
            iban: form.iban_beneficiary, bic: form.bic_beneficiary,
            bank_name: form.bank_name, country: form.country, city: form.city, label: form.label,
          }, { headers });
          setBeneficiaries((prev) => [nb.data, ...prev]);
          toast.success(t("beneficiary.saved_ok"));
        } catch (errBenef) {
          // Surface the real reason instead of silently swallowing it
          console.warn("[beneficiaries] POST failed", errBenef?.response?.status, errBenef?.response?.data);
          toast.error(tError(errBenef, t));
        }
      }
      if (data.needs_code) {
        setPending(data);
      } else {
        toast.success(t("transfer.done"));
        setForm(EMPTY_TRANSFER_FORM);
        setSaveBeneficiary(false);
        reload();
      }
    } catch (err) { toast.error(tError(err, t)); }
  };

  const animateProgress = (target, durationMs = 2400) => {
    return new Promise((resolve) => {
      const start = performance.now();
      const tick = (now) => {
        const ratio = Math.min(1, (now - start) / durationMs);
        const val = ratio * target;
        // Direct DOM updates — bar and number stay perfectly in sync, no React batching
        if (barRef.current) barRef.current.style.width = `${val}%`;
        if (pctRef.current) pctRef.current.textContent = `${Math.round(val)}%`;
        setProgress(val); // keep React state in sync for conditional rendering
        if (ratio < 1) requestAnimationFrame(tick); else resolve();
      };
      requestAnimationFrame(tick);
    });
  };

  const validate = async (e) => {
    e.preventDefault();
    if (progressActive) return;
    setProgressActive(true);
    setProgressDone(false);
    setProgressMessage("");
    setProgress(0);
    try {
      const { data } = await api.post(`/bank-user/transfers/${pending.id}/validate`, { code }, { headers });
      const target = Number(data?.stop_pct ?? 100);
      await animateProgress(target);
      if (data?.completed) {
        setProgressDone(true);
        setProgressMessage(t("transfer.progress.completed"));
        toast.success(t("transfer.validated"));
        setTimeout(() => {
          setPending(null); setCode(""); setForm(EMPTY_TRANSFER_FORM); setSaveBeneficiary(false);
          setProgress(0); setProgressActive(false); setProgressDone(false); setProgressMessage("");
          reload();
        }, 1200);
      } else {
        setProgressMessage(data?.stop_message || t("transfer.progress.default_message"));
        setProgressActive(false);
      }
    } catch (err) {
      toast.error(tError(err, t));
      setProgress(0); setProgressActive(false); setProgressMessage("");
    }
  };

  const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid rgba(0,0,0,0.22)", background: "var(--bg)", color: "var(--navy)", fontSize: "0.9rem", outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div>
      {!pending ? (
        <form onSubmit={send} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Saved beneficiaries picker */}
          {beneficiaries.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)" }}>{t("beneficiary.saved")}</span>
                <button type="button" onClick={() => setShowBenefPicker((v) => !v)} style={{ fontSize: "0.75rem", fontWeight: 600, color: style.accent, background: style.accent + "15", border: `1px solid ${style.accent}30`, borderRadius: 8, padding: "3px 10px", cursor: "pointer" }}>
                  {showBenefPicker ? "✕" : t("beneficiary.pick")}
                </button>
              </div>
              {showBenefPicker && (
                <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden", marginBottom: 4 }}>
                  {beneficiaries.map((b) => (
                    <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--line)", cursor: "pointer" }}
                      onClick={() => pickBenef(b)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: ".85rem", color: "var(--navy)" }}>{b.last_name} {b.first_name}</div>
                        <div style={{ fontSize: ".72rem", color: "var(--muted)", fontFamily: "monospace" }}>{b.iban}</div>
                        {b.bank_name && <div style={{ fontSize: ".7rem", color: "var(--muted)" }}>{b.bank_name}</div>}
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteBenef(b.id); }}
                        style={{ background: "#fee2e2", border: "none", borderRadius: 8, padding: "4px 8px", fontSize: ".7rem", color: "#dc2626", cursor: "pointer" }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Section bénéficiaire */}
          <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: style.accent, marginBottom: -4 }}>{t("transfer.section.beneficiary")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("auth.field.lastName")}</label>
              <input value={form.last_name} required onChange={(e) => setForm({ ...form, last_name: e.target.value })} style={{ ...inputStyle, textTransform: "uppercase" }} data-testid="bu-tr-lastname" />
            </div>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("auth.field.firstName")}</label>
              <input value={form.first_name} required onChange={(e) => setForm({ ...form, first_name: e.target.value })} style={inputStyle} data-testid="bu-tr-firstname" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.field.iban")}</label>
            <input value={form.iban_beneficiary} required onChange={(e) => setForm({ ...form, iban_beneficiary: e.target.value.toUpperCase().replace(/\s/g, "") })} style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: "0.05em" }} data-testid="bu-tr-iban" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.field.bic")}</label>
              <input value={form.bic_beneficiary} onChange={(e) => setForm({ ...form, bic_beneficiary: e.target.value.toUpperCase() })} style={{ ...inputStyle, fontFamily: "monospace" }} data-testid="bu-tr-bic" />
            </div>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.field.bank")}</label>
              <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} style={inputStyle} data-testid="bu-tr-bank" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("auth.field.country")}</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} style={inputStyle} data-testid="bu-tr-country" />
            </div>
            <div>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.field.city")}</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} data-testid="bu-tr-city" />
            </div>
          </div>
          {/* Section montant & motif */}
          <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: style.accent, marginBottom: -4, marginTop: 4 }}>{t("transfer.section.details")}</div>
          <div>
            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.amount")}</label>
            <div style={{ position: "relative" }}>
              <input type="number" min={1} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ ...inputStyle, paddingRight: 60, fontSize: "1.1rem", fontWeight: 600 }} data-testid="bu-tr-amount" />
              <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: "0.8rem", fontWeight: 700, color: "var(--muted)" }}>{me?.currency || bank?.default_currency}</span>
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: 6 }}>{t("transfer.balance")} <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{fmtMoney(me.balance, me?.currency || bank?.default_currency)}</span></div>
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>{t("transfer.reason")}</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} style={inputStyle} data-testid="bu-tr-label" />
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 10 }}>{t("transfer.execution")}</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[{ k: "immediate", label: t("transfer.immediate") }, { k: "deferred", label: t("transfer.delayed") }].map((opt) => (
                <button key={opt.k} type="button" onClick={() => setExecution(opt.k)} style={{ padding: "12px", borderRadius: 10, border: `2px solid ${execution === opt.k ? "var(--accent)" : "var(--line)"}`, background: execution === opt.k ? "var(--accent)" + "15" : "transparent", color: execution === opt.k ? "var(--accent)" : "var(--muted)", fontWeight: execution === opt.k ? 700 : 400, fontSize: "0.85rem", cursor: "pointer" }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "var(--navy)", cursor: "pointer" }}>
            <input type="checkbox" checked={saveBeneficiary} onChange={(e) => setSaveBeneficiary(e.target.checked)} style={{ width: 15, height: 15, accentColor: style.accent }} />
            {t("beneficiary.save_this")}
          </label>
          <button type="submit" style={{ width: "100%", padding: "14px", borderRadius: 12, background: "var(--accent)", color: "#fff", fontWeight: 700, fontSize: "1rem", border: "none", cursor: "pointer", marginTop: 4, boxShadow: `0 4px 20px ${style.accent}40` }} data-testid="bu-tr-send">{t("transfer.continue")}</button>
        </form>
      ) : (
        <form onSubmit={validate} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ borderRadius: 12, padding: 18, background: "var(--accent)" + "15", border: "1px solid var(--accent)" + "30" }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--accent)", marginBottom: 8 }}>{t("transfer.pending.title")}</div>
            <div style={{ fontSize: "0.85rem", color: "var(--navy)", lineHeight: 1.8 }}>
              {t("transfer.pending.amount")} <strong>{fmtMoney(pending.amount, me?.currency || bank?.default_currency)}</strong><br />
              {t("transfer.pending.to")} <strong>{pending.recipient}</strong><br />
              {pending.iban_beneficiary && <><span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{t("transfer.iban_label")} </span><span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{pending.iban_beneficiary}</span><br /></>}
              {pending.bank_name && <><span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{t("transfer.bank_label")} </span><span style={{ fontSize: "0.82rem" }}>{pending.bank_name}</span></>}
            </div>
            <p style={{ fontSize: "0.78rem", color: "var(--muted)", margin: "10px 0 0", lineHeight: 1.5 }}>{t("transfer.pending.hint")}</p>
            {setActiveSection && (
              <button
                onClick={() => setActiveSection("messages")}
                style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.75rem", fontWeight: 600, color: style.accent, background: style.accent + "18", border: `1px solid ${style.accent}40`, borderRadius: 8, padding: "5px 12px", cursor: "pointer" }}
                data-testid="transfer-contact-support"
              >
                <MessageCircle size={13} />{t("notif.contact_support")}
              </button>
            )}
          </div>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 10 }}>{t("transfer.code")}</label>
            <OTPBoxes value={code} onChange={setCode} disabled={progressActive || progressDone} accent={style.accent} />
          </div>
          {(progressActive || progress > 0 || progressMessage) && (
            <div style={{ borderRadius: 16, padding: "20px 18px", background: progressDone ? "#f0fdf4" : progressMessage && !progressActive ? "#fffbeb" : "var(--bg)", border: `1.5px solid ${progressDone ? "#86efac" : progressMessage && !progressActive ? "#fcd34d" : "var(--line)"}`, transition: "background 0.4s, border-color 0.4s" }}>
              {/* Icon + label */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: progressDone ? "#dcfce7" : progressMessage && !progressActive ? "#fef3c7" : style.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.4s" }}>
                  {progressDone
                    ? <CheckCircle size={20} color="#16a34a" />
                    : progressMessage && !progressActive
                      ? <AlertCircle size={20} color="#d97706" />
                      : <Send size={18} color={style.accent} style={{ animation: "pulse 1.2s ease-in-out infinite" }} />
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--navy)" }}>
                    {progressDone ? t("transfer.progress.completed") : progressMessage && !progressActive ? t("transfer.progress.paused") : t("transfer.progress.processing")}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: 2 }}>
                    {pending?.recipient && `→ ${pending.recipient}`}
                  </div>
                </div>
                <span ref={pctRef} style={{ fontSize: "1rem", fontWeight: 700, color: progressDone ? "#16a34a" : progressMessage && !progressActive ? "#d97706" : style.accent, fontFamily: "monospace", minWidth: 44, textAlign: "right" }}>
                  {Math.round(progress)}%
                </span>
              </div>
              {/* Bar */}
              <div style={{ height: 8, borderRadius: 100, background: "var(--line)", overflow: "hidden" }}>
                <div ref={barRef} style={{ height: "100%", width: `${progress}%`, borderRadius: 100, background: progressDone ? "linear-gradient(90deg,#4ade80,#16a34a)" : progressMessage && !progressActive ? "#f59e0b" : `linear-gradient(90deg,${style.accent}cc,${style.accent})` }} />
              </div>
              {/* Blocked message */}
              {progressMessage && !progressDone && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, padding: "10px 12px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 10 }}>
                  <AlertCircle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: "0.78rem", color: "#92400e", margin: 0, lineHeight: 1.5 }}>{progressMessage}</p>
                </div>
              )}
            </div>
          )}
          <button type="submit" disabled={progressActive || progressDone || !code} style={{ width: "100%", padding: "14px", borderRadius: 12, background: "var(--accent)", color: "#fff", fontWeight: 700, fontSize: "1rem", border: "none", cursor: (progressActive || progressDone || !code) ? "not-allowed" : "pointer", boxShadow: `0 4px 20px ${style.accent}40`, opacity: (progressActive || progressDone || !code) ? 0.6 : 1 }} data-testid="bu-tr-validate">{progressActive ? t("transfer.progress.processing") : t("transfer.pending.validate")}</button>
          <button type="button" onClick={() => { setPending(null); setProgress(0); setProgressActive(false); setProgressDone(false); setProgressMessage(""); setCode(""); setSaveBeneficiary(false); }} disabled={progressActive} style={{ width: "100%", padding: "12px", borderRadius: 12, background: "transparent", color: "var(--muted)", fontWeight: 500, fontSize: "0.85rem", border: "1px solid var(--line)", cursor: progressActive ? "not-allowed" : "pointer", opacity: progressActive ? 0.5 : 1 }}>{t("transfer.cancel")}</button>
        </form>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* CARDS SECTION BODY                                                          */
/* -------------------------------------------------------------------------- */
const CARD_COLORS = [
  { key: "night", value: "#0f172a" },
  { key: "slate", value: "#1e293b" },
  { key: "steel", value: "#374151" },
  { key: "royal", value: "#2563eb" },
  { key: "cyan", value: "#0891b2" },
  { key: "violet", value: "#7c3aed" },
  { key: "lavender", value: "#6d28d9" },
  { key: "emerald", value: "#059669" },
  { key: "forest", value: "#166534" },
  { key: "gold", value: "#b45309" },
  { key: "amber", value: "#d97706" },
  { key: "orange", value: "#ea580c" },
  { key: "pink", value: "#db2777" },
  { key: "ruby", value: "#dc2626" },
  { key: "terracotta", value: "#c2410c" },
];

function fmtCardNumber(num) {
  if (!num) return "";
  return num.replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim();
}

function CardVisual({ c, style, accent, t, reveal }) {
  const isVisa = (c.card_type || "visa") === "visa";
  const cardColor = c.color || "#0f172a";
  const isBlocked = c.status === "blocked";
  const isRevealed = !!reveal;
  const secsLeft = isRevealed ? Math.max(0, Math.ceil((reveal.until - Date.now()) / 1000)) : 0;
  const mmss = `${String(Math.floor(secsLeft / 60)).padStart(2, "0")}:${String(secsLeft % 60).padStart(2, "0")}`;
  return (
    <div style={{
      background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor}cc 100%)`,
      borderRadius: 16,
      padding: "18px 22px",
      width: "100%",
      maxWidth: 380,
      aspectRatio: "1.586",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: `0 8px 28px ${cardColor}55`,
      position: "relative",
      overflow: "hidden",
      filter: isBlocked ? "grayscale(0.6) brightness(0.75)" : "none",
      transition: "filter .3s",
    }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)", borderRadius: 16 }} />
      {isBlocked && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
          <div style={{ background: "rgba(0,0,0,0.55)", borderRadius: 8, padding: "4px 12px", color: "#fff", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>{t("cards.blocked_label")}</div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1 }}>
        <div style={{ width: 32, height: 24, background: "rgba(255,255,255,0.25)", borderRadius: 4 }} />
        {isVisa ? (
          <span style={{ color: "#fff", fontWeight: 900, fontSize: "1.1rem", fontStyle: "italic", letterSpacing: "0.04em", fontFamily: "serif" }}>VISA</span>
        ) : (
          <div style={{ display: "flex" }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eb001b", opacity: 0.9 }} />
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#f79e1b", opacity: 0.9, marginLeft: -10 }} />
          </div>
        )}
      </div>
      {isRevealed && (
        <div style={{ position: "absolute", top: 12, left: 12, zIndex: 3, display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.45)", borderRadius: 100, padding: "3px 9px" }}>
          <Eye size={11} color="#fff" />
          <span style={{ color: "#fff", fontSize: "0.62rem", fontWeight: 700, fontFamily: "monospace" }}>{mmss}</span>
        </div>
      )}
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ color: "rgba(255,255,255,0.95)", fontSize: "0.95rem", letterSpacing: "0.18em", fontFamily: "monospace", marginBottom: 12 }}>
          {isRevealed ? fmtCardNumber(reveal.number_full) : c.number_masked}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 2 }}>{t("cards.holder")}</div>
            <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.75rem" }}>{c.holder_name}</div>
          </div>
          {isRevealed && (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 2 }}>{t("cards.reveal.cvv")}</div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem", fontFamily: "monospace", letterSpacing: "0.1em" }}>{reveal.cvv}</div>
            </div>
          )}
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 2 }}>{t("cards.expires")}</div>
            <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.75rem" }}>{isRevealed ? (reveal.expiry || c.expiry) : c.expiry}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PHYS_CARD_GRADIENTS = {
  metal: "linear-gradient(135deg, #1c1c1e 0%, #3a3a3c 50%, #6b7280 100%)",
  premium: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
  gold: "linear-gradient(135deg, #78350f 0%, #b45309 50%, #fbbf24 100%)",
};

const PHYS_TYPES_DEFAULT = [
  { id: "metal", name: "Metal", price: 29, features_keys: ["cards.feat.gold_exclusive", "cards.feat.cashback_05", "cards.feat.buyer_protection", "cards.feat.support_standard"] },
  { id: "premium", name: "Premium", price: 49, features_keys: ["cards.feat.plastic_premium", "cards.feat.cashback_1", "cards.feat.travel_standard", "cards.feat.support_priority"] },
  { id: "gold", name: "Gold", price: 99, features_keys: ["cards.feat.metal_brushed", "cards.feat.cashback_2_unlimited", "cards.feat.travel_premium", "cards.feat.vip_lounge", "cards.feat.support_247"] },
];

const PHYS_ACCENT = { metal: "#9ca3af", premium: "#818cf8", gold: "#fbbf24" };

function CardsSectionBody({ style, cards: initialCards, me, bank, headers, setCards: setCardsOuter, reloadUser }) {
  const { t } = useContext(BankLangCtx);
  const [cards, setCardsLocal] = useState(initialCards);
  // PIN-gated card reveal: prompt PIN, then surface details on the card for 5 min
  const [pinPrompt, setPinPrompt] = useState(null); // card awaiting PIN
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinVerifying, setPinVerifying] = useState(false);
  const [revealed, setRevealed] = useState({}); // { [cardId]: { number_full, cvv, expiry, until } }
  const [, forceTick] = useState(0); // drives the 5-min countdown
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ card_type: "visa", color: "#0f172a" });
  const [createLoading, setCreateLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(null);
  const [pinCard, setPinCard] = useState(null);
  const [pinData, setPinData] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  // Physical card ordering
  const [physOrderOpen, setPhysOrderOpen] = useState(false);
  const [physTypes, setPhysTypes] = useState([]);
  const [physSelected, setPhysSelected] = useState(null);
  const [physNetwork, setPhysNetwork] = useState("visa");
  const [physLoading, setPhysLoading] = useState(false);
  const [physTargetCard, setPhysTargetCard] = useState(null);
  const [trackingPopup, setTrackingPopup] = useState(null); // { trackingNumber }
  const [cardFees, setCardFees] = useState({ visa: { local: 18, currency: "EUR" }, mastercard: { local: 25, currency: "EUR" } });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedCard, setCopiedCard] = useState(null);
  const cur = me?.currency || bank?.default_currency;

  useEffect(() => { setCardsLocal(initialCards); }, [initialCards]);

  useEffect(() => {
    if (headers) {
      api.get("/bank-user/virtual-card-fees", { headers })
        .then((r) => setCardFees(r.data))
        .catch(() => {});
    }
  }, [headers]);

  useEffect(() => {
    if (bank?.slug) {
      api.get(`/public/bank/${bank.slug}/physical-card-types`)
        .then((r) => {
          const types = (r.data && r.data.length > 0) ? r.data : PHYS_TYPES_DEFAULT;
          setPhysTypes(types);
          setPhysSelected(types[0] || null);
        })
        .catch(() => { setPhysTypes(PHYS_TYPES_DEFAULT); setPhysSelected(PHYS_TYPES_DEFAULT[0]); });
    } else {
      setPhysTypes(PHYS_TYPES_DEFAULT);
      setPhysSelected(PHYS_TYPES_DEFAULT[0]);
    }
  }, [bank?.slug]);

  const reload = async () => {
    try {
      const { data } = await api.get("/bank-user/cards", { headers });
      setCardsLocal(data);
      if (setCardsOuter) setCardsOuter(data);
    } catch { /* silent */ }
  };

  const openPhysOrder = (card) => {
    if (card.physical_ordered) { toast.info(t("cards.toast.physical_ordered")); return; }
    setPhysTargetCard(card);
    setPhysOrderOpen(true);
  };

  const orderPhysical = async () => {
    if (!physTargetCard || !physSelected) return;
    setPhysLoading(true);
    try {
      const { data } = await api.post(`/bank-user/cards/${physTargetCard.id}/order-physical`, { physical_type: physSelected.id, network: physNetwork }, { headers });
      setPhysOrderOpen(false);
      setPhysTargetCard(null);
      reload();
      if (reloadUser) reloadUser();
      if (data.tracking_number) setTrackingPopup({ trackingNumber: data.tracking_number });
    } catch (err) { toast.error(tError(err, t)); }
    finally { setPhysLoading(false); }
  };

  const toggleBlock = async (c) => {
    setBlockLoading(c.id);
    try {
      const { data } = await api.put(`/bank-user/cards/${c.id}/block`, {}, { headers });
      setCardsLocal((prev) => prev.map((x) => x.id === c.id ? { ...x, status: data.status } : x));
      toast.success(data.status === "blocked" ? t("cards.toast.blocked") : t("cards.toast.unblocked"));
    } catch (err) { toast.error(tError(err, t)); }
    finally { setBlockLoading(null); }
  };

  const openPin = async (c) => {
    setPinCard(c); setPinData(null); setPinLoading(true);
    try {
      const { data } = await api.get(`/bank-user/cards/${c.id}/pin`, { headers });
      setPinData(data);
    } catch (err) { toast.error(tError(err, t)); setPinCard(null); }
    finally { setPinLoading(false); }
  };

  // Step 1 — clicking "info" asks for the user's PIN before revealing anything.
  const openReveal = (c) => {
    setPinPrompt(c); setPinInput(""); setPinError("");
  };

  // Step 2 — PIN verified → fetch details and surface them on the card for 5 minutes.
  const confirmRevealPin = async () => {
    if (!pinPrompt || pinInput.length !== 4) return;
    setPinVerifying(true); setPinError("");
    try {
      await api.post("/bank-user/verify-pin", { pin: pinInput }, { headers });
      const { data } = await api.get(`/bank-user/cards/${pinPrompt.id}/reveal`, { headers });
      const until = Date.now() + 5 * 60 * 1000;
      setRevealed((prev) => ({ ...prev, [pinPrompt.id]: { ...data, until } }));
      setPinPrompt(null); setPinInput("");
    } catch (err) {
      setPinError(err?.response?.status === 401 ? (t("auth.pin.length_error") || "Code PIN incorrect") : tError(err, t));
      setPinInput("");
    } finally { setPinVerifying(false); }
  };

  // Tick once a second while any card is revealed, then auto-hide on expiry.
  useEffect(() => {
    const ids = Object.keys(revealed);
    if (ids.length === 0) return;
    const iv = setInterval(() => {
      const now = Date.now();
      let changed = false;
      const next = { ...revealed };
      for (const id of ids) {
        if (revealed[id].until <= now) { delete next[id]; changed = true; }
      }
      if (changed) setRevealed(next);
      forceTick((x) => x + 1);
    }, 1000);
    return () => clearInterval(iv);
  }, [revealed]);


  const deleteCard = async () => {
    if (!deleteConfirmId) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/bank-user/cards/${deleteConfirmId}`, { headers });
      setDeleteConfirmId(null);
      toast.success(t("cards.deleted") || "Carte supprimée");
      reload();
      if (reloadUser) reloadUser();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setDeleteLoading(false); }
  };

  const createCard = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await api.post("/bank-user/cards", { ...createForm, holder_name: me?.name?.toUpperCase() || "" }, { headers });
      toast.success(t("cards.toast.created"));
      setCreateOpen(false);
      reload();
      if (reloadUser) reloadUser();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setCreateLoading(false); }
  };

  if (cards.length === 0) {
    return (
      <>
        <div style={{ textAlign: "center", padding: "40px 20px 28px" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--bg)", border: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <CardIcon size={28} color="var(--muted)" />
          </div>
          <p style={{ color: "var(--muted)", fontSize: "0.85rem", margin: "0 0 20px", whiteSpace: "pre-line" }}>{t("cards.none_sub")}</p>
          <button onClick={() => setCreateOpen(true)} style={{ background: style.accent, color: "#fff", border: "none", borderRadius: 12, padding: "11px 24px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <CardIcon size={16} /> {t("cards.generate_btn")}
          </button>
        </div>
        {createOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
            <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 440, width: "100%", border: "1px solid var(--line)", overflow: "hidden" }}>
              <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif" }}>{t("cards.create.title")}</span>
                <button onClick={() => setCreateOpen(false)} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
              </div>
              <form onSubmit={createCard} style={{ padding: "1.25rem 1.4rem" }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>{t("cards.create.network")}</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["visa", "mastercard"].map((type) => {
                      const fee = cardFees[type];
                      return (
                        <button key={type} type="button" onClick={() => setCreateForm((f) => ({ ...f, card_type: type }))}
                          style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `2px solid ${createForm.card_type === type ? style.accent : "var(--line)"}`, background: "var(--bg)", cursor: "pointer", fontWeight: 700, fontSize: ".82rem", color: createForm.card_type === type ? style.accent : "var(--muted)", transition: "all .15s", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                          <span>{type === "visa" ? "VISA" : "Mastercard"}</span>
                          <span style={{ fontSize: ".68rem", fontWeight: 500, opacity: 0.8 }}>{fee ? `${fmtMoney(fee.local, fee.currency)}` : ""}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>{t("cards.create.color")}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {CARD_COLORS.map((cl) => (
                      <button key={cl.value} type="button" title={t("card.color." + cl.key)}onClick={() => setCreateForm((f) => ({ ...f, color: cl.value }))}
                        style={{ width: 32, height: 32, borderRadius: "50%", background: cl.value, border: `3px solid ${createForm.color === cl.value ? style.accent : "transparent"}`, cursor: "pointer", outline: createForm.color === cl.value ? `2px solid ${style.accent}40` : "none", transition: "all .15s" }} />
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 18, borderRadius: 12, overflow: "hidden", display: "flex", justifyContent: "center" }}>
                  <CardVisual c={{ card_type: createForm.card_type, color: createForm.color, number_masked: "•••• •••• •••• ••••", holder_name: me?.name?.toUpperCase() || t("cards.create.preview_name"), expiry: t("cards.create.preview_expiry"), status: "active" }} style={style} accent={style.accent} t={t} />
                </div>
                {(() => { const fee = cardFees[createForm.card_type]; const bal = me?.balance || 0; const canAfford = !fee || bal >= fee.local; return !canAfford ? (
                  <p style={{ fontSize: ".78rem", color: "#dc2626", textAlign: "center", margin: "0 0 10px", fontWeight: 600 }}>{t("cards.insufficient_short", { available: fmtMoney(bal, fee?.currency) })}</p>
                ) : fee ? (
                  <p style={{ fontSize: ".78rem", color: "var(--muted)", textAlign: "center", margin: "0 0 10px" }}>{t("cards.fee_deducted", { amount: fmtMoney(fee.local, fee.currency) })}</p>
                ) : null; })()}
                <button type="submit" disabled={createLoading || (() => { const fee = cardFees[createForm.card_type]; return fee && (me?.balance || 0) < fee.local; })()} style={{ width: "100%", background: style.accent, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontWeight: 700, fontSize: ".9rem", cursor: createLoading ? "not-allowed" : "pointer", opacity: createLoading ? 0.7 : 1 }}>
                  {createLoading ? t("cards.create.loading") : t("cards.create.submit")}
                </button>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CardIcon size={18} color={style.accent} />
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif" }}>{t("page.cards")}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setCreateOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 12, background: "var(--bg)", color: style.accent, border: `1.5px solid ${style.accent}`, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap" }}>
            <Plus size={15} /> {t("cards.buy_virtual_cta")}
          </button>
          <button onClick={() => {
            const eligible = cards.find((c) => !c.physical_ordered);
            if (!eligible) { toast.info(t("cards.toast.physical_ordered")); return; }
            setPhysTargetCard(eligible);
            setPhysOrderOpen(true);
          }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 12, background: style.accent, color: "#fff", border: "none", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", boxShadow: `0 4px 16px ${style.accent}50`, whiteSpace: "nowrap" }}>
            <CardIcon size={15} /> {t("cards.physical.order_cta")}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {cards.map((c) => (
          <div key={c.id} data-testid={`card-${c.id}`} style={{ maxWidth: 500 }}>
            <div style={{ marginBottom: 14 }}>
              <CardVisual c={c} style={style} accent={style.accent} t={t} reveal={revealed[c.id]} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              <button onClick={() => toggleBlock(c)} disabled={blockLoading === c.id}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 4px", borderRadius: 12, background: c.status === "blocked" ? "#fef2f2" : "var(--bg)", border: `1px solid ${c.status === "blocked" ? "#fca5a5" : "var(--line)"}`, cursor: "pointer", color: c.status === "blocked" ? "#dc2626" : "var(--navy)", transition: "all .15s" }}>
                <Lock size={16} color={c.status === "blocked" ? "#dc2626" : style.accent} />
                <span style={{ fontSize: "0.62rem", fontWeight: 600 }}>{c.status === "blocked" ? t("cards.btn.unblock") : t("cards.btn.block")}</span>
              </button>
              <button onClick={() => openReveal(c)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 4px", borderRadius: 12, background: "var(--bg)", border: "1px solid var(--line)", cursor: "pointer", color: "var(--navy)" }}>
                <Eye size={16} color={style.accent} />
                <span style={{ fontSize: "0.62rem", fontWeight: 600 }}>{t("cards.btn.info")}</span>
              </button>
              <button onClick={() => openPin(c)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 4px", borderRadius: 12, background: "var(--bg)", border: "1px solid var(--line)", cursor: "pointer", color: "var(--navy)" }}>
                <Key size={16} color={style.accent} />
                <span style={{ fontSize: "0.62rem", fontWeight: 600 }}>{t("cards.pin_label")}</span>
              </button>
              <button onClick={() => !c.physical_ordered && setDeleteConfirmId(c.id)} disabled={!!c.physical_ordered} title={c.physical_ordered ? t("cards.delete.physical_tooltip") : undefined}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "10px 4px", borderRadius: 12, background: c.physical_ordered ? "var(--bg)" : "#fef2f2", border: `1px solid ${c.physical_ordered ? "var(--line)" : "#fca5a5"}`, cursor: c.physical_ordered ? "not-allowed" : "pointer", color: c.physical_ordered ? "var(--muted)" : "#dc2626", opacity: c.physical_ordered ? 0.45 : 1 }}>
                <Trash2 size={16} color={c.physical_ordered ? "var(--muted)" : "#dc2626"} />
                <span style={{ fontSize: "0.62rem", fontWeight: 600 }}>{t("common.delete")}</span>
              </button>
            </div>

            <div style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 14, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 14, color: "var(--navy)" }}>{t("cards.limits_title")}</div>
              {[{ label: t("cards.daily"), used: 0, max: c.limit_daily ?? 1000 }, { label: t("cards.weekly"), used: 0, max: c.limit_weekly ?? 5000 }, { label: t("cards.monthly"), used: 0, max: c.limit_monthly ?? 10000 }].map((p) => {
                const pct = Math.round((p.used / p.max) * 100);
                return (
                  <div key={p.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", marginBottom: 6 }}>
                      <span style={{ color: "var(--muted)" }}>{p.label}</span>
                      <span style={{ fontWeight: 600, color: "var(--navy)" }}>{fmtMoney(p.used, cur)} / {fmtMoney(p.max, cur)}</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 100, background: "var(--line)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: pct > 80 ? "#ef4444" : style.accent, borderRadius: 100, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {c.physical_ordered && c.tracking_number && (
              <div style={{ marginTop: 8, padding: "12px 14px", background: "rgba(0,0,0,0.05)", borderRadius: 10, fontSize: "0.72rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, color: "var(--navy)", fontFamily: "monospace" }}>{t("tracking.label")} : {c.tracking_number}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(c.tracking_number);
                      setCopiedCard(c.id);
                      setTimeout(() => setCopiedCard(null), 1500);
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.68rem", fontWeight: 700, color: style.accent, background: "transparent", border: `1px solid ${style.accent}55`, borderRadius: 6, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    {copiedCard === c.id ? <Check size={11} /> : <Copy size={11} />}
                    {copiedCard === c.id ? t("transfer.code.copied") : t("transfer.code.copy")}
                  </button>
                </div>
                {["preparation","shipped","transit","delivered"].map((step, i) => {
                  const steps = ["preparation","shipped","transit","delivered"];
                  const currentIdx = steps.indexOf(c.tracking_status || "preparation");
                  const done = i <= currentIdx;
                  const labels = { preparation: t("tracking.status.preparation"), shipped: t("tracking.status.shipped"), transit: t("tracking.status.transit"), delivered: t("tracking.status.delivered") };
                  const msg = c.tracking_messages?.[step];
                  return (
                    <div key={step} style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom: msg && done ? 8 : 4, opacity: done ? 1 : 0.4 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background: done ? style.accent : "#ccc", flexShrink:0, marginTop: 3 }} />
                      <div>
                        <span style={{ color: done ? style.accent : "var(--muted)", fontWeight: done ? 600 : 400 }}>{labels[step]}</span>
                        {msg && done && (
                          <div style={{ color: "var(--muted)", fontSize: "0.67rem", marginTop: 2 }}>{msg}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal — révéler infos carte */}
      {/* Modal — PIN gate before revealing card details */}
      {pinPrompt && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 380, width: "100%", border: "1px solid var(--line)" }}>
            <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 8 }}>
                <Key size={16} color={style.accent} /> {t("cards.reveal.title")}
              </span>
              <button onClick={() => { setPinPrompt(null); setPinInput(""); setPinError(""); }} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
            </div>
            <div style={{ padding: "1.5rem 1.4rem" }}>
              <p style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", margin: "0 0 18px", lineHeight: 1.5 }}>
                {t("cards.reveal.pin_prompt")}
              </p>
              <OTPBoxes value={pinInput} onChange={(v) => { setPinInput(v); setPinError(""); }} disabled={pinVerifying} accent={style.accent} length={4} autoFocus testId="card-reveal-pin" />
              {pinError && <p style={{ fontSize: ".78rem", color: "#dc2626", textAlign: "center", margin: "12px 0 0" }}>{pinError}</p>}
              <button
                type="button"
                onClick={confirmRevealPin}
                disabled={pinVerifying || pinInput.length !== 4}
                style={{ width: "100%", marginTop: 18, padding: "13px", borderRadius: 12, background: style.accent, color: "#fff", fontWeight: 700, fontSize: "0.92rem", border: "none", cursor: (pinVerifying || pinInput.length !== 4) ? "not-allowed" : "pointer", opacity: (pinVerifying || pinInput.length !== 4) ? 0.6 : 1 }}
                data-testid="card-reveal-pin-submit"
              >
                {pinVerifying ? t("common.loading") : t("cards.reveal.show")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — code PIN */}
      {pinCard && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 360, width: "100%", border: "1px solid var(--line)" }}>
            <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif", display: "flex", alignItems: "center", gap: 8 }}>
                <Key size={16} color={style.accent} /> {t("cards.pin.title")}
              </span>
              <button onClick={() => { setPinCard(null); setPinData(null); }} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
            </div>
            <div style={{ padding: "1.5rem 1.4rem", textAlign: "center" }}>
              {pinLoading ? (
                <div style={{ color: "var(--muted)", fontSize: ".85rem" }}>{t("common.loading")}</div>
              ) : pinData ? (
                <>
                  <div style={{ background: "var(--bg)", border: "2px solid var(--line)", borderRadius: 14, padding: "20px 0", marginBottom: 16 }}>
                    <div style={{ fontFamily: "monospace", fontSize: "2.4rem", fontWeight: 700, letterSpacing: "0.35em", color: "var(--navy)" }}>{pinData.pin}</div>
                  </div>
                  <div style={{ background: style.accent + "10", border: `1px solid ${style.accent}30`, borderRadius: 10, padding: "10px 14px", marginBottom: 4 }}>
                    <p style={{ fontSize: ".78rem", color: "var(--navy)", margin: 0, lineHeight: 1.5, fontWeight: 500 }}>
                      {t("cards.pin.message")}
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal — acheter une carte virtuelle */}
      {createOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 440, width: "100%", border: "1px solid var(--line)", overflow: "hidden" }}>
            <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif" }}>{t("cards.create.title")}</span>
              <button onClick={() => setCreateOpen(false)} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
            </div>
            <form onSubmit={createCard} style={{ padding: "1.25rem 1.4rem" }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>{t("cards.create.network")}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {["visa", "mastercard"].map((type) => {
                    const fee = cardFees[type];
                    return (
                      <button key={type} type="button" onClick={() => setCreateForm((f) => ({ ...f, card_type: type }))}
                        style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `2px solid ${createForm.card_type === type ? style.accent : "var(--line)"}`, background: "var(--bg)", cursor: "pointer", fontWeight: 700, fontSize: ".82rem", color: createForm.card_type === type ? style.accent : "var(--muted)", transition: "all .15s", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <span>{type === "visa" ? "VISA" : "Mastercard"}</span>
                        <span style={{ fontSize: ".68rem", fontWeight: 500, opacity: 0.8 }}>{fee ? `${fmtMoney(fee.local, fee.currency)}` : ""}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--muted)", marginBottom: 8 }}>{t("cards.create.color")}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {CARD_COLORS.map((cl) => (
                    <button key={cl.value} type="button" title={t("card.color." + cl.key)}onClick={() => setCreateForm((f) => ({ ...f, color: cl.value }))}
                      style={{ width: 32, height: 32, borderRadius: "50%", background: cl.value, border: `3px solid ${createForm.color === cl.value ? style.accent : "transparent"}`, cursor: "pointer", outline: createForm.color === cl.value ? `2px solid ${style.accent}40` : "none", transition: "all .15s" }} />
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 18, borderRadius: 12, overflow: "hidden", display: "flex", justifyContent: "center" }}>
                <CardVisual c={{ card_type: createForm.card_type, color: createForm.color, number_masked: "•••• •••• •••• ••••", holder_name: me?.name?.toUpperCase() || t("cards.create.preview_name"), expiry: t("cards.create.preview_expiry"), status: "active" }} style={style} accent={style.accent} t={t} />
              </div>
              {(() => { const fee = cardFees[createForm.card_type]; const bal = me?.balance || 0; const canAfford = !fee || bal >= fee.local; return !canAfford ? (
                <p style={{ fontSize: ".78rem", color: "#dc2626", textAlign: "center", margin: "0 0 10px", fontWeight: 600 }}>{t("cards.insufficient_short", { available: fmtMoney(bal, fee?.currency) })}</p>
              ) : fee ? (
                <p style={{ fontSize: ".78rem", color: "var(--muted)", textAlign: "center", margin: "0 0 10px" }}>{t("cards.fee_deducted", { amount: fmtMoney(fee.local, fee.currency) })}</p>
              ) : null; })()}
              <button type="submit" disabled={createLoading || (() => { const fee = cardFees[createForm.card_type]; return fee && (me?.balance || 0) < fee.local; })()} style={{ width: "100%", background: style.accent, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontWeight: 700, fontSize: ".9rem", cursor: createLoading ? "not-allowed" : "pointer", opacity: createLoading ? 0.7 : 1 }}>
                {createLoading ? t("cards.create.loading") : t("cards.create.submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Popup — numéro de suivi après commande */}
      {trackingPopup && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
          <div style={{ background: "var(--bg)", borderRadius: 20, padding: 28, maxWidth: 360, width: "100%", boxShadow: "0 25px 50px rgba(0,0,0,0.3)", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: style.accent + "20", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Package size={26} color={style.accent} />
            </div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "var(--navy)", marginBottom: 8 }}>{t("cards.order.confirmed")}</div>
            <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: 20 }}>{t("cards.order.confirmed_body")}</p>
            <div style={{ background: style.accent + "12", border: `2px dashed ${style.accent}50`, borderRadius: 12, padding: "14px 20px", marginBottom: 20 }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: style.accent, marginBottom: 4 }}>{t("cards.order.tracking_no")}</div>
              <div style={{ fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 700, color: "var(--navy)", letterSpacing: "0.06em" }}>{trackingPopup.trackingNumber}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { navigator.clipboard?.writeText(trackingPopup.trackingNumber); toast.success(t("transfer.code.copied")); }}
                style={{ flex: 1, padding: "11px", borderRadius: 12, border: `1.5px solid ${style.accent}`, background: "transparent", color: style.accent, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
              >{t("transfer.code.copy")}</button>
              <button
                onClick={() => setTrackingPopup(null)}
                style={{ flex: 1, padding: "11px", borderRadius: 12, border: "none", background: style.accent, color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
              >{t("common.close")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — commander carte physique (nouveau) */}
      {physOrderOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem", overflowY: "auto" }}>
          <div style={{ background: "var(--surface)", borderRadius: 20, maxWidth: 560, width: "100%", border: "1px solid var(--line)", maxHeight: "90vh", overflowY: "auto" }}>
            {/* Header */}
            <div style={{ padding: "1.1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif" }}>{t("cards.physical.order_cta")}</span>
              <button onClick={() => setPhysOrderOpen(false)} style={{ background: "var(--bg)", border: "none", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", color: "var(--muted)", display: "grid", placeItems: "center" }}>✕</button>
            </div>
            <div style={{ padding: "1.25rem 1.4rem" }}>

              {/* Step 1 — Choose type */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>{t("cards.physical.choose_type")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {physTypes.map((pt) => {
                    const isSelected = physSelected?.id === pt.id;
                    const balance = me?.balance || 0;
                    const canAfford = balance >= pt.price;
                    const accentColor = PHYS_CARD_GRADIENTS[pt.id] || "#1e293b";
                    return (
                      <button key={pt.id} type="button" onClick={() => setPhysSelected(pt)}
                        style={{ display: "flex", alignItems: "stretch", borderRadius: 16, border: `2px solid ${isSelected ? style.accent : "var(--line)"}`, background: isSelected ? style.accent + "08" : "var(--bg)", cursor: "pointer", overflow: "hidden", transition: "all .15s", textAlign: "left" }}>
                        {/* Card preview */}
                        <div style={{ width: 90, minHeight: 70, background: accentColor, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "10px 10px 8px", position: "relative" }}>
                          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,rgba(255,255,255,0.12) 0%,transparent 60%)" }} />
                          <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.78rem", fontFamily: "'Syne',sans-serif", letterSpacing: "0.02em", position: "relative" }}>{pt.name}</div>
                          <div style={{ display: "flex", justifyContent: "flex-end", position: "relative" }}>
                            <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: 900, fontSize: "0.6rem", fontStyle: "italic", fontFamily: "serif" }}>VISA</span>
                          </div>
                        </div>
                        {/* Info */}
                        <div style={{ flex: 1, padding: "10px 14px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "var(--navy)", fontFamily: "'Syne',sans-serif" }}>{pt.name}</span>
                            <span style={{ fontWeight: 700, fontSize: "0.92rem", color: isSelected ? style.accent : "var(--navy)" }}>{Number(pt.price).toFixed(2)} €</span>
                          </div>
                          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexWrap: "wrap", gap: "3px 10px" }}>
                            {(pt.features || (pt.features_keys || []).map((k) => t(k))).slice(0, 3).map((f, i) => (
                              <li key={i} style={{ fontSize: "0.65rem", color: "var(--muted)", display: "flex", alignItems: "center", gap: 3 }}>
                                <span style={{ color: "#22c55e", fontSize: "0.7rem" }}>✓</span>{f}
                              </li>
                            ))}
                          </ul>
                          {!canAfford && <span style={{ fontSize: "0.62rem", color: "#ef4444", marginTop: 4, fontWeight: 600 }}>{t("cards.physical.insufficient")}</span>}
                        </div>
                        {isSelected && <div style={{ width: 4, background: style.accent, flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 — Network */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>{t("cards.create.network")}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[{ id: "visa", label: "VISA" }, { id: "mastercard", label: "Mastercard" }].map((n) => (
                    <button key={n.id} type="button" onClick={() => setPhysNetwork(n.id)}
                      style={{ flex: 1, padding: "12px", borderRadius: 12, border: `2px solid ${physNetwork === n.id ? style.accent : "var(--line)"}`, background: physNetwork === n.id ? style.accent + "12" : "transparent", color: physNetwork === n.id ? style.accent : "var(--muted)", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      {n.id === "visa" ? (
                        <span style={{ fontStyle: "italic", fontFamily: "serif", fontWeight: 900 }}>VISA</span>
                      ) : (
                        <div style={{ display: "flex" }}>
                          <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#eb001b" }} />
                          <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#f79e1b", marginLeft: -7 }} />
                        </div>
                      )}
                      {n.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3 — Confirm */}
              {physSelected && (
                <div style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: ".82rem", color: "var(--muted)" }}>{t("cards.physical.fee_label")}</span>
                    <span style={{ fontWeight: 700, color: "var(--navy)" }}>{Number(physSelected.price).toFixed(2)} €</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: ".82rem", color: "var(--muted)" }}>{t("cards.physical.balance_label")}</span>
                    <span style={{ fontWeight: 700, color: (me?.balance || 0) >= physSelected.price ? "#15803d" : "#dc2626" }}>{fmtMoney(me?.balance || 0, cur)}</span>
                  </div>
                </div>
              )}

              {physSelected && (me?.balance || 0) < physSelected.price ? (
                <p style={{ fontSize: ".82rem", color: "#dc2626", textAlign: "center", margin: "0 0 14px" }}>{t("cards.physical.insufficient")}</p>
              ) : (
                <p style={{ fontSize: ".8rem", color: "var(--muted)", textAlign: "center", margin: "0 0 14px" }}>{t("cards.physical.info")}</p>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setPhysOrderOpen(false)} style={{ flex: 1, padding: "11px", borderRadius: 12, border: "1px solid var(--line)", background: "var(--bg)", color: "var(--navy)", fontWeight: 600, fontSize: ".85rem", cursor: "pointer" }}>{t("common.cancel")}</button>
                <button onClick={orderPhysical} disabled={physLoading || !physSelected || (me?.balance || 0) < (physSelected?.price || 0) || cards.every((c) => c.physical_ordered)}
                  style={{ flex: 1, padding: "11px", borderRadius: 12, border: "none", background: (!physSelected || (me?.balance || 0) < (physSelected?.price || 0)) ? "var(--line)" : style.accent, color: "#fff", fontWeight: 700, fontSize: ".85rem", cursor: (!physSelected || (me?.balance || 0) < (physSelected?.price || 0)) ? "not-allowed" : "pointer", opacity: physLoading ? 0.7 : 1 }}>
                  {physLoading ? t("cards.physical.ordering") : t("cards.physical.order_btn", { fee: `${Number(physSelected?.price || 0).toFixed(2)} €` })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirmId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: "1rem" }}>
          <div style={{ background: "var(--surface)", borderRadius: 18, maxWidth: 340, width: "100%", border: "1px solid var(--line)", padding: "1.5rem" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Trash2 size={28} color="#dc2626" style={{ marginBottom: 10 }} />
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--navy)", marginBottom: 6 }}>{t("cards.delete.title")}</div>
              <p style={{ color: "var(--muted)", fontSize: ".82rem", lineHeight: 1.5, margin: 0 }}>{t("cards.delete.confirm_msg")}</p>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button onClick={() => setDeleteConfirmId(null)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", cursor: "pointer", fontWeight: 600 }}>{t("common.cancel")}</button>
              <button onClick={deleteCard} disabled={deleteLoading} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: "#dc2626", color: "#fff", cursor: deleteLoading ? "not-allowed" : "pointer", fontWeight: 700, opacity: deleteLoading ? 0.7 : 1 }}>
                {deleteLoading ? "…" : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* PROFILE SECTION BODY                                                        */
/* -------------------------------------------------------------------------- */
function NotificationsSection({ style, notifs, setActiveSection, dismissNotif }) {
  const { t } = useContext(BankLangCtx);
  const [copied, setCopied] = useState(null);
  // No auto-expiration of transfer_code notifs — they stay until the user
  // dismisses them (X). Removed the 120 s TTL filter + countdown.

  const visibleNotifs = notifs;

  const copyCode = (n) => {
    try { navigator.clipboard?.writeText(n.code); } catch (_) {}
    setCopied(n.id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="notifications-section">
      {visibleNotifs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)", fontSize: "0.85rem", background: "var(--bg)", border: "1px dashed var(--line)", borderRadius: 12 }}>
          <Bell size={28} style={{ opacity: 0.3, marginBottom: 10 }} />
          <div>{t("notif.none")}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleNotifs.map((n) => {
            const isAdmin = n.kind === "admin";
            const isCode = n.notif_type === "transfer_code" && n.code;
            // For transfer_code we override the stored (FR) title/body with i18n keys
            const displayTitle = isCode ? t("notif.transfer_code.title") : n.title;
            const displayBody = isCode ? t("notif.transfer_code.body") : n.body;
            return (
            <div key={n.id} style={{ position: "relative", background: isCode ? "#f0fdf4" : "var(--bg)", border: isCode ? "1.5px solid #86efac" : `1px solid ${style.accent}40`, borderLeft: isCode ? "3px solid #16a34a" : `3px solid ${style.accent}`, borderRadius: 12, padding: "14px 16px" }} data-testid={`notif-item-${n.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Bell size={14} color={isCode ? "#16a34a" : style.accent} />
                <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--navy)", fontFamily: "'Syne', sans-serif", flex: 1 }}>{displayTitle}</span>
                {isCode ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {dismissNotif && (
                      <button onClick={() => dismissNotif(n.id)} data-testid={`notif-dismiss-${n.id}`} aria-label={t("notif.dismiss")} title={t("notif.dismiss")}
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, border: "none", background: "transparent", color: "var(--muted)", cursor: "pointer", flexShrink: 0 }}>
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ) : isAdmin ? (
                  <Lock size={12} color={style.accent} style={{ opacity: 0.5 }} title={t("notif.locked")} />
                ) : (dismissNotif && (
                  <button onClick={() => dismissNotif(n.id)} data-testid={`notif-dismiss-${n.id}`}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, border: "none", background: "transparent", color: "var(--muted)", cursor: "pointer", flexShrink: 0 }}>
                    <X size={15} />
                  </button>
                ))}
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--navy)", lineHeight: 1.55, opacity: 0.85 }}>{displayBody}</div>
              {isCode && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, padding: "10px 14px", background: "#fff", border: "1px solid #86efac", borderRadius: 10 }}>
                  <span style={{ flex: 1, fontFamily: "monospace", fontSize: "1.8rem", fontWeight: 700, letterSpacing: "0.4em", color: "var(--navy)", textAlign: "center" }} data-testid={`notif-code-value-${n.id}`}>{n.code}</span>
                  <button type="button" onClick={() => copyCode(n)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.78rem", fontWeight: 700, color: "#fff", background: "#16a34a", border: "none", borderRadius: 8, padding: "8px 14px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                    data-testid={`notif-code-copy-${n.id}`}>
                    {copied === n.id ? <Check size={14} /> : <Copy size={14} />}
                    {copied === n.id ? t("transfer.code.copied") : t("transfer.code.copy")}
                  </button>
                </div>
              )}
              <div style={{ fontSize: "0.68rem", color: "var(--muted)", marginTop: 8, opacity: 0.7 }}>{new Date(n.created_at).toLocaleString()}</div>
              {!isCode && setActiveSection && (
                <button onClick={() => setActiveSection("messages")} data-testid={`notif-contact-support-${n.id}`}
                  style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6, background: style.accent, color: "#fff", border: "none", borderRadius: 9, padding: "8px 14px", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
                  <MessageCircle size={14} />{t("notif.contact_support")}
                </button>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function ProfileSectionBody({ style, me, notifs, logout, initials, headers, reload }) {
  const { t } = useContext(BankLangCtx);
  const { bank } = useContext(BankCtx);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error(t("profile.photo_too_large"));
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.patch("/bank-user/profile", { avatar_url: dataUrl }, { headers });
      toast.success(t("profile.photo_updated"));
      reload?.();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.patch("/bank-user/profile", { avatar_url: "" }, { headers });
      toast.success(t("profile.photo_removed"));
      reload?.();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8 }}>
        <div style={{ position: "relative", marginBottom: 14 }}>
          <div style={{ width: 96, height: 96, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "1.9rem", boxShadow: `0 4px 24px ${style.accent}45`, fontFamily: "'Syne', sans-serif", overflow: "hidden" }}>
            {me.avatar_url ? <img src={me.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label={t("profile.change_photo")}
            data-testid="profile-avatar-edit"
            style={{ position: "absolute", bottom: 0, right: 0, width: 30, height: 30, borderRadius: "50%", background: "var(--surface)", border: `2px solid ${style.accent}`, color: style.accent, cursor: uploading ? "wait" : "pointer", display: "grid", placeItems: "center", boxShadow: "0 2px 6px rgba(0,0,0,.15)" }}
          >
            <Camera size={14} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
        </div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--navy)" }}>{me.name}</div>
        <div style={{ fontSize: "0.82rem", color: "var(--muted)", marginTop: 4 }}>{me.email}</div>
        {me.avatar_url && (
          <button type="button" onClick={removeAvatar} disabled={uploading} style={{ marginTop: 8, fontSize: ".72rem", color: "#dc2626", background: "transparent", border: "none", cursor: uploading ? "wait" : "pointer" }} data-testid="profile-avatar-remove">
            {t("profile.remove_photo")}
          </button>
        )}
      </div>
      <div style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
        {[{ label: t("profile.email"), value: me.email }, me.phone && { label: t("profile.phone"), value: me.phone }, me.country && { label: t("profile.country"), value: me.country }, me.address && { label: t("profile.address"), value: me.address }].filter(Boolean).map((item, i, arr) => (
          <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: i < arr.length - 1 ? "1px solid var(--line)" : "none" }}>
            <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>{item.label}</span>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, maxWidth: "60%", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--navy)" }}>{item.value}</span>
          </div>
        ))}
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Bell size={15} color="var(--muted)" />
          <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--navy)" }}>{t("profile.notifs_title", { count: notifs.length })}</span>
        </div>
        {notifs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: "var(--muted)", fontSize: "0.82rem" }}>{t("profile.no_notifs")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {notifs.slice(0, 5).map((n) => (
              <div key={n.id} style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: 4, color: "var(--navy)" }}>{n.title}</div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", lineHeight: 1.5 }}>{n.body}</div>
                <div style={{ fontSize: "0.68rem", color: "var(--muted)", marginTop: 6, opacity: 0.7 }}>{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <button onClick={logout} style={{ width: "100%", padding: "14px", borderRadius: 12, background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: "0.9rem", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} data-testid="bank-profile-logout">
        <LogOut size={17} />{t("profile.logout")}
      </button>
      <div style={{ textAlign: "center", fontSize: "0.72rem", color: "var(--muted)", opacity: 0.5, paddingBottom: 4 }}>{t("profile.powered", { bank: bank?.name || "" })}</div>
    </div>
  );
}
